<template>
  <div ref="paperWorkspace" class="paper-workspace" :class="{ 'is-detail': detailMode, 'is-interaction-locked': interactionLocked }" @click="closeFloatingMenus">
    <header v-if="!detailMode" class="paper-list-header">
      <div class="paper-breadcrumb">
        <button class="paper-breadcrumb-root" type="button" @click="$emit('back-root')">{{ ui.myFiles }}</button>
        <span
          v-for="(crumb, index) in paperBreadcrumbItems"
          :key="(crumb.resourceId || 'paper-root') + '-' + index"
          class="paper-breadcrumb-segment"
        >
          <img class="paper-breadcrumb-separator" :src="paperIcons.breadcrumbSeparator" alt="" />
          <button
            v-if="index < paperBreadcrumbItems.length - 1"
            class="paper-breadcrumb-link"
            type="button"
            :title="getKnowledgeBaseDisplayName(crumb)"
            @click="openPaperBreadcrumb(index)"
          >{{ getKnowledgeBaseDisplayName(crumb) }}</button>
          <strong v-else :title="getKnowledgeBaseDisplayName(crumb)">{{ getKnowledgeBaseDisplayName(crumb) }}</strong>
        </span>
      </div>
      <div class="paper-header-actions">
        <button v-if="canCreateKnowledgeBaseFolder" class="paper-icon-button" type="button" :title="ui.createFolder" @click.stop="openPaperCreateFolderModal">
          <img :src="paperIcons.createFolder" alt="" />
        </button>
        <button class="paper-icon-button" type="button" :title="ui.search" @click.stop="$emit('global-search')">
          <img :src="paperIcons.search" alt="" />
        </button>
        <button v-if="false" class="paper-import-button" type="button" @click.stop="fakeAction(ui.importPdf)">
          <img :src="paperIcons.import" alt="" />
          <span>{{ ui.importPdf }}</span>
        </button>
      </div>
    </header>

    <header v-else class="paper-detail-header">
      <div class="paper-detail-title">
        <button class="paper-back-button" type="button" :aria-label="ui.back" @click="handleDetailBack">
          <span></span>
        </button>
        <strong :title="paperDetailTitle">{{ paperDetailTitle }}</strong>
      </div>
      <div class="paper-detail-actions">
        <button
          v-if="hasDetailRecordings && !pageGridOpen"
          class="paper-tool-button"
          type="button"
          :class="{ active: recordPanelOpen && !recordTextOpen }"
          :disabled="paperDetailLoading"
          :title="ui.recordings"
          @click.stop="toggleRecordPanel"
        >
          <img :src="paperIcons.record" alt="" />
        </button>
        <button class="paper-tool-button" type="button" :disabled="paperDetailLoading || detailPages.length == 0 || (pageGridOpen && selectedDetailPages.length == 0)" :title="ui.download" @click.stop="downloadCurrentPage">
          <img :src="paperIcons.download" alt="" />
        </button>
        <button
          v-if="!pageGridOpen"
          class="paper-tool-button"
          type="button"
          :class="{ active: pageGridOpen }"
          :disabled="paperDetailLoading || pageGridOpen || detailPages.length == 0"
          :title="ui.pageGrid"
          @click.stop="togglePageGrid"
        >
          <img :src="paperIcons.pageGrid" alt="" />
        </button>
        <button v-if="!pageGridOpen" class="paper-tool-button" type="button" :disabled="paperDetailLoading || detailPages.length == 0 || pageGridOpen || zoomLevel >= 5" :title="ui.zoomIn" @click.stop="zoomIn">
          <img :src="paperIcons.zoomIn" alt="" />
          <span v-if="isPreviewZoomTipVisible('paper', 'in')" class="preview-zoom-tip">{{ previewZoomTip.text }}</span>
        </button>
        <button v-if="!pageGridOpen" class="paper-tool-button" type="button" :disabled="paperDetailLoading || detailPages.length == 0 || pageGridOpen || zoomLevel <= -3" :title="ui.zoomOut" @click.stop="zoomOut">
          <img :src="paperIcons.zoomOut" alt="" />
          <span v-if="isPreviewZoomTipVisible('paper', 'out')" class="preview-zoom-tip">{{ previewZoomTip.text }}</span>
        </button>
      </div>
    </header>

    <template v-if="!detailMode">
      <div
        v-if="hasPaperSelection"
        ref="paperSelectionLayer"
        class="paper-selection-layer"
        :class="{ dragging: selectionBarDragging }"
        :style="selectionBarLayerStyle"
        @mousedown.stop="startSelectionBarDrag"
        @click.stop
      >
        <div class="paper-selection-bar">
          <button class="paper-selection-close" type="button" :aria-label="ui.cancel" @click="clearSelection">
            <span></span>
          </button>
          <span>{{ ui.selectedPrefix }} {{ selectedCount }} {{ ui.selectedSuffix }}</span>
          <div class="paper-selection-actions">
            <button type="button" :disabled="!canRenameSelected" @click="handleSelectionAction('rename')">
              <img :src="paperIcons.actionRename" alt="" />
              <span>{{ ui.rename }}</span>
            </button>
            <button v-if="canUseKnowledgeBaseMove" type="button" :disabled="!canMoveSelected" @click="handleSelectionAction('move')">
              <img :src="paperIcons.move" alt="" />
              <span>{{ ui.move }}</span>
            </button>
            <button type="button" :disabled="!canBatchSelectionAction" @click="handleSelectionAction('download')">
              <img :src="paperIcons.actionDownload" alt="" />
              <span>{{ ui.download }}</span>
            </button>
            <button v-if="showSelectionDeleteAction" type="button" :disabled="!canDeleteSelectedPaperItems" @click="handleSelectionAction('delete')">
              <img :src="paperIcons.actionDelete" alt="" />
              <span>{{ ui.delete }}</span>
            </button>
          </div>
        </div>
      </div>

      <div class="paper-toolbar">
        <div v-if="false" class="paper-filter" @click.stop>
          <button class="paper-filter-button" type="button" @click="filterMenuOpen = !filterMenuOpen">
            <img class="paper-filter-icon" :src="paperIcons.filterAll" alt="" />
            <span>{{ activeFilterLabel }}</span>
            <span class="filter-arrow" :class="{ open: filterMenuOpen }"></span>
          </button>
          <div v-if="filterMenuOpen" class="paper-filter-menu">
            <button
              v-for="item in filterOptions"
              :key="item.key"
              type="button"
              :class="{ active: paperFilter == item.key }"
              @click="selectFilter(item.key)"
            >
              <img class="paper-menu-icon" :src="item.icon" alt="" />
              <span>{{ item.label }}</span>
              <img v-if="paperFilter == item.key" class="filter-check" :src="paperIcons.check" alt="" />
            </button>
          </div>
        </div>

        <div class="paper-toolbar-right">
          <div v-if="!isPaperRootLevel" class="paper-sort" @click.stop>
            <button class="paper-sort-button" type="button" @click="sortMenuOpen = !sortMenuOpen">{{ activeSortLabel }}</button>
            <div v-if="sortMenuOpen" class="paper-sort-menu">
              <button
                v-for="item in sortOptions"
                :key="item.key"
                type="button"
                :class="{ active: sortField == item.key }"
                @click="selectSort(item.key)"
              >
                <span>{{ item.label }}</span>
                <img v-if="sortField == item.key" class="filter-check" :src="paperIcons.check" alt="" />
              </button>
            </div>
          </div>
          <button v-if="!isPaperRootLevel" class="paper-order-button" type="button" :title="sortDesc ? ui.desc : ui.asc" @click.stop="toggleSortOrder">
            <img :src="sortDesc ? paperIcons.sortDesc : paperIcons.sortAsc" alt="" />
          </button>
          <div class="paper-view-switch" role="group">
            <img :src="viewSwitchIcon" alt="" />
            <button
              type="button"
              class="paper-view-hotspot paper-view-list"
              :aria-label="ui.listView"
              @click.stop="setViewMode('list')"
            ></button>
            <button
              type="button"
              class="paper-view-hotspot paper-view-grid"
              :aria-label="ui.gridView"
              @click.stop="setViewMode('grid')"
            ></button>
          </div>
        </div>
      </div>

      <div v-if="searchOpen && !hasPaperSelection" class="paper-search-line" @click.stop>
        <img :src="paperIcons.search" alt="" />
        <input ref="paperSearchInput" v-model.trim="searchKeyword" type="text" :placeholder="ui.search" maxlength="125" />
        <button v-if="searchKeyword" type="button" @click="searchKeyword = ''"></button>
      </div>

      <section ref="paperListArea" class="paper-list-area" :class="{ 'is-grid': viewMode == 'grid' }" @scroll.passive="handlePaperListScroll">
        <a-spin :spinning="paperListLoading" wrapper-class-name="paper-list-spin">
          <div class="paper-list-content">
            <div v-if="!paperListLoading && visiblePaperItems.length == 0" class="paper-empty">
              <img :src="paperIcons.recordEmpty" alt="" />
              <p>{{ ui.empty }}</p>
            </div>

            <div v-if="!paperListLoading && viewMode == 'grid' && visiblePaperItems.length > 0" ref="paperGrid" class="paper-grid">
              <div
                v-for="(item, index) in visiblePaperItems"
                :key="item.id"
                class="paper-card"
                :class="getPaperGridItemClasses(item, index)"
                role="button"
                tabindex="0"
                @click="handlePaperItemClick(item, $event)"
                @dblclick.stop.prevent="handlePaperItemDblClick(item, $event)"
                @keyup.enter="handlePaperItemKeyup(item)"
              >
                <span class="paper-cover" :class="{ empty: !shouldDisplayGridCover(item), locked: item.encrypted, template: item.template }">
                  <img v-if="shouldDisplayGridCover(item)" class="paper-cover-image" :src="getPaperGridCoverImageUrl(item)" alt="" loading="lazy" decoding="async" @error="markPaperGridCoverFailed(item)" />
                  <img v-else class="paper-cover-default" :class="{ 'folder-icon': item.isFolder && !item.encrypted, 'encrypt-icon': item.isFolder && item.encrypted }" :src="getFallbackCover(item, 'grid')" alt="" />
                </span>
                <span class="paper-card-title-row">
                  <span class="paper-card-name" :title="getKnowledgeBaseDisplayName(item)">{{ getKnowledgeBaseDisplayName(item) }}</span>
                  <img v-if="item.favorite" class="paper-favorite-status paper-card-favorite-status" :src="paperIcons.favoriteFilled" :alt="ui.favorite" />
                </span>
                <span class="paper-card-meta">
                  <span>{{ formatDate(getDisplayDate(item)) }}</span>
                  <span>{{ formatPageCount(item) }}</span>
                </span>
              </div>
            </div>

            <div v-if="!paperListLoading && viewMode == 'list' && visiblePaperItems.length > 0" class="paper-table">
              <div
                v-for="item in visiblePaperItems"
                :key="item.id"
                class="paper-row"
                :class="{ selected: isPaperSelected(item) }"
                role="button"
                tabindex="0"
                :title="getKnowledgeBaseDisplayName(item)"
                @click="handlePaperItemClick(item, $event)"
                @dblclick.stop.prevent="handlePaperItemDblClick(item, $event)"
                @keyup.enter="handlePaperItemKeyup(item)"
              >
                <span class="paper-row-cover" :class="{ empty: !shouldDisplayCover(item), locked: item.encrypted, 'has-cover': shouldDisplayCover(item) }">
                  <img v-if="shouldDisplayCover(item)" class="paper-row-cover-image" :src="item.cover" alt="" loading="lazy" decoding="async" @error="markCoverFailed(item)" />
                  <img v-else class="paper-row-cover-default" :class="{ 'folder-icon': item.isFolder && !item.encrypted, 'encrypt-icon': item.encrypted }" :src="getFallbackCover(item, 'list')" alt="" />
                </span>
                <strong>{{ getKnowledgeBaseDisplayName(item) }}</strong>
                <span class="paper-row-count">{{ formatPageCount(item) }}</span>
                <span class="paper-row-date">{{ formatDate(getDisplayDate(item)) }}</span>
                <span class="paper-row-favorite-status">
                  <img v-if="item.favorite" class="paper-favorite-status" :src="paperIcons.favoriteFilled" :alt="ui.favorite" />
                </span>
                <span v-if="!isPaperRootLevel" class="paper-more-wrap" @click.stop>
                  <button class="paper-more-button" type="button" :title="ui.more" @click="toggleMoreMenu(item.id)">
                    <svg class="paper-more-icon" viewBox="0 0 12 4" aria-hidden="true" focusable="false">
                      <circle cx="2" cy="2" r="1.25"></circle>
                      <circle cx="6" cy="2" r="1.25"></circle>
                      <circle cx="10" cy="2" r="1.25"></circle>
                    </svg>
                  </button>
                  <div v-if="moreMenuId == item.id" class="paper-more-menu">
                    <button type="button" :disabled="!canPerformPaperAction('rename', [item])" @click="handleItemMenuAction('rename', item)">
                      <img :src="paperIcons.actionRename" alt="" />
                      <span>{{ ui.rename }}</span>
                    </button>
                    <button v-if="canUseKnowledgeBaseMove" type="button" :disabled="!canPerformPaperAction('move', [item])" @click="handleItemMenuAction('move', item)">
                      <img :src="paperIcons.move" alt="" />
                      <span>{{ ui.move }}</span>
                    </button>
                    <button type="button" :disabled="!isPaperDownloadableItem(item)" @click="handleItemMenuAction('download', item)">
                      <img :src="paperIcons.actionDownload" alt="" />
                      <span>{{ ui.download }}</span>
                    </button>
                    <button type="button" :disabled="!isPaperDeletableItem(item)" @click="handleItemMenuAction('delete', item)">
                      <img :src="paperIcons.actionDelete" alt="" />
                      <span>{{ ui.delete }}</span>
                    </button>
                  </div>
                </span>
              </div>
            </div>
            <div v-if="paperListAppendLoading" class="list-bottom-loading">
              <a-spin size="small" :spinning="true" />
              <span>{{ ui.loading }}</span>
            </div>
          </div>
        </a-spin>
      </section>
    </template>

    <section
      v-else
      class="paper-detail-body"
      :class="{
        'pages-open': pageGridOpen,
        'is-resolving': paperDetailLoading,
        'is-knowledge-ai-note-preview': isKnowledgeBaseAppAiNotePreview
      }"
    >
      <div v-if="paperDetailLoading || paperDetailError" class="paper-detail-resolving">
        <div class="paper-detail-progress-box" :class="{ error: paperDetailError }">
          <strong>{{ paperDetailError ? ui.loadFailed : ui.loading }}</strong>
          <span v-if="!paperDetailError">{{ paperDetailProgress }}%</span>
          <div v-if="!paperDetailError" class="paper-detail-progress-track">
            <i :style="{ width: paperDetailProgress + '%' }"></i>
          </div>
          <p v-if="paperDetailError">{{ paperDetailError }}</p>
          <button v-if="paperDetailError" type="button" @click.stop="retryPaperDetailLoad">{{ ui.retry }}</button>
        </div>
      </div>
      <template v-else>
        <div v-if="detailPages.length == 0" class="paper-detail-empty">
          <img :src="paperIcons.recordEmpty" alt="" />
          <p>{{ noContentText }}</p>
        </div>
        <div v-else-if="pageGridOpen" ref="paperPagesGrid" class="paper-pages-grid">
          <button
            v-for="(page, index) in detailPages"
            :key="page.pageNo"
            type="button"
            :class="{ active: page.pageNo == currentPageNo, selected: isDetailPageSelected(page.pageNo), 'grid-edge-top': isPaperPagesGridTopEdge(index), 'grid-selected-row-start': isPaperPagesGridSelectedRowStart(index), 'grid-selection-continues-below': isPaperPagesGridSelectionContinuesBelow(index) }"
            @click="toggleDetailPageSelection(page.pageNo, $event)"
            @dblclick="openDetailPageFromGrid(page.pageNo)"
          >
            <span>
              <img v-if="isKnowledgeBaseImageOnlyPage(page)" :src="page.imageUrl" alt="" loading="lazy" decoding="async" />
              <div v-else-if="isKnowledgeBaseTextPage(page)" class="knowledge-page-thumb" :class="{ 'has-image': page.imageUrl }">
                <b>{{ getKnowledgeBasePageTitle(page) }}</b>
                <img v-if="page.imageUrl" class="knowledge-page-thumb-image" :src="page.imageUrl" alt="" loading="lazy" decoding="async" />
                <p>{{ getKnowledgeBasePagePreviewText(page) }}</p>
              </div>
              <em v-else class="page-fallback" :class="{ pdf: page.pdfPreviewUrl }">{{ page.pdfPreviewUrl ? 'PDF' : '' }}</em>
            </span>
            <strong :title="getKnowledgeBasePageLabel(page)">{{ getKnowledgeBasePageLabel(page) }}</strong>
          </button>
        </div>
        <div
          v-else
          ref="paperPreviewShell"
          class="paper-preview-shell"
          :class="{ dragging: paperPreviewDragState }"
          :style="{ '--paper-preview-zoom': zoomValue }"
          @mousedown="startPaperPreviewDrag"
        >
          <div class="paper-preview-zoom">
            <div class="paper-preview-stage">
              <iframe v-if="currentPage && currentPage.pdfPreviewUrl" class="paper-preview-pdf" :src="currentPage.pdfPreviewUrl" title="PDF" @mousedown.stop></iframe>
              <img v-else-if="isKnowledgeBaseImageOnlyPage(currentPage)" :key="currentPage.imageUrl" class="paper-preview-page-image" :class="{ 'is-loading': isPaperPreviewPageImageLoading(currentPage) }" :src="currentPage.imageUrl" alt="" decoding="async" fetchpriority="high" @load="handlePaperPreviewPageImageLoad($event, currentPage)" @error="handlePaperPreviewPageImageError($event, currentPage)" @dragstart.prevent />
              <div
                v-else-if="isKnowledgeBaseTextPage(currentPage)"
                :key="`knowledge-text-page-${currentPage && currentPage.pageNo ? currentPage.pageNo : currentPageIndex}`"
                ref="knowledgeTextPage"
                class="knowledge-text-page"
              >
                <h2>{{ getKnowledgeBasePageTitle(currentPage) }}</h2>
                <div v-if="currentPage && currentPage.imageUrl" class="knowledge-text-image-block">
                  <img :src="currentPage.imageUrl" alt="" decoding="async" @load="handlePaperPreviewPageImageLoad($event, currentPage)" @error="handlePaperPreviewPageImageError($event, currentPage)" @dragstart.prevent />
                </div>
                <div v-if="getKnowledgeBasePageRemarkDisplayText(currentPage)" class="knowledge-text-remark">{{ getKnowledgeBasePageRemarkDisplayText(currentPage) }}</div>
                <div class="knowledge-text-content" v-html="renderKnowledgeBaseMarkdownContent(currentPage && currentPage.content)"></div>
              </div>
              <div v-else class="paper-preview-empty">
                <img :src="paperIcons.recordEmpty" alt="" />
                <p>{{ noContentText }}</p>
              </div>
              <div v-if="isKnowledgeBaseImageOnlyPage(currentPage) && isPaperPreviewPageImageLoading(currentPage)" class="paper-preview-page-loading" aria-hidden="true">
                <a-spin :spinning="true" class="paper-preview-page-loading-spin" />
              </div>
            </div>
          </div>
        </div>
      </template>

      <div v-if="!pageGridOpen && detailPages.length > 0" class="paper-page-controls">
        <div class="paper-page-count">
          <button type="button" :disabled="isPaperPreviewNavigationLocked() || currentPageIndex <= 0" @click="goPrevPage"></button>
          <span>{{ currentPageIndex + 1 }}/{{ detailPages.length }}</span>
          <button type="button" :disabled="isPaperPreviewNavigationLocked() || currentPageIndex >= detailPages.length - 1" @click="goNextPage"></button>
        </div>
      </div>

      <div v-if="recordPanelOpen" class="paper-record-mask" @click="closeRecordPanel"></div>
      <section v-if="recordPanelOpen" class="paper-record-panel" @click.stop>
        <button class="paper-record-close" type="button" :aria-label="ui.close" @click="closeRecordPanel">
          <span></span>
        </button>
        <template v-if="!recordTextOpen">
          <header>{{ ui.allRecordings }}</header>
          <div v-if="detailRecordings.length > 0" class="record-list">
            <div v-for="recording in detailRecordings" :key="recording.id" class="record-row">
              <img class="record-icon" :src="paperIcons.record" alt="" />
              <div class="record-main">
                <strong>{{ recording.recordingName }}</strong>
                <span>{{ formatDuration(recording.duration) }} <em></em> {{ formatDateTime(recording.recordedAtMs) }}</span>
              </div>
              <button
                class="record-action"
                type="button"
                :disabled="!hasTranscript(recording)"
                :title="ui.transcript"
                @click="openRecordingText(recording)"
              >
                <img :src="paperIcons.transcript" alt="" />
              </button>
              <button class="record-action" type="button" :title="ui.download" @click="downloadUrl(recording.fileUrl, getRecordingDownloadName(recording))">
                <img :src="paperIcons.download" alt="" />
              </button>
            </div>
          </div>
          <div v-else class="record-empty">
            <img :src="paperIcons.recordEmpty" alt="" />
          </div>
        </template>

        <template v-else>
          <header class="record-text-head">
            <button type="button" @click="closeRecordingText">
              <span></span>
            </button>
            <strong>{{ activeRecordingName }}</strong>
          </header>
          <div class="record-text-body">
            {{ activeRecordingText }}
          </div>
        </template>
      </section>
    </section>

    <div v-if="paperPasswordModalOpen" class="paper-password-mask" @click="closePaperPasswordModal"></div>
    <section v-if="paperPasswordModalOpen" class="paper-password-modal" @click.stop>
      <header>{{ ui.passwordTitle }}</header>
      <div class="paper-password-field" :class="{ error: paperPasswordError }">
        <input
          ref="paperPasswordInput"
          v-model="paperPasswordInput"
          :type="paperPasswordVisible ? 'text' : 'password'"
          :placeholder="ui.passwordPlaceholder"
          maxlength="125"
          :disabled="paperPasswordSubmitting"
          @keyup.enter="confirmPaperPassword"
        />
        <button type="button" :disabled="paperPasswordSubmitting" :aria-label="paperPasswordVisible ? ui.passwordHide : ui.passwordShow" @click="togglePaperPasswordVisible">
          <span :class="{ visible: paperPasswordVisible }"></span>
        </button>
      </div>
      <p v-if="paperPasswordError" class="paper-password-error">{{ paperPasswordError }}</p>
      <footer>
        <div>
          <button type="button" :disabled="paperPasswordSubmitting" @click="closePaperPasswordModal">{{ ui.cancel }}</button>
          <button type="button" :disabled="paperPasswordSubmitting" @click="confirmPaperPassword">{{ paperPasswordSubmitting ? ui.passwordConfirming : ui.ok }}</button>
        </div>
      </footer>
    </section>

    <div v-if="paperDownloadFormatModalOpen" class="paper-download-mask" @click="closePaperDownloadFormatModal"></div>
    <section v-if="paperDownloadFormatModalOpen" class="paper-download-modal" @click.stop>
      <header>
        <strong>{{ ui.chooseFormat }}</strong>
        <button type="button" :aria-label="ui.close" @click="closePaperDownloadFormatModal">
          <span></span>
        </button>
      </header>
      <div class="paper-download-options">
        <label>
          <input v-model="paperDownloadFormat" type="radio" value="pdf" />
          <span>PDF</span>
        </label>
        <label>
          <input v-model="paperDownloadFormat" type="radio" value="png" />
          <span>PNG</span>
        </label>
      </div>
      <footer>
        <button type="button" @click="closePaperDownloadFormatModal">{{ ui.cancel }}</button>
        <button type="button" @click="confirmPaperDownloadFormat">{{ ui.ok }}</button>
      </footer>
    </section>

    <div v-if="paperRenameModalOpen" class="paper-rename-mask" @click="closePaperRenameModal"></div>
    <section v-if="paperRenameModalOpen" class="paper-rename-modal" @click.stop>
      <header>
        <strong>{{ ui.rename }}</strong>
        <button type="button" :disabled="paperRenameSubmitting" :aria-label="ui.close" @click="closePaperRenameModal">
          <span></span>
        </button>
      </header>
      <div class="paper-rename-field" :class="{ disabled: paperRenameSubmitting }">
        <input
          ref="paperRenameInput"
          v-model="paperRenameInput"
          class="paper-rename-input"
          type="text"
          maxlength="255"
          :disabled="paperRenameSubmitting"
          @input="limitPaperRenameInput"
          @keyup.enter="confirmPaperRename"
        />
        <button
          v-if="paperRenameInput"
          class="paper-rename-clear"
          type="button"
          :disabled="paperRenameSubmitting"
          :aria-label="ui.clear || ui.cancel"
          @mousedown.prevent
          @click="clearPaperRenameInput"
        >
          <span></span>
        </button>
      </div>
      <footer>
        <button type="button" :disabled="paperRenameSubmitting" @click="closePaperRenameModal">{{ ui.cancel }}</button>
        <button type="button" :disabled="paperRenameSubmitting" @click="confirmPaperRename">{{ paperRenameSubmitting ? ui.loading : ui.ok }}</button>
      </footer>
    </section>

    <div v-if="paperCreateFolderModalOpen" class="paper-rename-mask" @click="closePaperCreateFolderModal"></div>
    <section v-if="paperCreateFolderModalOpen" class="paper-rename-modal" @click.stop>
      <header>
        <strong>{{ ui.createFolder }}</strong>
        <button type="button" :disabled="paperCreateFolderSubmitting" :aria-label="ui.close" @click="closePaperCreateFolderModal">
          <span></span>
        </button>
      </header>
      <div class="paper-rename-field" :class="{ disabled: paperCreateFolderSubmitting }">
        <input
          ref="paperCreateFolderInput"
          v-model="paperCreateFolderInput"
          class="paper-rename-input"
          type="text"
          maxlength="255"
          :disabled="paperCreateFolderSubmitting"
          @input="limitPaperCreateFolderInput"
          @keyup.enter="confirmPaperCreateFolder"
        />
        <button
          v-if="paperCreateFolderInput"
          class="paper-rename-clear"
          type="button"
          :disabled="paperCreateFolderSubmitting"
          :aria-label="ui.clear || ui.cancel"
          @mousedown.prevent
          @click="clearPaperCreateFolderInput"
        >
          <span></span>
        </button>
      </div>
      <footer>
        <button type="button" :disabled="paperCreateFolderSubmitting" @click="closePaperCreateFolderModal">{{ ui.cancel }}</button>
        <button type="button" :disabled="paperCreateFolderSubmitting" @click="confirmPaperCreateFolder">{{ paperCreateFolderSubmitting ? paperCreateFolderSubmittingText : ui.ok }}</button>
      </footer>
    </section>

    <div v-if="paperMoveModalOpen" class="paper-move-mask" @click="closePaperMoveModal"></div>
    <section v-if="paperMoveModalOpen" class="paper-move-modal" @click.stop>
      <header>
        <strong>{{ ui.moveTo || ui.move }}</strong>
        <button type="button" :disabled="paperMoveSubmitting" :aria-label="ui.close" @click="closePaperMoveModal">
          <span></span>
        </button>
      </header>
      <nav class="paper-move-breadcrumb" :aria-label="ui.moveTo || ui.move">
        <template v-for="(crumb, index) in paperMoveBreadcrumbDisplayItems">
          <span
            v-if="crumb.collapsed"
            :key="crumb.key"
            class="paper-move-breadcrumb-ellipsis"
            :title="paperMoveBreadcrumbTitle"
          >...</span>
          <button
            v-else
            :key="crumb.key"
            type="button"
            :class="{ active: crumb.originalIndex == paperMoveBreadcrumbItems.length - 1 }"
            :disabled="paperMoveSubmitting || paperMoveLoading || crumb.originalIndex == paperMoveBreadcrumbItems.length - 1 || isPaperMoveBreadcrumbLocked(crumb.originalIndex)"
            :title="crumb.name"
            @click="openPaperMoveBreadcrumb(crumb.originalIndex)"
          >{{ crumb.name }}</button>
          <img
            v-if="index < paperMoveBreadcrumbDisplayItems.length - 1"
            :key="crumb.key + '-separator'"
            class="paper-move-breadcrumb-separator"
            :src="paperIcons.breadcrumbSeparator"
            alt=""
          />
        </template>
      </nav>
      <div ref="paperMoveFolderList" class="paper-move-folder-list" @scroll.passive="handlePaperMoveFolderScroll">
        <div v-if="paperMoveLoading" class="paper-move-loading">
          <a-spin :spinning="true" />
        </div>
        <div v-else-if="paperMoveFolders.length == 0" class="paper-move-empty">{{ ui.empty }}</div>
        <template v-else>
          <button
            v-for="folder in paperMoveFolders"
            :key="folder.id"
            type="button"
            class="paper-move-folder-row"
            :class="{ selected: isPaperMoveFolderSelected(folder), disabled: isPaperMoveFolderDisabled(folder) }"
            :disabled="paperMoveSubmitting || isPaperMoveFolderDisabled(folder)"
            :title="folder.name"
            @click="selectPaperMoveFolder(folder)"
            @dblclick.stop.prevent="enterPaperMoveFolder(folder)"
          >
            <img :src="getPaperMoveFolderIcon(folder)" alt="" />
            <span>{{ folder.name }}</span>
          </button>
        </template>
        <div v-if="paperMoveAppendLoading" class="paper-move-bottom-loading">
          <a-spin size="small" :spinning="true" />
          <span>{{ ui.loading }}</span>
        </div>
      </div>
      <footer>
        <button class="paper-move-create-button" type="button" :disabled="paperMoveSubmitting" @click="openPaperMoveCreateFolderModal">
          <img :src="paperIcons.createFolder" alt="" />
          <span>{{ ui.createFolder }}</span>
        </button>
        <div class="paper-move-footer-actions">
          <button type="button" :disabled="paperMoveSubmitting" @click="closePaperMoveModal">{{ ui.cancel }}</button>
          <button type="button" :disabled="paperMoveConfirmDisabled" @click="confirmPaperMove">{{ paperMoveSubmitting ? ui.loading : ui.ok }}</button>
        </div>
      </footer>
    </section>

    <div v-if="paperMoveCreateFolderModalOpen" class="paper-rename-mask paper-move-create-mask" @click="closePaperMoveCreateFolderModal"></div>
    <section v-if="paperMoveCreateFolderModalOpen" class="paper-rename-modal paper-move-create-modal" @click.stop>
      <header>
        <strong>{{ ui.createFolder }}</strong>
        <button type="button" :disabled="paperMoveCreateFolderSubmitting" :aria-label="ui.close" @click="closePaperMoveCreateFolderModal">
          <span></span>
        </button>
      </header>
      <div class="paper-rename-field" :class="{ disabled: paperMoveCreateFolderSubmitting }">
        <input
          ref="paperMoveCreateFolderInput"
          v-model="paperMoveCreateFolderInput"
          class="paper-rename-input"
          type="text"
          maxlength="255"
          :disabled="paperMoveCreateFolderSubmitting"
          @input="limitPaperMoveCreateFolderInput"
          @keyup.enter="confirmPaperMoveCreateFolder"
        />
        <button
          v-if="paperMoveCreateFolderInput"
          class="paper-rename-clear"
          type="button"
          :disabled="paperMoveCreateFolderSubmitting"
          :aria-label="ui.clear || ui.cancel"
          @mousedown.prevent
          @click="clearPaperMoveCreateFolderInput"
        >
          <span></span>
        </button>
      </div>
      <footer>
        <button type="button" :disabled="paperMoveCreateFolderSubmitting" @click="closePaperMoveCreateFolderModal">{{ ui.cancel }}</button>
        <button type="button" :disabled="paperMoveCreateFolderSubmitting" @click="confirmPaperMoveCreateFolder">{{ paperMoveCreateFolderSubmitting ? paperMoveCreateFolderSubmittingText : ui.ok }}</button>
      </footer>
    </section>

    <div v-if="paperDeleteModalOpen" class="paper-delete-mask" @click="closePaperDeleteModal"></div>
    <section v-if="paperDeleteModalOpen" class="paper-delete-modal" @click.stop>
      <header>
        <strong>{{ ui.delete }}</strong>
      </header>
      <p :title="paperDeleteConfirmText">{{ paperDeleteConfirmText }}</p>
      <footer>
        <button type="button" :disabled="paperDeleteSubmitting" @click="closePaperDeleteModal">{{ ui.cancel }}</button>
        <button type="button" :disabled="paperDeleteSubmitting" @click="confirmPaperDelete">{{ paperDeleteSubmitting ? ui.loading : ui.ok }}</button>
      </footer>
    </section>
  </div>
</template>

<script>
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import pako from 'pako'
import MarkdownIt from 'markdown-it'
import html2canvas from 'html2canvas'
import pdfjsLib from '@/utils/pdfjs'
import { getKnowledgeBaseSyncList, getKnowledgeBaseSyncDetail , checkCloudPassword, deleteWebSyncNote, deleteWebSyncFolder, renameWebSyncResource, createWebSyncFolder, getResourceSyncFolderPage, moveWebSyncResource } from '@/api/api'
import downloadCenterMixin from '@/mixins/downloadCenterMixin'
import paperListPaginationMixin from '@/mixins/paperListPaginationMixin'
import cloudListStateMixin from '@/mixins/cloudListStateMixin'
import directFilePreviewMixin from '@/mixins/directFilePreviewMixin'
import previewPerformanceMixin from '@/mixins/previewPerformanceMixin'
import pcPreviewEntryPerformanceMixin from '@/mixins/pcPreviewEntryPerformanceMixin'
import pcRowActionMenuMixin from '@/mixins/pcRowActionMenuMixin'
import { getAppUiText, normalizeLanguage } from '@/utils/language/common'
import { selectItemWithWindowsRange } from '@/utils/rangeSelection'

const MOCK_PAGE_IMAGE = require('@/assets/pc/knowledge-base/mock-page.png')
const MOCK_SMALL_PAGE_IMAGE = require('@/assets/pc/knowledge-base/mock-small-page.png')
const MOCK_TEMPLATE_IMAGE = require('@/assets/pc/knowledge-base/template-placeholder.png')
const ICON_CREATE_FOLDER = require('@/assets/pc/knowledge-base/create-folder.png')
const ICON_SEARCH = require('@/assets/pc/knowledge-base/search.png')
const ICON_IMPORT = require('@/assets/pc/knowledge-base/import-pdf.png')
const ICON_VIEW_GRID = require('@/assets/pc/knowledge-base/view-grid.png')
const ICON_VIEW_LIST = require('@/assets/pc/knowledge-base/view-list.png')
const ICON_SORT_DESC = require('@/assets/pc/knowledge-base/sort-desc.png')
const ICON_SORT_ASC = require('@/assets/pc/knowledge-base/sort-asc.png')
const ICON_BREADCRUMB_SEPARATOR = require('@/assets/pc/knowledge-base/more.png')
const ICON_FILTER_ALL = require('@/assets/pc/knowledge-base/filter-all.png')
const ICON_FILTER_FAVORITE = require('@/assets/pc/knowledge-base/favorite.png')
const ICON_FILTER_DELETED = require('@/assets/pc/knowledge-base/delete.png')
const ICON_CHECK = require('@/assets/pc/knowledge-base/check.png')
const ICON_DOWNLOAD = require('@/assets/pc/knowledge-base/download.png')
const ICON_RENAME = require('@/assets/pc/knowledge-base/rename.png')
const ICON_MOVE = require('@/assets/pc/knowledge-base/move.png')
const ICON_ENCRYPT_MENU = require('@/assets/pc/knowledge-base/encrypt-file.png')
const ICON_DELETE = require('@/assets/pc/knowledge-base/delete.png')
const ACTION_ICON_DOWNLOAD = require('@/assets/pc/action-download.png')
const ACTION_ICON_RENAME = require('@/assets/pc/action-rename.png')
const ACTION_ICON_DELETE = require('@/assets/pc/action-delete.png')
const ICON_FAVORITE = require('@/assets/pc/knowledge-base/favorite.png')
const ICON_FAVORITE_FILLED = require('@/assets/pc/knowledge-base/favorite-filled.png')
const ICON_FOLDER_LIST_FILLED = require('@/assets/pc/knowledge-base/list-folder-filled.png')
const ICON_FOLDER_LIST_EMPTY = require('@/assets/pc/knowledge-base/list-folder-empty.png')
const ICON_FOLDER_GRID_FILLED = require('@/assets/pc/knowledge-base/grid-folder-filled.png')
const ICON_FOLDER_GRID_EMPTY = require('@/assets/pc/knowledge-base/grid-folder-empty.png')
const ICON_TEMPLATE_LIST_FILLED = require('@/assets/pc/knowledge-base/list-template-filled.png')
const ICON_TEMPLATE_LIST_EMPTY = require('@/assets/pc/knowledge-base/list-template-empty.png')
const ICON_TEMPLATE_GRID_FILLED = require('@/assets/pc/knowledge-base/grid-template-filled.png')
const ICON_TEMPLATE_GRID_EMPTY = require('@/assets/pc/knowledge-base/grid-template-empty.png')
const ICON_FILE_LIST_PDF = require('@/assets/pc/knowledge-base/list-pdf.png')
const ICON_FILE_LIST_PAPER = require('@/assets/pc/knowledge-base/list-paper.png')
const ICON_FILE_LIST_ENCRYPT = require('@/assets/pc/knowledge-base/list-encrypt.png')
const ICON_FILE_GRID_PDF = require('@/assets/pc/knowledge-base/grid-pdf.png')
const ICON_FILE_GRID_PAPER = require('@/assets/pc/knowledge-base/grid-paper.png')
const ICON_FILE_GRID_ENCRYPT = require('@/assets/pc/knowledge-base/grid-encrypt.png')
const ICON_RESOURCE_TYPE_1_2 = require('@/assets/pc/knowledge-base/resource-type-1-2.png')
const ICON_RESOURCE_TYPE_3 = require('@/assets/pc/knowledge-base/resource-type-3.png')
const ICON_PAGE_GRID = require('@/assets/pc/knowledge-base/page-grid.png')
const ICON_ZOOM_IN = require('@/assets/pc/knowledge-base/zoom-in.png')
const ICON_ZOOM_OUT = require('@/assets/pc/knowledge-base/zoom-out.png')
const ICON_CLOSE = require('@/assets/pc/knowledge-base/close-plain.png')
const RECORD_ICON = require('@/assets/pc/knowledge-base/record-file.png')
const TRANSCRIPT_ICON = require('@/assets/pc/knowledge-base/record-transcript.png')
const RECORD_EMPTY_ICON = require('@/assets/pc/knowledge-base/record-empty.png')
const KNOWLEDGE_BASE_APP_TYPE = 4
const KNOWLEDGE_BASE_MARKDOWN = new MarkdownIt({
  html: false,
  breaks: true,
  linkify: true,
  typographer: false,
  langPrefix: 'language-'
})
const DEFAULT_MARKDOWN_LINK_OPEN = KNOWLEDGE_BASE_MARKDOWN.renderer.rules.link_open || function (tokens, idx, options, env, self) {
  return self.renderToken(tokens, idx, options)
}
KNOWLEDGE_BASE_MARKDOWN.renderer.rules.link_open = function (tokens, idx, options, env, self) {
  const token = tokens[idx]
  token.attrSet('target', '_blank')
  token.attrSet('rel', 'noopener noreferrer')
  return DEFAULT_MARKDOWN_LINK_OPEN(tokens, idx, options, env, self)
}
const KNOWLEDGE_BASE_FIXED_FOLDER_LABELS = {
  picknotes: {
    zh: '\u968f\u8bb0',
    tw: '\u96a8\u8a18',
    nl: 'Notities',
    uk: 'PickNotes',
    us: 'PickNotes',
    fr: 'Notes rapides',
    de: 'Notizen',
    it: 'Note rapide',
    ja: '\u30e1\u30e2',
    ko: '\uba54\ubaa8',
    pt: 'Notas r\u00e1pidas',
    ru: '\u0417\u0430\u043c\u0435\u0442\u043a\u0438',
    es: 'Notas r\u00e1pidas'
  },
  visualnotes: {
    zh: '\u56fe\u6587',
    tw: '\u5716\u6587',
    nl: 'Afbeelding en tekst',
    uk: 'Image & Text',
    us: 'Image & Text',
    fr: 'Image et texte',
    de: 'Bild und Text',
    it: 'Immagine e testo',
    ja: '\u753b\u50cf\u3068\u30c6\u30ad\u30b9\u30c8',
    ko: '\uc774\ubbf8\uc9c0 \ubc0f \ud14d\uc2a4\ud2b8',
    pt: 'Imagem e texto',
    ru: '\u0418\u0437\u043e\u0431\u0440\u0430\u0436\u0435\u043d\u0438\u0435 \u0438 \u0442\u0435\u043a\u0441\u0442',
    es: 'Imagen y texto'
  },
  ainotes: {
    zh: 'Ai\u7b14\u8bb0',
    tw: 'AI\u7b46\u8a18',
    nl: 'AI-notities',
    uk: 'AI Notes',
    us: 'AI Notes',
    fr: 'Notes IA',
    de: 'KI-Notizen',
    it: 'Note AI',
    ja: 'AI\u30ce\u30fc\u30c8',
    ko: 'AI \ub178\ud2b8',
    pt: 'Notas de IA',
    ru: 'AI-\u0437\u0430\u043c\u0435\u0442\u043a\u0438',
    es: 'Notas de IA'
  }
}
const KNOWLEDGE_BASE_NO_CONTENT_LABELS = {
  zh: '\u6682\u65e0\u5185\u5bb9',
  tw: '\u66ab\u7121\u5167\u5bb9',
  nl: 'Geen inhoud',
  uk: 'No content',
  us: 'No content',
  fr: 'Aucun contenu',
  de: 'Kein Inhalt',
  it: 'Nessun contenuto',
  ja: '\u30b3\u30f3\u30c6\u30f3\u30c4\u304c\u3042\u308a\u307e\u305b\u3093',
  ko: '\ucf58\ud150\uce20\uac00 \uc5c6\uc2b5\ub2c8\ub2e4',
  pt: 'Sem conte\u00fado',
  ru: '\u041d\u0435\u0442 \u0441\u043e\u0434\u0435\u0440\u0436\u0438\u043c\u043e\u0433\u043e',
  es: 'Sin contenido'
}

export default {
  name: 'KnowledgeBaseSync',
  downloadPdfPageSize: 'a4',
  paperPreviewPagePreload: true,
  mixins: [downloadCenterMixin, cloudListStateMixin, paperListPaginationMixin, directFilePreviewMixin, previewPerformanceMixin, pcPreviewEntryPerformanceMixin, pcRowActionMenuMixin],
  props: {
    language: {
      type: String,
      default: 'us'
    },
    detailBackRoot: {
      type: Boolean,
      default: false
    },
    initialRefreshState: {
      type: Object,
      default: null
    },
    skipInitialListLoad: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      paperListLoading: false,
      paperDetailLoading: false,
      paperDetailProgress: 0,
      paperDetailError: '',
      paperItems: [],
      paperFilter: 'all',
      filterMenuOpen: false,
      sortMenuOpen: false,
      sortField: 'createTime',
      sortDesc: true,
      viewMode: 'list',
      searchOpen: false,
      searchKeyword: '',
      paperPasswordModalOpen: false,
      paperPasswordTarget: null,
      paperPasswordInput: '',
      paperPasswordVisible: false,
      paperPasswordError: '',
      paperPasswordSubmitting: false,
      paperPasswordRequestSeq: 0,
      paperPasswordAction: 'open',
      paperPasswordItems: [],
      paperPasswordOpenOptions: null,
      paperDownloadFormatModalOpen: false,
      paperDownloadFormat: 'pdf',
      paperDownloadItems: [],
      paperDownloadPassword: '',
      paperDownloadFormatContext: 'items',
      paperDownloadDetailPages: [],
      Visible: true,
      paperRenameModalOpen: false,
      paperRenameTarget: null,
      paperRenameInput: '',
      paperRenameOriginalName: '',
      paperRenameSubmitting: false,
      paperRenameRequestSeq: 0,
      paperCreateFolderModalOpen: false,
      paperCreateFolderInput: '',
      paperCreateFolderSubmitting: false,
      paperCreateFolderElapsedSeconds: 0,
      paperCreateFolderStartedAt: 0,
      paperCreateFolderWaitSeconds: 5,
      paperCreateFolderTimer: null,
      paperCreateFolderRequestSeq: 0,
      paperMoveModalOpen: false,
      paperMoveItems: [],
      paperMoveSubmitting: false,
      paperMoveFolders: [],
      paperMoveSelectedFolder: null,
      paperMoveFolderPath: [],
      paperMoveResourceId: '',
      paperMovePageIndex: 1,
      paperMovePageSize: 100,
      paperMoveTotalCount: 0,
      paperMoveLastPageSize: 0,
      paperMoveReachedEnd: false,
      paperMoveLoading: false,
      paperMoveAppendLoading: false,
      paperMovePendingLoad: null,
      paperMoveRequestSeq: 0,
      paperMoveCreateFolderModalOpen: false,
      paperMoveCreateFolderInput: '',
      paperMoveCreateFolderSubmitting: false,
      paperMoveCreateFolderElapsedSeconds: 0,
      paperMoveCreateFolderStartedAt: 0,
      paperMoveCreateFolderWaitSeconds: 5,
      paperMoveCreateFolderTimer: null,
      paperMoveCreateFolderRequestSeq: 0,
      paperDeleteModalOpen: false,
      paperDeleteItems: [],
      paperDeleteSubmitting: false,
      selectedPaperId: '',
      selectedPaperIds: [],
      selectedPaperAnchorId: '',
      paperItemClickTimer: null,
      moreMenuId: '',
      detailMode: false,
      currentPaper: null,
      currentPaperPassword: '',
      paperDetail: null,
      paperDetailRequestSeq: 0,
      paperPreviewObjectUrls: [],
      paperDetailCache: {},
      paperListReturnScrollTop: 0,
      currentPageNo: 1,
      pageGridOpen: false,
      pageGridReturnPageNo: 1,
      paperPagesGridColumnCount: 1,
      selectedDetailPageNos: [],
      selectedDetailPageAnchorNo: '',
      recordPanelOpen: false,
      recordTextOpen: false,
      recordTextLoading: false,
      recordTextError: '',
      recordTextRequestSeq: 0,
      activeRecording: null,
      zoomLevel: 0,
      zoomValue: 1,
      paperPreviewDragState: null,
      paperListPendingLoad: false,
      paperListRequestSeq: 0,
      paperListResourceId: '',
      paperFolderPath: [],
      paperGridColumnCount: 1,
      selectionBarOffsetY: 0,
      selectionBarScrollbarOffset: 0,
      selectionBarDragging: false,
      selectionBarDragState: null,
      paperIcons: {
        createFolder: ICON_CREATE_FOLDER,
        search: ICON_SEARCH,
        import: ICON_IMPORT,
        viewList: ICON_VIEW_LIST,
        viewGrid: ICON_VIEW_GRID,
        sortDesc: ICON_SORT_DESC,
        sortAsc: ICON_SORT_ASC,
        breadcrumbSeparator: ICON_BREADCRUMB_SEPARATOR,
        filterAll: ICON_FILTER_ALL,
        filterFavorite: ICON_FILTER_FAVORITE,
        filterDeleted: ICON_FILTER_DELETED,
        check: ICON_CHECK,
        download: ICON_DOWNLOAD,
        rename: ICON_RENAME,
        actionDownload: ACTION_ICON_DOWNLOAD,
        actionRename: ACTION_ICON_RENAME,
        actionDelete: ACTION_ICON_DELETE,
        move: ICON_MOVE,
        encryptMenu: ICON_ENCRYPT_MENU,
        delete: ICON_DELETE,
        favorite: ICON_FAVORITE,
        favoriteFilled: ICON_FAVORITE_FILLED,
        folderListFilled: ICON_FOLDER_LIST_FILLED,
        folderListEmpty: ICON_FOLDER_LIST_EMPTY,
        folderGridFilled: ICON_FOLDER_GRID_FILLED,
        folderGridEmpty: ICON_FOLDER_GRID_EMPTY,
        templateListFilled: ICON_TEMPLATE_LIST_FILLED,
        templateListEmpty: ICON_TEMPLATE_LIST_EMPTY,
        templateGridFilled: ICON_TEMPLATE_GRID_FILLED,
        templateGridEmpty: ICON_TEMPLATE_GRID_EMPTY,
        fileListPdf: ICON_FILE_LIST_PDF,
        fileListPaper: ICON_FILE_LIST_PAPER,
        fileListEncrypt: ICON_FILE_LIST_ENCRYPT,
        fileGridPdf: ICON_FILE_GRID_PDF,
        fileGridPaper: ICON_FILE_GRID_PAPER,
        fileGridEncrypt: ICON_FILE_GRID_ENCRYPT,
        resourceType12: ICON_RESOURCE_TYPE_1_2,
        resourceType3: ICON_RESOURCE_TYPE_3,
        pageGrid: ICON_PAGE_GRID,
        zoomIn: ICON_ZOOM_IN,
        zoomOut: ICON_ZOOM_OUT,
        close: ICON_CLOSE,
        record: RECORD_ICON,
        transcript: TRANSCRIPT_ICON,
        recordEmpty: RECORD_EMPTY_ICON
      },
      ui: {
        all: '\u5168\u90e8',
        favorite: '\u6536\u85cf',
        recentDeleted: '\u6700\u8fd1\u5220\u9664',
        search: '\u641c\u7d22',
        createFolder: '\u65b0\u5efa\u6587\u4ef6\u5939',
        importPdf: '\u5bfc\u5165 PDF',
        modifyTime: '\u4fee\u6539\u65f6\u95f4',
        createTime: '\u521b\u5efa\u65f6\u95f4',
        name: '\u540d\u79f0',
        desc: '\u5012\u5e8f',
        asc: '\u6b63\u5e8f',
        listView: '\u5217\u8868',
        gridView: '\u7f51\u683c',
        empty: '\u6682\u65e0\u6570\u636e',
        item: '\u9879',
        page: '\u9875',
        selectedPrefix: '\u5df2\u9009\u62e9',
        selectedSuffix: '\u9879',
        cancel: '\u53d6\u6d88',
        more: '\u66f4\u591a',
        download: '\u4e0b\u8f7d',
        rename: '\u91cd\u547d\u540d',
        move: '\u79fb\u52a8',
        moveTo: '\u79fb\u52a8\u81f3',
        moveSuccess: '\u79fb\u52a8\u6210\u529f',
        moveFailed: '\u79fb\u52a8\u5931\u8d25',
        moveSameDirectoryUnsupported: '\u76f8\u540c\u76ee\u5f55\u4e0d\u652f\u6301\u79fb\u52a8',
        moveItemFailed: '{name}\u79fb\u52a8\u5931\u8d25',
        encrypt: '\u52a0\u5bc6',
        decrypt: '\u53d6\u6d88\u52a0\u5bc6',
        encrypted: '\u5df2\u52a0\u5bc6',
        unfavorite: '\u53d6\u6d88\u6536\u85cf',
        delete: '\u5220\u9664',
        back: '\u8fd4\u56de',
        recordings: '\u5f55\u97f3',
        allRecordings: '\u5168\u90e8\u5f55\u97f3',
        transcript: '\u5f55\u97f3\u8f6c\u6587\u672c',
        pageGrid: '\u9875\u9762\u5217\u8868',
        zoomIn: '\u653e\u5927',
        zoomOut: '\u7f29\u5c0f',
        close: '\u5173\u95ed',
        createFolderSuccess: '\u65b0\u5efa\u6210\u529f',
        createFolderFailed: '\u65b0\u5efa\u5931\u8d25',
        createFolderCreating: '\u521b\u5efa\u4e2d...({seconds}s)',
        folderNameEmpty: '\u6587\u4ef6\u5939\u540d\u79f0\u4e0d\u80fd\u4e3a\u7a7a',
        fakeTip: '\u63a5\u53e3\u6682\u672a\u5f00\u653e\uff0c\u5df2\u7528\u5047\u63a5\u53e3\u5360\u4f4d'
      }
    }
  },
  computed: {
    filterOptions() {
      return [
        { key: 'all', label: this.ui.all, icon: this.paperIcons.filterAll },
        { key: 'favorite', label: this.ui.favorite, icon: this.paperIcons.filterFavorite },
        { key: 'deleted', label: this.ui.recentDeleted, icon: this.paperIcons.filterDeleted }
      ]
    },
    sortOptions() {
      return [
        { key: 'createTime', label: this.ui.createTime },
        { key: 'modifyTime', label: this.ui.modifyTime },
        { key: 'name', label: this.ui.name }
      ]
    },
    activeFilterLabel() {
      const item = this.filterOptions.find(option => option.key == this.paperFilter)
      return item ? item.label : this.ui.all
    },
    activeSortLabel() {
      const item = this.sortOptions.find(option => option.key == this.sortField)
      return item ? item.label : this.ui.createTime
    },
    noContentText() {
      const language = normalizeLanguage(this.language)
      return KNOWLEDGE_BASE_NO_CONTENT_LABELS[language] || KNOWLEDGE_BASE_NO_CONTENT_LABELS.us
    },
    viewSwitchIcon() {
      return this.viewMode == 'list'
        ? this.paperIcons.viewList
        : this.paperIcons.viewGrid
    },
    selectionBarLayerStyle() {
      return {
        transform: `translateY(${this.selectionBarOffsetY}px)`,
        right: `${this.selectionBarScrollbarOffset}px`
      }
    },
    paperBreadcrumbItems() {
      return [{ name: 'Knowledge Base', resourceId: '', pathKey: 'root' }].concat(this.normalizeCloudPaperFolderPath(this.paperFolderPath))
    },
    paperCreateFolderSubmittingText() {
      const waitSeconds = Math.max(3, Math.min(5, Number(this.paperCreateFolderWaitSeconds) || 5))
      const seconds = Math.max(1, Math.min(waitSeconds, Number(this.paperCreateFolderElapsedSeconds) || 1))
      const template = this.ui.createFolderCreating || '\u521b\u5efa\u4e2d...({seconds}s)'
      return String(template).replace('{seconds}', seconds)
    },
    paperMoveCreateFolderSubmittingText() {
      const waitSeconds = Math.max(3, Math.min(5, Number(this.paperMoveCreateFolderWaitSeconds) || 5))
      const seconds = Math.max(1, Math.min(waitSeconds, Number(this.paperMoveCreateFolderElapsedSeconds) || 1))
      const template = this.ui.createFolderCreating || '\u521b\u5efa\u4e2d...({seconds}s)'
      return String(template).replace('{seconds}', seconds)
    },
    knowledgeBasePickNotesRootFolder() {
      const rootFolder = this.normalizePaperFolderPathItem(this.paperFolderPath[0], 0)
      return this.isKnowledgeBasePickNotesFolder(rootFolder) ? rootFolder : null
    },
    canCreateKnowledgeBaseFolder() {
      if (this.paperFilter == 'deleted') {
        return false
      }
      return !!this.knowledgeBasePickNotesRootFolder
    },
    canUseKnowledgeBaseMove() {
      return this.paperFilter != 'deleted' && !!this.knowledgeBasePickNotesRootFolder
    },
    isPaperRootLevel() {
      return !this.paperListResourceId && this.paperFolderPath.length == 0
    },
    canSelectPaperItems() {
      return !this.isPaperRootLevel
    },
    activePaperSubTab() {
      const rootFolder = this.normalizePaperFolderPathItem(this.paperFolderPath[0], 0)
      return rootFolder ? this.normalizePaperSubTab(rootFolder.subTab) : -1
    },
    paperDownloadTaskSummary() {
      const total = this.paperDownloadTasks.length
      const running = this.paperDownloadTasks.filter(task => task.state == 'running').length
      const failed = this.paperDownloadTasks.filter(task => task.state == 'error').length
      if (running > 0) {
        return `${running}/${total} ${this.ui.downloading}`
      }
      if (failed > 0) {
        return `${failed}/${total} ${this.ui.downloadFailed}`
      }
      return `${total}/${total} ${this.ui.downloadDone}`
    },
    paperMoveBreadcrumbItems() {
      return [{ name: 'Knowledge Base', resourceId: '', pathKey: 'move-root' }].concat(this.normalizePaperMoveFolderPath(this.paperMoveFolderPath))
    },
    paperMoveBreadcrumbDisplayItems() {
      const items = this.paperMoveBreadcrumbItems.map((crumb, index) => ({
        key: `${crumb.pathKey || crumb.resourceId || 'move-root'}-${index}`,
        name: this.getKnowledgeBaseDisplayName(crumb) || crumb.name || '',
        originalIndex: index,
        collapsed: false
      }))
      if (items.length <= 4) {
        return items
      }
      return [
        items[0],
        items[1],
        { key: 'paper-move-breadcrumb-ellipsis', name: '...', originalIndex: -1, collapsed: true },
        items[items.length - 2],
        items[items.length - 1]
      ]
    },
    paperMoveBreadcrumbTitle() {
      return this.paperMoveBreadcrumbItems.map(item => this.getKnowledgeBaseDisplayName(item) || item.name || '').join(' > ')
    },
    interactionLocked() {
      return this.paperListLoading || this.paperDetailLoading || this.recordTextLoading || this.paperPasswordSubmitting || this.paperRenameSubmitting || this.paperCreateFolderSubmitting || this.paperMoveSubmitting || this.paperMoveCreateFolderSubmitting || this.paperDeleteSubmitting
    },
    visiblePaperItems() {
      let items = this.paperItems.slice()
      if (this.paperFilter == 'favorite') {
        items = items.filter(item => item.favorite)
      } else if (this.paperFilter == 'deleted') {
        items = items.filter(item => item.deleted)
      } else {
        items = items.filter(item => !item.deleted)
      }
      const keyword = String(this.searchKeyword || '').toLowerCase()
      if (keyword) {
        items = items.filter(item => {
          const sourceName = String(item.name || '').toLowerCase()
          const displayName = String(this.getKnowledgeBaseDisplayName(item) || '').toLowerCase()
          return sourceName.includes(keyword) || displayName.includes(keyword)
        })
      }
      return items
    },
    selectedPaperItems() {
      const selectedIds = new Set(this.selectedPaperIds)
      return this.paperItems.filter(item => selectedIds.has(item.id))
    },
    selectedPaper() {
      return this.selectedPaperItems[0] || null
    },
    selectedCount() {
      return this.selectedPaperItems.length
    },
    hasPaperSelection() {
      return this.canSelectPaperItems && this.selectedCount > 0
    },
    showSelectionDeleteAction() {
      return this.canSelectPaperItems && this.selectedCount > 0
    },
    canBatchSelectionAction() {
      return this.selectedCount > 0 && this.selectedPaperItems.every(item => this.isPaperDownloadableItem(item))
    },
    canDeleteSelectedPaperItems() {
      return this.canSelectPaperItems && this.selectedCount > 0 && this.selectedPaperItems.every(item => this.isPaperDeletableItem(item))
    },
    paperDeleteConfirmText() {
      const list = Array.isArray(this.paperDeleteItems) ? this.paperDeleteItems.filter(Boolean) : []
      if (list.length == 0) {
        return ''
      }
      const firstName = this.getKnowledgeBaseDisplayName(list[0]) || ''
      if (list.length == 1) {
        return `\u786e\u5b9e\u5220\u9664${firstName}\uff1f`
      }
      return `\u5220\u9664${firstName}\u7b49\u7b49\uff1f`
    },
    canRenameSelected() {
      return this.selectedCount == 1 && this.isPaperRenameableItem(this.selectedPaper)
    },
    canMoveSelected() {
      return this.canUseKnowledgeBaseMove && !this.paperMoveSubmitting && this.selectedCount > 0 && this.selectedPaperItems.every(item => this.isPaperMoveableItem(item))
    },
    paperMoveConfirmDisabled() {
      if (this.paperMoveSubmitting || this.paperMoveItems.length == 0) {
        return true
      }
      return this.isPaperMoveTargetSameAsOriginalParent(this.getPaperMoveTargetParentId())
    },
    hasNextPaperMoveFolderPage() {
      if (this.paperMoveReachedEnd) {
        return false
      }
      const total = Number(this.paperMoveTotalCount)
      const pageSize = this.getPaperMoveFolderPageSize()
      if (Number.isFinite(total) && total > 0) {
        return (Number(this.paperMovePageIndex) || 1) < Math.max(1, Math.ceil(total / pageSize))
      }
      return Math.max(0, Number(this.paperMoveLastPageSize) || 0) >= pageSize
    },
    canEncryptSelected() {
      return this.selectedCount == 1 && !!this.selectedPaper
    },
    canUnfavoriteSelected() {
      return this.selectedCount == 1 && this.selectedPaper && this.selectedPaper.favorite
    },
    selectionEncryptLabel() {
      return this.selectedPaper && this.selectedPaper.encrypted ? this.ui.decrypt : this.ui.encrypt
    },
    paperDetailTitle() {
      if (this.paperDetail && this.paperDetail.name) {
        return this.paperDetail.name
      }
      return this.currentPaper && this.currentPaper.name ? this.currentPaper.name : 'Knowledge Base'
    },
    detailPages() {
      return this.paperDetail && Array.isArray(this.paperDetail.imagePages) ? this.paperDetail.imagePages : []
    },
    selectedDetailPages() {
      const selectedNos = new Set(this.selectedDetailPageNos)
      return this.detailPages.filter(page => selectedNos.has(page.pageNo))
    },
    detailRecordings() {
      return this.paperDetail && Array.isArray(this.paperDetail.recordings) ? this.paperDetail.recordings : []
    },
    hasDetailRecordings() {
      return this.detailRecordings.length > 0
    },
    currentPageIndex() {
      const index = this.detailPages.findIndex(page => page.pageNo == this.currentPageNo)
      return index >= 0 ? index : 0
    },
    currentPage() {
      return this.detailPages[this.currentPageIndex] || null
    },
    knowledgeBasePreviewResourceType() {
      const detailResourceTypeValue = this.paperDetail && this.paperDetail.resourceType
      const detailResourceType = detailResourceTypeValue === undefined || detailResourceTypeValue === null || detailResourceTypeValue === ''
        ? NaN
        : Number(detailResourceTypeValue)
      if (Number.isFinite(detailResourceType)) {
        return detailResourceType
      }
      const itemResourceTypeValue = this.currentPaper && this.currentPaper.resourceType
      const itemResourceType = itemResourceTypeValue === undefined || itemResourceTypeValue === null || itemResourceTypeValue === ''
        ? NaN
        : Number(itemResourceTypeValue)
      return Number.isFinite(itemResourceType) ? itemResourceType : -1
    },
    isKnowledgeBaseAppAiNotePreview() {
      return this.knowledgeBasePreviewResourceType === 3
    },
    activeRecordingName() {
      return this.activeRecording && this.activeRecording.recordingName ? this.activeRecording.recordingName : ''
    },
    activeRecordingText() {
      if (!this.activeRecording) {
        return ''
      }
      if (this.recordTextLoading) {
        return this.ui.transcriptLoading
      }
      if (this.recordTextError) {
        return this.recordTextError
      }
      if (this.activeRecording.transcriptText) {
        return this.activeRecording.transcriptText
      }
      if (this.activeRecording.transcriptTextUrl) {
        return this.ui.transcriptPending
      }
      return this.ui.noTranscript
    }
  },
  watch: {
    language: {
      immediate: true,
      handler(value) {
        this.ui = getAppUiText(value)
      }
    },
    isPaperRootLevel(value) {
      if (value && this.selectedPaperIds.length > 0) {
        this.clearSelection()
      }
    },
    viewMode() {
      this.updatePaperGridColumnCount()
      this.syncSelectionBarScrollbarOffset()
    },
    visiblePaperItems() {
      this.updatePaperGridColumnCount()
      this.syncSelectionBarScrollbarOffset()
    },
    pageGridOpen() {
      this.updatePaperPagesGridColumnCount()
    },
    detailPages() {
      this.updatePaperPagesGridColumnCount()
    }
  },
  mounted() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handlePaperWindowResize)
    }
    this.applyInitialRefreshState()
    if (!this.skipInitialListLoad) {
      this.loadPaperList()
    }
    this.updatePaperGridColumnCount()
    this.syncSelectionBarScrollbarOffset()
  },
  beforeDestroy() {
    this.paperListRequestSeq += 1
    this.paperDetailRequestSeq += 1
    this.paperPasswordRequestSeq += 1
    this.paperRenameRequestSeq += 1
    this.paperCreateFolderRequestSeq += 1
    this.paperMoveRequestSeq += 1
    this.paperMoveCreateFolderRequestSeq += 1
    this.stopPaperCreateFolderTimer()
    this.stopPaperMoveCreateFolderTimer()
    this.paperListPendingLoad = false
    this.paperMovePendingLoad = null
    if (!this.$hasRunningDownloadTasks()) {
      this.clearPaperDetailCache()
      this.clearPaperPreviewObjectUrls()
    }
    this.clearPaperItemClickTimer()
    this.stopSelectionBarDrag()
    this.stopPaperPreviewDrag()
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handlePaperWindowResize)
    }
  },
  methods: {
    getKnowledgeBaseDisplayName(item) {
      const name = item && item.name !== undefined && item.name !== null ? String(item.name) : ''
      if (!name) {
        return ''
      }
      if (item && item.isFolder === false && Number(item.resourceType) !== 0 && Number(item.resourceType) !== 5) {
        return name
      }
      return this.translateKnowledgeBaseFixedFolderName(name)
    },
    translateKnowledgeBaseFixedFolderName(name) {
      const sourceName = String(name || '')
      const labels = KNOWLEDGE_BASE_FIXED_FOLDER_LABELS[this.normalizeFieldKey(sourceName)]
      if (!labels) {
        return sourceName
      }
      const language = normalizeLanguage(this.language)
      if (language == 'us' || language == 'uk') {
        return sourceName
      }
      return labels[language] || labels.us || sourceName
    },
    isKnowledgeBasePickNotesFolder(folder) {
      const name = folder && folder.name !== undefined && folder.name !== null ? String(folder.name).trim() : ''
      if (!name) {
        return false
      }
      const normalizedName = this.normalizeFieldKey(name)
      if (normalizedName == 'picknotes') {
        return true
      }
      const labels = KNOWLEDGE_BASE_FIXED_FOLDER_LABELS.picknotes || {}
      return Object.keys(labels).some(key => this.normalizeFieldKey(labels[key]) == normalizedName)
    },
    applyInitialRefreshState() {
      const state = this.initialRefreshState && typeof this.initialRefreshState == 'object' ? this.initialRefreshState : null
      if (!state) {
        return
      }
      const folderPath = this.normalizeRefreshFolderPath(state.paperFolderPath)
      const resourceId = state.paperListResourceId === undefined || state.paperListResourceId === null ? '' : String(state.paperListResourceId).trim()
      this.paperFolderPath = folderPath
      this.paperListResourceId = resourceId || (folderPath.length > 0 ? folderPath[folderPath.length - 1].resourceId || '' : '')
      this.paperFilter = this.normalizeRefreshPaperFilter(state.paperFilter)
    },
    getRefreshState() {
      return {
        paperListResourceId: this.paperListResourceId || '',
        paperFolderPath: this.normalizeRefreshFolderPath(this.paperFolderPath),
        paperFilter: this.normalizeRefreshPaperFilter(this.paperFilter)
      }
    },
    normalizeRefreshPaperFilter(filter) {
      const key = String(filter || '')
      return this.filterOptions.some(option => option.key == key) ? key : 'all'
    },
    normalizeRefreshFolderPath(folderPath) {
      return this.normalizeCloudPaperFolderPath(folderPath)
    },
    loadPaperList(options = {}) {
      const pageOptions = this.normalizePaperListLoadOptions(options)
      if (this.paperListLoading || this.paperListAppendLoading) {
        this.paperListPendingLoad = pageOptions
        return
      }
      const requestId = this.paperListRequestSeq + 1
      this.paperListRequestSeq = requestId
      this.paperListLoading = !pageOptions.append
      this.paperListAppendLoading = pageOptions.append
      getKnowledgeBaseSyncList(this.buildPaperListParams(pageOptions.pageIndex)).then(ret => {
        if (!this.isActivePaperListRequest(requestId)) {
          return
        }
        const payload = this.resolveApiPayload(ret)
        if (payload === null) {
          this.handlePaperListLoadFailed(ret)
          return
        }
        this.applyPaperListPage(payload, this.normalizePaperList(payload), pageOptions)
      }).catch(error => {
        if (!this.isActivePaperListRequest(requestId)) {
          return
        }
        this.handlePaperListLoadFailed(error)
      }).finally(() => {
        if (!this.isActivePaperListRequest(requestId)) {
          return
        }
        this.syncSelectionWithItems()
        this.paperListLoading = false
        this.paperListAppendLoading = false
        this.updatePaperGridColumnCount()
        if (this.paperListPendingLoad) {
          const pendingOptions = this.paperListPendingLoad
          this.paperListPendingLoad = false
          this.loadPaperList(pendingOptions)
        }
      })
    },
    isActivePaperListRequest(requestId) {
      return requestId == this.paperListRequestSeq
    },
    buildPaperListParams(pageIndex = this.paperListPageIndex) {
      return {
        pageIndex,
        pageSize: this.paperListPageSize,
        appType: KNOWLEDGE_BASE_APP_TYPE,
        resourceId: this.paperListResourceId,
        isDelete: this.paperFilter == 'deleted' ? 1 : -1,
        subTab: this.activePaperSubTab,
        star: this.paperFilter == 'favorite' ? 1 : -1,
        sortField: this.toResourceSortField(this.sortField),
        sortOrder: this.sortDesc ? 'desc' : 'asc'
      }
    },
    toResourceSortField(field) {
      if (field == 'createTime') {
        return 'creation_time'
      }
      if (field == 'modifyTime') {
        return 'last_modified_time'
      }
      if (field == 'name') {
        return 'name'
      }
      return ''
    },
    async loadPaperDetail(item, options = {}) {
      const password = options.password || ''
      const requestId = this.paperDetailRequestSeq + 1
      const cacheKey = this.getPaperDetailCacheKey(item, password)
      this.paperDetailRequestSeq = requestId
      this.paperDetailLoading = true
      this.paperDetailProgress = 0
      this.paperDetailError = ''
      this.clearPaperPreviewObjectUrls({ revoke: false })
      this.paperDetail = null
      this.currentPageNo = 1
      this.pageGridOpen = false
      this.selectedDetailPageNos = []
      this.selectedDetailPageAnchorNo = ''
      this.recordPanelOpen = false
      this.recordTextOpen = false
      this.activeRecording = null
      this.resetPaperZoom()
      this.clearPreviewZoomTip()

      const cachedDetail = !options.force ? this.getCachedPaperDetail(cacheKey) : null
      if (cachedDetail) {
        this.paperPreviewObjectUrls = cachedDetail.objectUrls || []
        this.applyPaperDetail(cachedDetail.detail, item)
        this.setPaperDetailProgress(100, requestId)
        this.paperDetailLoading = false
        return true
      }

      try {
        this.setPaperDetailProgress(5, requestId)
        const ret = !options.force && options.prefetchedRet ? options.prefetchedRet : await this.resolvePcPaperDetailResponse(
          item,
          password,
          () => this.fetchPaperDetailResponse(item, password),
          { force: !!options.force }
        )
        if (requestId != this.paperDetailRequestSeq) {
          return false
        }
        this.setPaperDetailProgress(18, requestId)
        if (password && this.isPaperPasswordCodeError(ret)) {
          throw new Error(this.getPaperPasswordErrorMessage())
        }
        if (this.isPaperApiCodeFailed(ret)) {
          throw new Error(this.getPaperApiErrorMessage(ret) || 'Knowledge Base detail failed')
        }
        const payload = this.resolveApiPayload(ret)
        if (payload === null) {
          throw new Error('Knowledge Base detail failed')
        }
        const rawDetail = this.normalizePaperDetail(payload, item)
        this.setPaperDetailProgress(25, requestId)
        const prepared = await this.preparePaperPreviewDetail(rawDetail, progress => {
          this.setPaperDetailProgress(progress, requestId)
        })
        if (requestId != this.paperDetailRequestSeq) {
          this.revokePaperPreviewObjectUrls(prepared.objectUrls)
          return false
        }
        this.paperPreviewObjectUrls = prepared.objectUrls || []
        this.applyPaperDetail(prepared.detail, item)
        this.setPaperDetailCache(cacheKey, prepared.detail, prepared.objectUrls)
        this.setPaperDetailProgress(100, requestId)
        return true
      } catch (error) {
        if (requestId != this.paperDetailRequestSeq) {
          return false
        }
        this.paperDetail = null
        this.clearPaperPreviewObjectUrls({ revoke: false })
        this.paperDetailError = password && this.isPaperPasswordCodeError(error)
          ? this.getPaperPasswordErrorMessage()
          : this.getPaperDetailErrorMessage(error)
        return false
      } finally {
        if (requestId == this.paperDetailRequestSeq) {
          this.paperDetailLoading = false
        }
      }
    },
    retryPaperDetailLoad() {
      if (this.paperDetailLoading || !this.currentPaper) {
        return
      }
      this.loadPaperDetail(this.currentPaper, { force: true, password: this.currentPaperPassword || '' })
    },
    applyPaperDetail(detail, sourceItem) {
      this.paperDetail = detail
      if (detail && detail.imagePages && detail.imagePages.length > 0) {
        this.currentPageNo = this.resolvePaperInitialPageNo(detail, sourceItem)
      }
      this.centerPaperPreviewScroll()
    },
    setPaperDetailProgress(progress, requestId) {
      if (requestId && requestId != this.paperDetailRequestSeq) {
        return
      }
      const value = Math.max(0, Math.min(100, Math.round(Number(progress) || 0)))
      this.paperDetailProgress = Math.max(this.paperDetailProgress || 0, value)
    },
    getPaperDetailCacheKey(item, password = '') {
      const resourceId = item && item.resourceId ? String(item.resourceId) : ''
      if (!resourceId) {
        return ''
      }
      return this.isPaperEncryptedItem(item) ? `${resourceId}:${password || 'encrypted'}` : resourceId
    },
    getPaperDetailSharedCacheScope() {
      return this.$options && this.$options.name ? this.$options.name : 'KnowledgeBaseSync'
    },
    getCachedPaperDetail(cacheKey) {
      if (!cacheKey) {
        return null
      }
      const cached = this.paperDetailCache && this.paperDetailCache[cacheKey]
      if (cached && cached.detail) {
        return cached
      }
      const shared = typeof this.getSharedPaperPreviewDetailCache == 'function'
        ? this.getSharedPaperPreviewDetailCache(this.getPaperDetailSharedCacheScope(), cacheKey)
        : null
      if (shared && shared.detail && this.paperDetailCache) {
        this.$set(this.paperDetailCache, cacheKey, shared)
      }
      return shared && shared.detail ? shared : null
    },
    setPaperDetailCache(cacheKey, detail, objectUrls) {
      if (!cacheKey || !detail) {
        return
      }
      const previous = this.paperDetailCache ? this.paperDetailCache[cacheKey] : null
      const entry = typeof this.setSharedPaperPreviewDetailCache == 'function'
        ? this.setSharedPaperPreviewDetailCache(this.getPaperDetailSharedCacheScope(), cacheKey, detail, objectUrls)
        : {
            detail,
            objectUrls: Array.isArray(objectUrls) ? objectUrls.slice() : [],
            cachedAt: Date.now()
          }
      if (previous && previous.objectUrls && previous.objectUrls !== objectUrls && previous !== entry) {
        this.revokePaperPreviewObjectUrls(previous.objectUrls)
      }
      this.$set(this.paperDetailCache, cacheKey, entry)
      this.trimPaperPreviewDetailCache(this.paperDetailCache, cacheKey)
    },
    getPaperDetailErrorMessage(error) {
      return this.getPaperApiErrorMessage(error) || this.ui.detailLoadFailed
    },
    getPaperPasswordErrorMessage() {
      return this.ui.passwordWrong
    },
    fetchPaperDetailResponse(item, password = '', requestOptions = {}) {
      const directDetail = this.createDirectFilePreviewDetail(item)
      if (directDetail) {
        return Promise.resolve({ code: 200, data: directDetail })
      }
      const resourceId = item && item.resourceId ? item.resourceId : ''
      const params = {
        uuid: resourceId
      }
      if (password || this.isPaperEncryptedItem(item)) {
        params.password = password
      }
      return getKnowledgeBaseSyncDetail(params, Object.assign({ skipGlobalErrorMessage: true }, requestOptions))
    },
    getPaperApiResponseData(source) {
      const responseData = source && source.response && source.response.data ? source.response.data : null
      return responseData || (source && source.data ? source.data : source)
    },
    getPaperApiCode(source) {
      const data = this.getPaperApiResponseData(source)
      if (!data || typeof data != 'object') {
        return undefined
      }
      return this.pickValue(data, ['code', 'statusCode'])
    },
    isPaperApiCodeFailed(source) {
      const code = this.getPaperApiCode(source)
      if (code === undefined || code === null || code === '') {
        return false
      }
      return code != 200 && code != '200'
    },
    isPaperPasswordCodeError(source) {
      const code = this.getPaperApiCode(source)
      return code == 10013 || code == '10013'
    },
    resolveApiPayload(ret) {
      const data = ret && ret.data ? ret.data : ret
      if (!data) {
        return null
      }
      if (data.code == 200 || data.code == '200') {
        return data.data === undefined ? {} : data.data
      }
      if (data.success === true) {
        return data.data === undefined ? {} : data.data
      }
      if (Array.isArray(data) || data.list || data.records || data.items || data.meta || data.paperMeeting) {
        return data
      }
      return null
    },
    handlePaperListLoadFailed(error) {
      this.resetPaperListPagination()
      this.paperItems = []
      this.clearSelection()
      this.filterMenuOpen = false
      this.sortMenuOpen = false
      this.showPaperListLoadError(error)
    },
    showPaperListLoadError(error) {
      const message = this.getPaperApiErrorMessage(error) || this.ui.dataLoadFailed.replace('{app}', 'Knowledge Base')
      if (typeof this.$error == 'function') {
        this.$error({
          title: this.ui.notice,
          content: message,
          okButtonProps: {
            style: {
              backgroundColor: '#1f3a2d',
              borderColor: '#1f3a2d'
            }
          }
        })
        return
      }
      if (this.$message && typeof this.$message.error == 'function') {
        this.$message.error(message)
      }
    },
    getPaperApiErrorMessage(error) {
      const responseData = error && error.response && error.response.data ? error.response.data : null
      const data = responseData || (error && error.data ? error.data : error)
      if (data && typeof data == 'object') {
        return this.pickString(data, ['msg', 'message', 'errorMessage', 'error'])
      }
      if (error && error.message) {
        return error.message
      }
      return ''
    },
    showPaperDetailRequestError(error) {
      const message = this.getPaperDetailErrorMessage(error)
      if (this.$message && typeof this.$message.error == 'function') {
        this.$message.error(message)
      }
    },
    normalizePaperList(payload) {
      const list = this.extractArray(payload)
      return list.map((item, index) => this.normalizePaperItem(item, index))
    },
    extractArray(payload, depth = 0) {
      if (Array.isArray(payload)) {
        return payload
      }
      if (!payload || typeof payload != 'object' || depth > 4) {
        return []
      }
      const keys = ['list', 'items', 'records', 'rows', 'data', 'resourceList', 'paperList']
      for (let i = 0; i < keys.length; i++) {
        if (Array.isArray(payload[keys[i]])) {
          return payload[keys[i]]
        }
      }
      const values = Object.keys(payload).map(key => payload[key])
      for (let i = 0; i < values.length; i++) {
        const nested = this.extractArray(values[i], depth + 1)
        if (nested.length > 0) {
          return nested
        }
      }
      return []
    },
    normalizePaperItem(raw, index) {
      const item = raw && typeof raw == 'object' ? raw : {}
      const meta = this.parseMeta(this.pickValue(item, ['meta']))
      const paperMeeting = this.resolvePaperMeetingMeta(meta)
      const imagePages = Array.isArray(paperMeeting.imagePages) ? paperMeeting.imagePages : []
      const explicitResourceId = this.pickValue(item, ['resourceId'])
      const fallbackResourceId = this.pickString(item, ['uuid', 'id', 'resourceUuid', 'paperId'])
      const resourceId = explicitResourceId !== undefined && explicitResourceId !== null
        ? String(explicitResourceId).trim()
        : fallbackResourceId
      const parentId = this.pickString(item, ['parentId', 'parent_id', 'pid'])
      const resourceType = this.pickValue(item, ['resourceType'])
      const numericResourceType = Number(resourceType)
      const hasNumericResourceType = resourceType !== undefined && resourceType !== null && resourceType !== '' && Number.isFinite(numericResourceType)
      const isFolder = hasNumericResourceType
        ? numericResourceType === 0 || numericResourceType === 5
        : this.pickBoolean(item, ['isFolder', 'folder', 'isDir', 'directory'])
      const isTemplate = hasNumericResourceType
        ? numericResourceType === 5
        : this.pickBoolean(item, ['template', 'isTemplate', 'templateFolder'])
      const name = this.pickString(item, ['name', 'title', 'fileName', 'resourceName', 'paperName']) || paperMeeting.name || `\u5f55\u97f3 ${index + 1}`
      const cover = this.pickString(item, ['cover'])
        || this.pickString(item, ['coverUrl', 'imageUrl', 'thumbnail', 'thumbnailUrl'])
      const gridCover = this.pickPaperCoverImageUrl(item)
      const filePath = this.pickString(item, ['filePath'])
        || this.pickString(item, ['file_path', 'path', 'fileUrl', 'url', 'src'])
        || this.pickString(paperMeeting, ['filePath', 'file_path', 'path', 'fileUrl', 'url', 'src'])
      const pageCount = this.pickValue(item, ['pageCount', 'pages', 'pageNum', 'imagePageCount', 'totalPage', 'childSize'])
      const childSize = this.pickValue(item, ['childSize', 'child_size', 'childrenSize', 'childCount', 'childrenCount'])
      const subTab = this.resolvePaperSubTab(item, paperMeeting)
      const pathKey = this.getPaperFolderPathKey({ resourceId, resourceType, subTab, name, filePath }, index)
      const id = this.getPaperItemKey({ resourceId, resourceType, subTab, name, filePath }, index) || resourceId || fallbackResourceId || `paper-${index}`
      const createdAt = this.pickValue(item, ['creationTime', 'createdAt', 'createAt', 'createTime', 'ctime', 'createDate', 'createdDate', 'createdTime', 'creationDate', 'gmtCreate'])
        || this.pickValue(paperMeeting, ['creationTime', 'createdAt', 'createAt', 'createTime', 'ctime', 'createDate', 'createdDate', 'createdTime', 'creationDate', 'gmtCreate'])
      const updatedAt = this.pickValue(item, ['lastModifiedTime', 'updatedAt', 'updateAt', 'modifyAt', 'modifyTime', 'modifiedAt', 'modifiedTime', 'mtime', 'updateTime', 'updatedTime', 'updateDate', 'updatedDate', 'lastModified', 'lastUpdateTime', 'gmtModify', 'gmtModified'])
        || this.pickValue(paperMeeting, ['lastModifiedTime', 'updatedAt', 'updateAt', 'modifyAt', 'modifyTime', 'modifiedAt', 'modifiedTime', 'mtime', 'updateTime', 'updatedTime', 'updateDate', 'updatedDate', 'lastModified', 'lastUpdateTime', 'gmtModify', 'gmtModified'])
      const currentPage = this.pickPaperCurrentPageValue(paperMeeting, meta, item)
      return {
        id,
        pathKey,
        resourceId,
        parentId,
        resourceType,
        name,
        currentPage,
        cover,
        gridCover,
        filePath,
        subTab,
        pageCount: pageCount === undefined || pageCount === null || pageCount === '' ? imagePages.length : pageCount,
        childSize: childSize === undefined || childSize === null || childSize === '' ? pageCount : childSize,
        updatedAt,
        createdAt,
        lastModifiedTime: updatedAt,
        creationTime: createdAt,
        favorite: this.pickBoolean(item, ['favorite', 'isFavorite', 'starred', 'isStar', 'star', 'start']),
        deleted: this.pickBoolean(item, ['deleted', 'isDeleted', 'inTrash', 'isDelete']),
        encrypted: this.pickBoolean(item, ['encrypted', 'isEncrypted', 'locked', 'isLock', 'encrypt', 'encryp']),
        isFolder,
        template: isTemplate,
        openFolder: this.pickBoolean(item, ['openFolder']),
        raw: item,
        meta
      }
    },
    normalizePaperDetail(payload, sourceItem) {
      const raw = payload && typeof payload == 'object' ? payload : {}
      const detail = raw.resource && typeof raw.resource == 'object' ? raw.resource : raw
      const sourceMeta = sourceItem && sourceItem.meta ? sourceItem.meta : {}
      const detailMeta = this.parseMeta(this.pickValue(detail, ['meta']))
      const meta = Object.assign({}, sourceMeta, detailMeta)
      if (detail.paperMeeting && typeof detail.paperMeeting == 'object') {
        meta.paperMeeting = detail.paperMeeting
      }
      const paperMeeting = this.resolvePaperMeetingMeta(meta)
      const detailPaperMeeting = this.resolvePaperMeetingMeta(detailMeta)
      const hasExplicitEmptyMetaPages = this.hasExplicitEmptyMetaPages(detailMeta)
        || (detailPaperMeeting !== detailMeta && this.hasExplicitEmptyMetaPages(detailPaperMeeting))
      const fallbackName = sourceItem && sourceItem.name ? sourceItem.name : 'Knowledge Base'
      const resourceType = this.pickValue(paperMeeting, ['resourceType'])
        || this.pickValue(meta, ['resourceType'])
        || this.pickValue(detail, ['resourceType'])
      const resourceUrl = this.pickString(paperMeeting, ['resourceUrl', 'resource_url', 'fileUrl', 'url'])
        || this.pickString(meta, ['resourceUrl', 'resource_url', 'fileUrl', 'url'])
        || this.pickString(detail, ['resourceUrl', 'resource_url', 'fileUrl', 'url'])
      const metaPages = Array.isArray(paperMeeting.imagePages)
        ? paperMeeting.imagePages
        : this.extractArray(this.pickValue(paperMeeting, ['imagePages', 'pages', 'pageList']))
      const metaRootPages = paperMeeting === meta
        ? []
        : this.extractArray(this.pickValue(meta, ['imagePages', 'pages', 'pageList']))
      const rawPages = hasExplicitEmptyMetaPages
        ? []
        : (metaPages.length > 0
        ? metaPages
        : (metaRootPages.length > 0 ? metaRootPages : this.extractArray(this.pickValue(detail, ['imagePages', 'pages', 'pageList']))))
      const allowPageFallback = !hasExplicitEmptyMetaPages && !(sourceItem && sourceItem.disableDownloadPageFallback) && !(Number(resourceType) === 5 && resourceUrl)
      const imagePages = this.normalizeImagePages(rawPages, sourceItem, allowPageFallback)
      const metaRecordings = Array.isArray(paperMeeting.recordings)
        ? paperMeeting.recordings
        : this.extractArray(this.pickValue(paperMeeting, ['recordings', 'recordingList', 'audios']))
      const rawRecordings = metaRecordings.length > 0
        ? metaRecordings
        : this.extractArray(this.pickValue(detail, ['recordings', 'recordingList', 'audios']))
      const currentPage = this.pickPaperCurrentPageValue(paperMeeting, meta, detail, sourceItem || {})
      return {
        name: this.pickString(paperMeeting, ['name', 'title', 'resourceName', 'paperName']) || this.pickString(detail, ['name', 'title', 'resourceName']) || fallbackName,
        currentPage,
        resourceType,
        resourceUrl,
        filePath: this.pickString(detail, ['filePath']) || this.pickString(sourceItem || {}, ['filePath']),
        isDirectFilePreview: !!(detail && detail.isDirectFilePreview) || this.isDirectFilePreviewItem(sourceItem),
        imagePages,
        recordings: this.normalizeRecordings(rawRecordings)
      }
    },
    hasExplicitEmptyMetaPages(meta) {
      if (!meta || typeof meta != 'object' || !Object.prototype.hasOwnProperty.call(meta, 'pages')) {
        return false
      }
      const pages = meta.pages
      return pages === null || pages === undefined || pages === '' || (Array.isArray(pages) && pages.length == 0)
    },
    normalizeImagePages(rawPages, sourceItem, allowFallback = true) {
      const pages = Array.isArray(rawPages) ? rawPages : []
      const normalized = pages.map((page, index) => {
        const item = page && typeof page == 'object' ? page : { imageUrl: page }
        const pageNo = Number(this.pickValue(item, ['pageNo', 'page', 'index', 'no'])) || index + 1
        const imageUrl = this.pickPaperBestImageUrl(item) || this.pickString(item, ['imageUrl', 'url', 'src', 'cover', 'thumbnail', 'thumbnailUrl', 'fileUrl', 'resourceUrl', 'path'])
        const name = this.pickString(item, ['name', 'title', 'pageName', 'fileName'])
        const contentValue = this.pickValue(item, ['content', 'text', 'body', 'html', 'description', 'value'])
        const content = contentValue === undefined || contentValue === null ? '' : String(contentValue)
        const remarkValue = this.pickValue(item, ['remark', 'remarks'])
        const remark = remarkValue === undefined || remarkValue === null ? '' : String(remarkValue)
        const hasContent = !!this.normalizeKnowledgeBaseContentText(content).trim()
        const hasRemark = !!this.normalizeKnowledgeBaseContentText(remark).trim()
        return {
          pageNo,
          imageUrl,
          name,
          content,
          remark,
          pageType: (hasContent || hasRemark) ? 'text' : (imageUrl ? 'image' : (name ? 'text' : 'blank'))
        }
      }).filter(page => page.imageUrl || page.name || page.content || page.pageNo)
        .sort((a, b) => a.pageNo - b.pageNo)
      if (normalized.length > 0 || !allowFallback) {
        return normalized
      }
      const cover = this.pickPaperBestImageUrl(sourceItem, { includeThumbnail: false }) || (sourceItem && sourceItem.cover ? sourceItem.cover : MOCK_PAGE_IMAGE)
      return [{ pageNo: 1, imageUrl: cover, name: '', content: '', pageType: 'image' }]
    },
    normalizeRecordings(rawRecordings) {
      const recordings = Array.isArray(rawRecordings) ? rawRecordings : []
      return recordings.map((recording, index) => {
        const item = recording && typeof recording == 'object' ? recording : {}
        return {
          id: this.pickString(item, ['id', 'recordingId', 'uuid', 'fileUrl']) || `recording-${index}`,
          recordingName: this.pickString(item, ['recordingName', 'name', 'title', 'fileName']) || `\u5f55\u97f3 ${index + 1}`,
          duration: this.pickValue(item, ['duration', 'durationMs', 'durationMillis']),
          fileSizeBytes: this.pickValue(item, ['fileSizeBytes', 'size', 'fileSize']),
          recordedAtMs: this.pickValue(item, ['recordedAtMs', 'recordedAt', 'createTime', 'createdAt']),
          fileUrl: this.pickString(item, ['fileUrl', 'url', 'src']),
          transcriptTextUrl: this.pickString(item, ['transcriptTextUrl', 'textUrl']),
          transcriptText: this.pickString(item, ['transcriptText', 'text', 'content'])
        }
      })
    },
    getMockPaperItems() {
      return [
        { id: 'mock-template', resourceId: '', name: '\u6211\u7684\u6a21\u677f', cover: '', pageCount: 4, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: false, isFolder: true, openFolder: false, template: true },
        { id: 'mock-folder-1', resourceId: '', name: '\u6587\u4ef6\u5939 1', cover: '', pageCount: 4, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: true, isFolder: true, openFolder: true },
        { id: 'mock-folder-2', resourceId: '', name: '\u6587\u4ef6\u5939 2', cover: '', pageCount: 4, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: false, isFolder: true, openFolder: false },
        { id: 'mock-paper-1', resourceId: 'mock-paper-1', name: '\u6587\u4ef6\u540d\u79f0\u6587\u4ef6\u540d\u6587\u4ef6\u540d\u79f0\u6587\u4ef6', cover: MOCK_SMALL_PAGE_IMAGE, pageCount: 23, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: false, isFolder: false },
        { id: 'mock-paper-2', resourceId: 'mock-paper-2', name: '\u6708\u8ba1\u5212', cover: MOCK_TEMPLATE_IMAGE, pageCount: 13, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: true, isFolder: false },
        { id: 'mock-paper-3', resourceId: 'mock-paper-3', name: '\u6211\u7684\u7b14\u8bb0', cover: '', pageCount: 13, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: true, encrypted: true, isFolder: false },
        { id: 'mock-paper-4', resourceId: 'mock-paper-4', name: '\u65e5\u5e38\u8bb0\u5f55', cover: MOCK_PAGE_IMAGE, pageCount: 13, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: false, isFolder: false }
      ]
    },
    getMockPaperDetail(item) {
      const name = item && item.name ? item.name : '\u65e5\u5e38\u8bb0\u5f55'
      const pages = []
      for (let i = 1; i <= 23; i++) {
        pages.push({
          pageNo: i,
          imageUrl: i == 1 ? (item && item.cover && !item.encrypted ? item.cover : MOCK_PAGE_IMAGE) : MOCK_PAGE_IMAGE
        })
      }
      return {
        name,
        imagePages: pages,
        recordings: [
          { id: 'mock-recording-1', recordingName: '\u5f55\u97f3\u540d\u79f0', duration: 158000, recordedAtMs: 1780056000000, fileUrl: '', transcriptText: '\u5404\u4f4d\u540c\u4e8b\uff0c\u6211\u4eec\u6765\u5bf9\u4e00\u4e0b\u8fd9\u5468\u7684\u4ea7\u54c1\u8fdb\u5ea6\u3002' },
          { id: 'mock-recording-2', recordingName: '\u5f55\u97f3\u540d\u79f0', duration: 158000, recordedAtMs: 1780056000000, fileUrl: '', transcriptTextUrl: 'mock://recording-2.txt' }
        ]
      }
    },
    parseMeta(value) {
      if (!value) {
        return {}
      }
      if (typeof value == 'object') {
        return value
      }
      const text = String(value).trim()
      if (!text) {
        return {}
      }
      const directMeta = this.parseMetaJson(text)
      if (directMeta) {
        return directMeta
      }
      const decodedText = this.decodeGzipBase64Text(text)
      if (!decodedText) {
        return {}
      }
      const decodedMeta = this.parseMetaJson(decodedText)
      if (decodedMeta) {
        console.log('[KnowledgeBaseSync] decoded meta:', decodedMeta)
      }
      return decodedMeta || {}
    },
    parseMetaJson(value) {
      try {
        const parsed = JSON.parse(value)
        return parsed && typeof parsed == 'object' ? parsed : null
      } catch (error) {
        return null
      }
    },
    decodeGzipBase64Text(value) {
      try {
        const base64 = String(value || '').replace(/\s/g, '').replace(/-/g, '+').replace(/_/g, '/')
        if (!base64) {
          return ''
        }
        const padding = base64.length % 4
        const paddedBase64 = padding ? base64 + '='.repeat(4 - padding) : base64
        const binary = typeof window !== 'undefined' && typeof window.atob == 'function'
          ? window.atob(paddedBase64)
          : atob(paddedBase64)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i)
        }
        return pako.ungzip(bytes, { to: 'string' })
      } catch (error) {
        return ''
      }
    },
    resolvePaperMeetingMeta(meta) {
      if (!meta || typeof meta != 'object') {
        return {}
      }
      const nested = this.pickValue(meta, [
        'paperMeeting',
        'paper_meeting',
        'paper',
        'meeting',
        'knowledgeBase',
        'knowledge_base',
        'knowledge',
        'kb'
      ])
      if (nested && typeof nested == 'object') {
        return nested
      }
      return meta
    },
    pickPaperCurrentPageValue(...sources) {
      const keys = ['currentPage', 'current_page']
      for (let i = 0; i < sources.length; i++) {
        const source = sources[i]
        if (!source || typeof source != 'object') {
          continue
        }
        const value = this.pickValue(source, keys)
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          return value
        }
      }
      return undefined
    },
    hasPaperCurrentPageField(...sources) {
      const keys = ['currentPage', 'current_page']
      for (let i = 0; i < sources.length; i++) {
        const source = sources[i]
        if (!source || typeof source != 'object') {
          continue
        }
        const value = this.pickValue(source, keys)
        if (value !== undefined && value !== null) {
          return true
        }
      }
      return false
    },
    toPaperCurrentPageNo(value) {
      const pageNo = Number(value)
      return Number.isFinite(pageNo) && pageNo > 0 ? Math.floor(pageNo) : 0
    },
    resolvePaperInitialPageNo(detail, sourceItem) {
      const pages = detail && Array.isArray(detail.imagePages) ? detail.imagePages : []
      if (pages.length == 0) {
        return 1
      }
      const fallbackPageNo = pages[0].pageNo || 1
      const sourceMeta = sourceItem && sourceItem.meta && typeof sourceItem.meta == 'object'
        ? sourceItem.meta
        : this.parseMeta(this.pickValue(sourceItem || {}, ['meta']))
      const sourcePaperMeeting = this.resolvePaperMeetingMeta(sourceMeta)
      const sourceCurrentPageSources = [sourcePaperMeeting, sourceMeta, sourceItem || {}]
      const currentPageValue = this.hasPaperCurrentPageField(...sourceCurrentPageSources)
        ? this.pickPaperCurrentPageValue(...sourceCurrentPageSources)
        : this.pickPaperCurrentPageValue(...sourceCurrentPageSources, detail || {})
      const targetPageNo = this.toPaperCurrentPageNo(currentPageValue)
      if (!targetPageNo) {
        return fallbackPageNo
      }
      const matchedPage = pages.find(page => Number(page.pageNo) == targetPageNo)
      return matchedPage ? matchedPage.pageNo : fallbackPageNo
    },
    pickValue(source, keys) {
      if (!source || typeof source != 'object') {
        return undefined
      }
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i]
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          return source[key]
        }
      }
      const sourceKeys = Object.keys(source)
      for (let i = 0; i < keys.length; i++) {
        const normalized = this.normalizeFieldKey(keys[i])
        const matched = sourceKeys.find(key => this.normalizeFieldKey(key) == normalized)
        if (matched) {
          return source[matched]
        }
      }
      return undefined
    },
    pickString(source, keys) {
      const value = this.pickValue(source, keys)
      if (value === undefined || value === null) {
        return ''
      }
      return String(value).trim()
    },
    pickBoolean(source, keys) {
      const value = this.pickValue(source, keys)
      if (typeof value == 'boolean') {
        return value
      }
      if (value === undefined || value === null || value === '') {
        return false
      }
      const normalized = this.normalizeFieldKey(value)
      return ['true', '1', 'yes', 'y', 'favorite', 'starred', 'deleted', 'folder', 'dir', 'locked', 'encrypted'].includes(normalized)
    },
    isPaperEncryptedItem(item) {
      if (!item) {
        return false
      }
      if (item.encrypted) {
        return true
      }
      const raw = item.raw && typeof item.raw == 'object' ? item.raw : item
      return this.pickBoolean(raw, ['encrypted', 'isEncrypted', 'locked', 'isLock', 'encrypt', 'encryp'])
    },
    normalizeFieldKey(value) {
      return String(value || '').toLowerCase().replace(/[\s_\-/.]/g, '')
    },
    comparePaperItems(a, b) {
      let result = 0
      if (this.sortField == 'name') {
        result = String(a.name || '').localeCompare(String(b.name || ''))
      } else {
        const aValue = this.getSortTime(a, this.sortField == 'createTime' ? 'creationTime' : 'lastModifiedTime')
        const bValue = this.getSortTime(b, this.sortField == 'createTime' ? 'creationTime' : 'lastModifiedTime')
        result = aValue - bValue
      }
      return this.sortDesc ? -result : result
    },
    getSortTime(item, field) {
      const value = item && item[field] ? item[field] : ''
      const timestamp = this.parseDateTime(value)
      return this.isDisplayableDateTime(timestamp) ? timestamp : 0
    },
    getDisplayDate(item) {
      return item && (item.creationTime || item.createdAt || item.createTime)
    },
    isPaperSelected(item) {
      return this.canSelectPaperItems && !!(item && this.selectedPaperIds.includes(item.id))
    },
    getPaperGridItemClasses(item, index) {
      return {
        folder: item && item.isFolder,
        selected: this.isPaperSelected(item),
        'grid-edge-left': this.isPaperGridLeftEdge(index),
        'grid-edge-right': this.isPaperGridRightEdge(index),
        'grid-edge-top': this.isPaperGridTopEdge(index),
        'grid-edge-bottom': this.isPaperGridBottomEdge(index)
      }
    },
    isPaperGridLeftEdge(index) {
      const columnCount = Math.max(1, Number(this.paperGridColumnCount) || 1)
      if (this.isPaperGridLastIncompleteRowLastItem(index)) {
        return false
      }
      return columnCount == 1 || index % columnCount == 0
    },
    isPaperGridRightEdge(index) {
      const columnCount = Math.max(1, Number(this.paperGridColumnCount) || 1)
      if (this.isPaperGridLastIncompleteRowLastItem(index)) {
        return false
      }
      return columnCount == 1 || (index + 1) % columnCount == 0
    },
    isPaperGridTopEdge(index) {
      const columnCount = Math.max(1, Number(this.paperGridColumnCount) || 1)
      return index < columnCount
    },
    isPaperGridBottomEdge() {
      return false
    },
    isPaperGridLastIncompleteRowLastItem(index) {
      const columnCount = Math.max(1, Number(this.paperGridColumnCount) || 1)
      const itemCount = this.visiblePaperItems.length
      return itemCount > 0 && itemCount % columnCount != 0 && index == itemCount - 1
    },
    updatePaperGridColumnCount() {
      if (this.viewMode != 'grid') {
        return
      }
      this.$nextTick(() => {
        const grid = this.$refs.paperGrid
        if (!grid || typeof window === 'undefined') {
          this.paperGridColumnCount = 1
          return
        }
        const styles = window.getComputedStyle(grid)
        const columns = String(styles.gridTemplateColumns || '')
          .split(/\s+/)
          .filter(column => column && column != 'none')
        this.paperGridColumnCount = Math.max(1, columns.length || 1)
      })
    },
    isPaperPagesGridTopEdge(index) {
      const columnCount = Math.max(1, Number(this.paperPagesGridColumnCount) || 1)
      return index < columnCount
    },
    isPaperPagesGridSelectedRowStart(index) {
      const columnCount = Math.max(1, Number(this.paperPagesGridColumnCount) || 1)
      const rowStart = Math.floor(index / columnCount) * columnCount
      if (rowStart == 0) {
        return true
      }
      return !this.hasSelectedPaperPagesGridRow(rowStart - columnCount, columnCount)
    },
    isPaperPagesGridSelectionContinuesBelow(index) {
      const columnCount = Math.max(1, Number(this.paperPagesGridColumnCount) || 1)
      const rowStart = Math.floor(index / columnCount) * columnCount
      return this.hasSelectedPaperPagesGridRow(rowStart + columnCount, columnCount)
    },
    hasSelectedPaperPagesGridRow(rowStart, columnCount) {
      if (rowStart < 0 || rowStart >= this.detailPages.length) {
        return false
      }
      const rowEnd = Math.min(rowStart + columnCount, this.detailPages.length)
      for (let index = rowStart; index < rowEnd; index += 1) {
        const page = this.detailPages[index]
        if (page && this.isDetailPageSelected(page.pageNo)) {
          return true
        }
      }
      return false
    },
    updatePaperPagesGridColumnCount() {
      if (!this.pageGridOpen) {
        return
      }
      this.$nextTick(() => {
        const grid = this.$refs.paperPagesGrid
        if (!grid || typeof window === 'undefined') {
          this.paperPagesGridColumnCount = 1
          return
        }
        const columns = String(window.getComputedStyle(grid).gridTemplateColumns || '')
          .split(/\s+/)
          .filter(column => column && column != 'none')
        this.paperPagesGridColumnCount = Math.max(1, columns.length || 1)
      })
    },
    handlePaperWindowResize() {
      this.updatePaperGridColumnCount()
      this.updatePaperPagesGridColumnCount()
      this.syncSelectionBarScrollbarOffset()
      this.selectionBarOffsetY = this.clampSelectionBarOffset(this.selectionBarOffsetY)
      if (this.detailMode && !this.pageGridOpen) {
        this.centerPaperPreviewScroll()
      }
    },
    syncSelectionBarScrollbarOffset() {
      this.$nextTick(() => {
        const area = this.$refs.paperListArea
        const nextOffset = area ? Math.max(0, area.offsetWidth - area.clientWidth) : 0
        if (this.selectionBarScrollbarOffset != nextOffset) {
          this.selectionBarScrollbarOffset = nextOffset
        }
      })
    },
    rememberPaperListScrollPosition() {
      const area = this.$refs.paperListArea
      this.paperListReturnScrollTop = area ? Math.max(0, Number(area.scrollTop) || 0) : 0
    },
    restorePaperListScrollPosition(attempt = 0) {
      const targetTop = Math.max(0, Number(this.paperListReturnScrollTop) || 0)
      this.$nextTick(() => {
        const area = this.$refs.paperListArea
        if (!area) {
          return
        }
        area.scrollTop = targetTop
        const maxTop = Math.max(0, area.scrollHeight - area.clientHeight)
        if (attempt < 4 && targetTop > 0 && area.scrollTop < Math.min(targetTop, maxTop) - 2) {
          setTimeout(() => this.restorePaperListScrollPosition(attempt + 1), 50)
        }
      })
    },
    startSelectionBarDrag(event) {
      if (!event || event.button !== 0) {
        return
      }
      if (event.target && event.target.closest && event.target.closest('button')) {
        return
      }
      event.preventDefault()
      this.selectionBarDragging = true
      this.selectionBarDragState = {
        startY: event.clientY,
        startOffset: this.selectionBarOffsetY
      }
      if (typeof document !== 'undefined') {
        document.addEventListener('mousemove', this.handleSelectionBarDrag)
        document.addEventListener('mouseup', this.stopSelectionBarDrag)
      }
    },
    handleSelectionBarDrag(event) {
      if (!this.selectionBarDragState || !event) {
        return
      }
      const nextOffset = this.selectionBarDragState.startOffset + event.clientY - this.selectionBarDragState.startY
      this.selectionBarOffsetY = this.clampSelectionBarOffset(nextOffset)
    },
    stopSelectionBarDrag() {
      if (typeof document !== 'undefined') {
        document.removeEventListener('mousemove', this.handleSelectionBarDrag)
        document.removeEventListener('mouseup', this.stopSelectionBarDrag)
      }
      this.selectionBarDragging = false
      this.selectionBarDragState = null
    },
    clampSelectionBarOffset(value) {
      const number = Number(value) || 0
      const maxOffset = this.getSelectionBarMaxOffset()
      return Math.min(Math.max(0, number), maxOffset)
    },
    getSelectionBarMaxOffset() {
      const workspace = this.$refs.paperWorkspace
      const layer = this.$refs.paperSelectionLayer
      if (!workspace || !layer) {
        return 0
      }
      const layerTop = layer.offsetTop || 0
      const layerHeight = layer.offsetHeight || 0
      return Math.max(0, workspace.clientHeight - layerTop - layerHeight)
    },
    resetSelectionBarOffset() {
      this.stopSelectionBarDrag()
      this.selectionBarOffsetY = 0
    },
    syncSelectionWithItems() {
      if (!this.canSelectPaperItems || this.selectedPaperIds.length == 0) {
        this.selectedPaperIds = []
        this.selectedPaperId = ''
        this.selectedPaperAnchorId = ''
        return
      }
      const existingIds = new Set(this.paperItems.map(item => item.id))
      this.selectedPaperIds = this.selectedPaperIds.filter(id => existingIds.has(id))
      this.selectedPaperId = this.selectedPaperIds[0] || ''
      if (!existingIds.has(this.selectedPaperAnchorId)) {
        this.selectedPaperAnchorId = this.selectedPaperId
      }
    },
    clearSelection() {
      this.clearPaperItemClickTimer()
      this.selectedPaperIds = []
      this.selectedPaperId = ''
      this.selectedPaperAnchorId = ''
      this.moreMenuId = ''
      this.resetSelectionBarOffset()
    },
    selectPaperItem(item, event) {
      if (!item || !this.canSelectPaperItems) {
        return
      }
      const hadSelection = this.selectedPaperIds.length > 0
      const result = selectItemWithWindowsRange(
        this.visiblePaperItems,
        this.selectedPaperIds,
        item,
        this.selectedPaperAnchorId,
        event
      )
      const selectedIds = result.selectedIds

      this.selectedPaperIds = selectedIds
      this.selectedPaperId = selectedIds[0] || ''
      this.selectedPaperAnchorId = result.anchorId
      if (!hadSelection || selectedIds.length == 0) {
        this.resetSelectionBarOffset()
      }
      this.moreMenuId = ''
      this.filterMenuOpen = false
      this.sortMenuOpen = false
      this.searchOpen = false
    },
    activatePaperListItem(item, options = {}) {
      if (!item) {
        return
      }
      if (this.canOpenPaperResourceFolder(item)) {
        this.openPaperResourceFolder(item)
        return
      }
      this.openPaperItem(item, options)
    },
    handlePaperItemClick(item, event) {
      if (!item) {
        return
      }
      this.prefetchPcPaperDetail(item, event)
      if (event && event.detail > 1) {
        this.clearPaperItemClickTimer()
        return
      }
      this.clearPaperItemClickTimer()
      if (!this.canSelectPaperItems) {
        return
      }
      const clickEvent = event
        ? { ctrlKey: event.ctrlKey, metaKey: event.metaKey, shiftKey: event.shiftKey }
        : null
      this.paperItemClickTimer = setTimeout(() => {
        this.paperItemClickTimer = null
        this.selectPaperItem(item, clickEvent)
      }, 260)
    },
    handlePaperItemDblClick(item, event) {
      this.clearPaperItemClickTimer()
      if (event && typeof event.preventDefault == 'function') {
        event.preventDefault()
      }
      this.activatePaperListItem(item, { force: true })
    },
    handlePaperItemKeyup(item) {
      if (!item) {
        return
      }
      this.clearPaperItemClickTimer()
      if (!this.canSelectPaperItems) {
        this.activatePaperListItem(item)
        return
      }
      this.selectPaperItem(item)
    },
    clearPaperItemClickTimer() {
      if (this.paperItemClickTimer) {
        clearTimeout(this.paperItemClickTimer)
        this.paperItemClickTimer = null
      }
    },
    canOpenPaperResourceFolder(item) {
      if (!item) {
        return false
      }
      const resourceType = Number(item.resourceType)
      return resourceType === 0 || resourceType === 5 || !!item.isFolder
    },
    isPaperDownloadableItem(item) {
      if (!item || !item.resourceId) {
        return false
      }
      if (this.isNonDownloadResourceId(item.resourceId)) {
        return false
      }
      const resourceType = Number(item.resourceType)
      if (Number.isFinite(resourceType) && (resourceType === 0 || resourceType === 5)) {
        return false
      }
      return !item.isFolder && !item.template
    },
    isPaperDeletableItem(item) {
      if (!this.canSelectPaperItems || !item || item.resourceId === undefined || item.resourceId === null) {
        return false
      }
      return String(item.resourceId).trim() !== ''
    },
    isPaperRenameableItem(item) {
      return this.isPaperDeletableItem(item)
    },
    isPaperMoveableItem(item) {
      if (!this.canUseKnowledgeBaseMove || !item || item.deleted) {
        return false
      }
      return this.getPaperResourceUuid(item) !== ''
    },
    isNonDownloadResourceId(resourceId) {
      const value = Number(resourceId)
      return Number.isFinite(value) && (value === 0 || value === 5)
    },
    getPaperResourceUuid(item) {
      if (!item) {
        return ''
      }
      const raw = item.raw && typeof item.raw == 'object' ? item.raw : item
      return this.pickString(raw, ['uuid', 'resourceUuid', 'resource_uuid'])
        || (item.resourceId !== undefined && item.resourceId !== null ? String(item.resourceId).trim() : '')
    },
    resetPaperListTransientState() {
      this.clearSelection()
      this.moreMenuId = ''
      this.filterMenuOpen = false
      this.sortMenuOpen = false
      this.searchOpen = false
      this.searchKeyword = ''
    },
    openPaperResourceFolder(item) {
      const folderSubTab = this.getPaperFolderSubTab(item)
      this.resetPaperListTransientState()
      const resourceId = item.resourceId || ''
      this.paperListResourceId = resourceId
      const folder = this.normalizePaperFolderPathItem({
        name: item.name || 'Knowledge Base',
        uuid: this.getPaperResourceUuid(item),
        resourceId,
        resourceType: item.resourceType,
        parentId: item.parentId,
        subTab: folderSubTab
      }, this.paperFolderPath.length)
      const folderPath = this.normalizeCloudPaperFolderPath(this.paperFolderPath)
      const existedIndex = folderPath.findIndex(crumb => crumb.pathKey == folder.pathKey)
      if (existedIndex >= 0) {
        this.paperFolderPath = folderPath.slice(0, existedIndex + 1)
      } else {
        this.paperFolderPath = folderPath.concat(folder)
      }
      this.loadPaperList()
    },
    openPaperBreadcrumb(index) {
      const folderPath = this.normalizeCloudPaperFolderPath(this.paperFolderPath)
      const breadcrumbs = [{ name: 'Knowledge Base', resourceId: '', pathKey: 'root' }].concat(folderPath)
      const target = breadcrumbs[index]
      if (!target || index == breadcrumbs.length - 1) {
        return
      }
      this.resetPaperListTransientState()
      this.paperListResourceId = target.resourceId || ''
      this.paperFolderPath = index <= 0 ? [] : folderPath.slice(0, index)
      this.loadPaperList()
    },
    getPaperFolderSubTab(item) {
      if (this.isPaperRootLevel) {
        return this.normalizePaperSubTab(item && item.subTab)
      }
      return this.activePaperSubTab
    },
    resolvePaperSubTab(item, meta) {
      const keys = ['subTab', 'sub_tab', 'subtab', 'subTabId', 'sub_tab_id']
      const value = this.pickValue(item, keys)
      if (value !== undefined && value !== null && value !== '') {
        return this.normalizePaperSubTab(value)
      }
      return this.normalizePaperSubTab(this.pickValue(meta, keys))
    },
    normalizePaperSubTab(value) {
      if (value === undefined || value === null || value === '') {
        return -1
      }
      const number = Number(value)
      return Number.isFinite(number) ? number : value
    },
    parseDateTime(value) {
      if (value instanceof Date) {
        return value.getTime()
      }
      const text = String(value || '').trim()
      if (!text) {
        return 0
      }
      const number = Number(text)
      if (Number.isFinite(number)) {
        if (number === 0) {
          return 0
        }
        return Math.abs(number) < 10000000000 ? number * 1000 : number
      }
      const direct = Date.parse(text)
      if (Number.isFinite(direct)) {
        return direct
      }
      const normalized = Date.parse(text.replace(/-/g, '/'))
      return Number.isFinite(normalized) ? normalized : 0
    },
    selectFilter(key) {
      if (this.paperFilter == key) {
        this.filterMenuOpen = false
        this.moreMenuId = ''
        return
      }
      this.clearSelection()
      this.paperFilter = key
      this.filterMenuOpen = false
      this.moreMenuId = ''
      this.loadPaperList()
    },
    selectSort(key) {
      if (this.sortField == key) {
        this.sortMenuOpen = false
        this.moreMenuId = ''
        return
      }
      this.sortField = key
      this.sortMenuOpen = false
      this.moreMenuId = ''
      this.loadPaperList()
    },
    toggleSortOrder() {
      this.sortDesc = !this.sortDesc
      this.moreMenuId = ''
      this.loadPaperList()
    },
    setViewMode(mode) {
      this.viewMode = mode
      this.moreMenuId = ''
    },
    toggleSearch() {
      this.searchOpen = !this.searchOpen
      this.$nextTick(() => {
        if (this.searchOpen && this.$refs.paperSearchInput) {
          this.$refs.paperSearchInput.focus()
        }
      })
    },
    closeFloatingMenus() {
      this.filterMenuOpen = false
      this.sortMenuOpen = false
      this.moreMenuId = ''
    },
    toggleMoreMenu(id) {
      this.moreMenuId = this.moreMenuId == id ? '' : id
    },
    handleSelectionAction(type) {
      this.performPaperAction(type, this.selectedPaperItems)
    },
    handleItemMenuAction(type, item) {
      this.performPaperAction(type, item ? [item] : [])
    },
    canPerformPaperAction(type, items) {
      const list = Array.isArray(items) ? items : []
      if (list.length == 0) {
        return false
      }
      if (type == 'rename') {
        return list.length == 1 && this.isPaperRenameableItem(list[0])
      }
      if (type == 'download') {
        return list.every(item => this.isPaperDownloadableItem(item))
      }
      if (type == 'delete') {
        return this.canSelectPaperItems && list.every(item => this.isPaperDeletableItem(item))
      }
      if (type == 'move') {
        return this.canUseKnowledgeBaseMove && !this.paperMoveSubmitting && list.every(item => this.isPaperMoveableItem(item))
      }
      if (list.length != 1) {
        return false
      }
      const item = list[0]
      if (type == 'encrypt') {
        return !item.encrypted
      }
      if (type == 'decrypt') {
        return !!item.encrypted
      }
      if (type == 'unfavorite') {
        return !!item.favorite
      }
      return type == 'rename' || type == 'favorite'
    },
    performPaperAction(type, items) {
      const list = Array.isArray(items) ? items.filter(Boolean) : []
      if (!this.canPerformPaperAction(type, list)) {
        return
      }
      this.moreMenuId = ''

      if (type == 'download') {
        if (!this.$canStartFileDownloadBatch(list.length)) {
          return
        }
        const encryptedItem = list.find(item => this.isPaperEncryptedItem(item))
        if (encryptedItem) {
          this.openPaperPasswordModal(encryptedItem, '', false, 'download', list)
          return
        }
        this.openPaperDownloadFormatModal(list)
        return
      }
      if (type == 'favorite') {
        this.toggleFavorite(list[0])
        return
      }
      if (type == 'unfavorite') {
        this.$set(list[0], 'favorite', false)
        if (this.paperFilter == 'favorite') {
          this.clearSelection()
        }
        this.$message.info(this.ui.fakeTip)
        return
      }
      if (type == 'encrypt') {
        this.$set(list[0], 'encrypted', true)
        this.$message.info(this.ui.fakeTip)
        return
      }
      if (type == 'decrypt') {
        this.$set(list[0], 'encrypted', false)
        this.$message.info(this.ui.fakeTip)
        return
      }
      if (type == 'delete') {
        this.openPaperDeleteModal(list)
        return
      }
      if (type == 'rename') {
        this.openPaperRenameModal(list[0])
        return
      }
      if (type == 'move') {
        this.openPaperMoveModal(list)
        return
      }
      this.fakeAction()
    },
    openPaperRenameModal(item) {
      if (!this.isPaperRenameableItem(item)) {
        this.showPaperRenameFailedMessage(item)
        return
      }
      const name = this.getPaperRenameSourceName(item).slice(0, 255)
      this.paperRenameTarget = item
      this.paperRenameOriginalName = name
      this.paperRenameInput = name
      this.paperRenameSubmitting = false
      this.paperRenameModalOpen = true
      this.moreMenuId = ''
      this.$nextTick(() => {
        const input = this.$refs.paperRenameInput
        if (input && typeof input.focus == 'function') {
          input.focus()
        }
        if (input && typeof input.select == 'function') {
          input.select()
        }
      })
    },
    closePaperRenameModal() {
      if (this.paperRenameSubmitting) {
        return
      }
      this.paperRenameRequestSeq += 1
      this.paperRenameModalOpen = false
      this.paperRenameTarget = null
      this.paperRenameInput = ''
      this.paperRenameOriginalName = ''
    },
    async confirmPaperRename() {
      if (this.paperRenameSubmitting) {
        return
      }
      const item = this.paperRenameTarget
      if (!this.isPaperRenameableItem(item)) {
        this.closePaperRenameModal()
        this.showPaperRenameFailedMessage(item)
        return
      }
      const nextName = String(this.paperRenameInput || '').trim().slice(0, 255)
      if (!nextName) {
        this.showPaperRenameEmptyMessage()
        return
      }
      const originalName = String(this.paperRenameOriginalName || this.getPaperRenameSourceName(item) || '').trim()
      if (nextName == originalName) {
        this.showPaperRenameSameMessage()
        return
      }
      const requestId = this.paperRenameRequestSeq + 1
      this.paperRenameRequestSeq = requestId
      this.paperRenameSubmitting = true
      try {
        const ret = await renameWebSyncResource({
          uuid: String(item.resourceId).trim(),
          appType: KNOWLEDGE_BASE_APP_TYPE,
          name: nextName
        }, { skipGlobalErrorMessage: true })
        if (requestId != this.paperRenameRequestSeq) {
          return
        }
        if (!this.isPaperRenameSuccessResponse(ret)) {
          throw ret
        }
        this.updatePaperResourceName(item, nextName)
        this.clearSelection()
        this.paperRenameModalOpen = false
        this.paperRenameTarget = null
        this.paperRenameInput = ''
        this.paperRenameOriginalName = ''
        this.showPaperRenameSuccessMessage()
      } catch (error) {
        if (requestId != this.paperRenameRequestSeq) {
          return
        }
        this.showPaperRenameFailedMessage(item, error)
      } finally {
        if (requestId == this.paperRenameRequestSeq) {
          this.paperRenameSubmitting = false
        }
      }
    },
    getPaperRenameSourceName(item) {
      if (!item) {
        return ''
      }
      const name = item.name !== undefined && item.name !== null ? String(item.name).trim() : ''
      return name || this.getKnowledgeBaseDisplayName(item)
    },
    limitPaperRenameInput() {
      const value = String(this.paperRenameInput || '')
      if (value.length > 255) {
        this.paperRenameInput = value.slice(0, 255)
      }
    },
    clearPaperRenameInput() {
      if (this.paperRenameSubmitting) {
        return
      }
      this.paperRenameInput = ''
      this.$nextTick(() => {
        const input = this.$refs.paperRenameInput
        if (input && typeof input.focus == 'function') {
          input.focus()
        }
      })
    },
    isSamePaperResource(a, b) {
      if (!a || !b) {
        return false
      }
      const aResourceId = a.resourceId !== undefined && a.resourceId !== null ? String(a.resourceId).trim() : ''
      const bResourceId = b.resourceId !== undefined && b.resourceId !== null ? String(b.resourceId).trim() : ''
      if (aResourceId && bResourceId) {
        return aResourceId == bResourceId
      }
      return !!(a.id && b.id && a.id == b.id)
    },
    updatePaperResourceName(target, name) {
      const applyName = item => {
        if (!item) {
          return
        }
        this.$set(item, 'name', name)
        if (item.raw && typeof item.raw == 'object') {
          this.$set(item.raw, 'name', name)
          this.$set(item.raw, 'resourceName', name)
        }
        if (item.meta && item.meta.paperMeeting && typeof item.meta.paperMeeting == 'object') {
          this.$set(item.meta.paperMeeting, 'name', name)
        }
      }
      const listItem = this.paperItems.find(item => this.isSamePaperResource(item, target))
      applyName(listItem)
      if (target && target !== listItem) {
        applyName(target)
      }
      if (this.currentPaper && this.isSamePaperResource(this.currentPaper, target)) {
        applyName(this.currentPaper)
      }
      if (this.paperDetail && this.currentPaper && this.isSamePaperResource(this.currentPaper, target)) {
        this.$set(this.paperDetail, 'name', name)
      }
      const folderPath = this.normalizeCloudPaperFolderPath(this.paperFolderPath)
      let changed = false
      const nextPath = folderPath.map(crumb => {
        if (!this.isSamePaperResource(crumb, target)) {
          return crumb
        }
        changed = true
        return Object.assign({}, crumb, { name })
      })
      if (changed) {
        this.paperFolderPath = nextPath
      }
    },
    isPaperRenameSuccessResponse(source) {
      return !this.isPaperApiCodeFailed(source)
    },
    showPaperRenameSameMessage() {
      if (this.$message && typeof this.$message.info == 'function') {
        this.$message.info('\u540d\u79f0\u548c\u4e4b\u524d\u7684\u4e00\u6837')
      }
    },
    showPaperRenameEmptyMessage() {
      if (this.$message && typeof this.$message.warning == 'function') {
        this.$message.warning('\u540d\u79f0\u4e0d\u80fd\u4e3a\u7a7a')
      }
    },
    showPaperRenameSuccessMessage() {
      if (this.$message && typeof this.$message.success == 'function') {
        this.$message.success('\u91cd\u547d\u540d\u6210\u529f')
      }
    },
    showPaperRenameFailedMessage(item, error) {
      const message = this.getPaperRenameFailureMessage(item, error)
      if (this.$message && typeof this.$message.error == 'function') {
        this.$message.error(message)
      }
    },
    getPaperRenameFailureMessage(item, error) {
      const name = item ? this.getKnowledgeBaseDisplayName(item) : ''
      const fallback = name ? `${name}\u91cd\u547d\u540d\u5931\u8d25` : '\u91cd\u547d\u540d\u5931\u8d25'
      const detail = this.getPaperApiErrorMessage(error)
      return detail ? `${fallback}\uff1a${detail}` : fallback
    },
    openPaperCreateFolderModal() {
      if (!this.canCreateKnowledgeBaseFolder) {
        return
      }
      this.stopPaperCreateFolderTimer()
      this.paperCreateFolderInput = ''
      this.paperCreateFolderSubmitting = false
      this.paperCreateFolderModalOpen = true
      this.moreMenuId = ''
      this.clearSelection()
      this.$nextTick(() => {
        const input = this.$refs.paperCreateFolderInput
        if (input && typeof input.focus == 'function') {
          input.focus()
        }
      })
    },
    closePaperCreateFolderModal() {
      if (this.paperCreateFolderSubmitting) {
        return
      }
      this.paperCreateFolderRequestSeq += 1
      this.paperCreateFolderModalOpen = false
      this.paperCreateFolderInput = ''
      this.stopPaperCreateFolderTimer()
    },
    async confirmPaperCreateFolder() {
      if (this.paperCreateFolderSubmitting) {
        return
      }
      if (!this.canCreateKnowledgeBaseFolder) {
        this.closePaperCreateFolderModal()
        return
      }
      const name = String(this.paperCreateFolderInput || '').trim().slice(0, 255)
      if (!name) {
        this.showPaperCreateFolderEmptyMessage()
        return
      }
      const requestId = this.paperCreateFolderRequestSeq + 1
      this.paperCreateFolderRequestSeq = requestId
      this.paperCreateFolderSubmitting = true
      this.startPaperCreateFolderTimer()
      const startedAt = this.paperCreateFolderStartedAt
      const waitSeconds = this.paperCreateFolderWaitSeconds
      try {
        const ret = await createWebSyncFolder({
          appType: KNOWLEDGE_BASE_APP_TYPE,
          name,
          parentId: this.getPaperCreateFolderParentId()
        }, { skipGlobalErrorMessage: true })
        if (requestId != this.paperCreateFolderRequestSeq) {
          return
        }
        if (!this.isPaperCreateFolderSuccessResponse(ret)) {
          throw ret
        }
        await this.waitPaperCreateFolderMinimumDuration(startedAt, waitSeconds)
        if (requestId != this.paperCreateFolderRequestSeq) {
          return
        }
        this.paperCreateFolderModalOpen = false
        this.paperCreateFolderInput = ''
        this.showPaperCreateFolderSuccessMessage()
        this.loadPaperList({ pageIndex: 1 })
        this.scrollPaperListToTop()
      } catch (error) {
        if (requestId != this.paperCreateFolderRequestSeq) {
          return
        }
        this.showPaperCreateFolderFailedMessage(error)
      } finally {
        if (requestId == this.paperCreateFolderRequestSeq) {
          this.paperCreateFolderSubmitting = false
          this.stopPaperCreateFolderTimer()
        }
      }
    },
    startPaperCreateFolderTimer() {
      this.stopPaperCreateFolderTimer()
      this.paperCreateFolderWaitSeconds = this.getPaperCreateFolderRandomWaitSeconds()
      this.paperCreateFolderStartedAt = Date.now()
      this.paperCreateFolderElapsedSeconds = 1
      this.paperCreateFolderTimer = setInterval(() => {
        const elapsedSeconds = Math.floor((Date.now() - this.paperCreateFolderStartedAt) / 1000) + 1
        this.paperCreateFolderElapsedSeconds = Math.max(1, Math.min(this.paperCreateFolderWaitSeconds, elapsedSeconds))
        if (elapsedSeconds >= this.paperCreateFolderWaitSeconds && this.paperCreateFolderTimer) {
          clearInterval(this.paperCreateFolderTimer)
          this.paperCreateFolderTimer = null
        }
      }, 250)
    },
    getPaperCreateFolderRandomWaitSeconds() {
      return 3 + Math.floor(Math.random() * 3)
    },
    stopPaperCreateFolderTimer() {
      if (this.paperCreateFolderTimer) {
        clearInterval(this.paperCreateFolderTimer)
        this.paperCreateFolderTimer = null
      }
      this.paperCreateFolderElapsedSeconds = 0
      this.paperCreateFolderStartedAt = 0
      this.paperCreateFolderWaitSeconds = 5
    },
    waitPaperCreateFolderMinimumDuration(startedAt, waitSeconds = this.paperCreateFolderWaitSeconds) {
      const elapsed = Date.now() - startedAt
      const seconds = Math.max(3, Math.min(5, Number(waitSeconds) || 5))
      const delay = Math.max(0, seconds * 1000 - elapsed)
      if (delay <= 0) {
        return Promise.resolve()
      }
      return new Promise(resolve => setTimeout(resolve, delay))
    },
    getPaperCreateFolderParentId() {
      const folderPath = this.normalizeCloudPaperFolderPath(this.paperFolderPath)
      const folder = folderPath.length > 0 ? folderPath[folderPath.length - 1] : null
      if (!folder) {
        return ''
      }
      return this.getPaperResourceUuid(folder) || (this.paperListResourceId ? String(this.paperListResourceId).trim() : '')
    },
    isPaperCreateFolderSuccessResponse(source) {
      return !this.isPaperApiCodeFailed(source)
    },
    limitPaperCreateFolderInput() {
      const value = String(this.paperCreateFolderInput || '')
      if (value.length > 255) {
        this.paperCreateFolderInput = value.slice(0, 255)
      }
    },
    clearPaperCreateFolderInput() {
      if (this.paperCreateFolderSubmitting) {
        return
      }
      this.paperCreateFolderInput = ''
      this.$nextTick(() => {
        const input = this.$refs.paperCreateFolderInput
        if (input && typeof input.focus == 'function') {
          input.focus()
        }
      })
    },
    showPaperCreateFolderEmptyMessage() {
      if (this.$message && typeof this.$message.warning == 'function') {
        this.$message.warning(this.ui.folderNameEmpty || this.ui.nameRequired || '\u6587\u4ef6\u5939\u540d\u79f0\u4e0d\u80fd\u4e3a\u7a7a')
      }
    },
    showPaperCreateFolderSuccessMessage() {
      if (this.$message && typeof this.$message.success == 'function') {
        this.$message.success(this.ui.createFolderSuccess || '\u65b0\u5efa\u6210\u529f')
      }
    },
    showPaperCreateFolderFailedMessage(error) {
      const detail = this.getPaperApiErrorMessage(error)
      const fallback = this.ui.createFolderFailed || '\u65b0\u5efa\u5931\u8d25'
      const message = detail ? `${fallback}\uff1a${detail}` : fallback
      if (this.$message && typeof this.$message.error == 'function') {
        this.$message.error(message)
      }
    },
    normalizePaperMoveFolderPath(folderPath) {
      return this.normalizeCloudPaperFolderPath(folderPath)
    },
    getKnowledgeBaseMoveRootFolder() {
      return this.knowledgeBasePickNotesRootFolder || null
    },
    getKnowledgeBaseMoveRootResourceId(rootFolder = this.getKnowledgeBaseMoveRootFolder()) {
      const folderResourceId = this.getPaperResourceUuid(rootFolder)
      if (folderResourceId && !this.isNonDownloadResourceId(folderResourceId)) {
        return folderResourceId
      }
      const folderPath = this.normalizeCloudPaperFolderPath(this.paperFolderPath)
      const firstChild = folderPath.length > 1 ? folderPath[1] : null
      const childParentId = firstChild && firstChild.parentId !== undefined && firstChild.parentId !== null
        ? String(firstChild.parentId).trim()
        : ''
      if (childParentId && !this.isNonDownloadResourceId(childParentId)) {
        return childParentId
      }
      if (folderPath.length <= 1) {
        const listResourceId = this.paperListResourceId ? String(this.paperListResourceId).trim() : ''
        if (listResourceId && !this.isNonDownloadResourceId(listResourceId)) {
          return listResourceId
        }
      }
      return folderResourceId || ''
    },
    openPaperMoveModal(items) {
      if (!this.canUseKnowledgeBaseMove) {
        return
      }
      const sourceRootFolder = this.getKnowledgeBaseMoveRootFolder()
      const rootResourceId = this.getKnowledgeBaseMoveRootResourceId(sourceRootFolder)
      const rootFolder = sourceRootFolder
        ? Object.assign({}, sourceRootFolder, { resourceId: rootResourceId || sourceRootFolder.resourceId || '' })
        : null
      const list = Array.isArray(items) ? items.filter(item => this.isPaperMoveableItem(item)) : []
      if (!rootFolder || list.length == 0) {
        return
      }
      this.paperMoveItems = list.slice()
      this.paperMoveModalOpen = true
      this.paperMoveSubmitting = false
      this.paperMoveFolderPath = [rootFolder]
      this.paperMoveResourceId = rootResourceId
      this.paperMoveSelectedFolder = null
      this.paperMovePendingLoad = null
      this.moreMenuId = ''
      this.resetPaperMoveFolderPagination()
      this.loadPaperMoveFolders({ pageIndex: 1 })
    },
    closePaperMoveModal(options = {}) {
      if (this.paperMoveSubmitting && !(options && options.force)) {
        return
      }
      this.paperMoveRequestSeq += 1
      this.paperMoveModalOpen = false
      this.resetPaperMoveModalState()
    },
    resetPaperMoveModalState() {
      this.paperMoveItems = []
      this.paperMoveSubmitting = false
      this.paperMoveFolders = []
      this.paperMoveSelectedFolder = null
      this.paperMoveFolderPath = []
      this.paperMoveResourceId = ''
      this.paperMoveLoading = false
      this.paperMoveAppendLoading = false
      this.paperMovePendingLoad = null
      this.resetPaperMoveFolderPagination()
      this.closePaperMoveCreateFolderModal({ force: true })
    },
    resetPaperMoveFolderPagination() {
      this.paperMovePageIndex = 1
      this.paperMoveTotalCount = 0
      this.paperMoveLastPageSize = 0
      this.paperMoveReachedEnd = false
      this.paperMoveAppendLoading = false
    },
    getPaperMoveFolderPageSize() {
      const pageSize = Number(this.paperMovePageSize)
      return Number.isFinite(pageSize) && pageSize > 0 ? Math.floor(pageSize) : 100
    },
    normalizePaperMoveFolderLoadOptions(options = {}) {
      const source = options && typeof options == 'object' ? options : {}
      const append = source.append === true
      let pageIndex = Number(source.pageIndex)
      if (!Number.isFinite(pageIndex) || pageIndex <= 0) {
        pageIndex = append ? (Number(this.paperMovePageIndex) || 1) + 1 : 1
      }
      return {
        append,
        pageIndex: Math.max(1, Math.floor(pageIndex))
      }
    },
    buildPaperMoveFolderParams(pageIndex = this.paperMovePageIndex) {
      return {
        pageIndex,
        pageSize: this.getPaperMoveFolderPageSize(),
        appType: KNOWLEDGE_BASE_APP_TYPE,
        resourceId: this.paperMoveResourceId || ''
      }
    },
    loadPaperMoveFolders(options = {}) {
      if (!this.paperMoveModalOpen) {
        return Promise.resolve(false)
      }
      const pageOptions = this.normalizePaperMoveFolderLoadOptions(options)
      if (this.paperMoveLoading || this.paperMoveAppendLoading) {
        this.paperMovePendingLoad = pageOptions
        return Promise.resolve(false)
      }
      const requestId = this.paperMoveRequestSeq + 1
      this.paperMoveRequestSeq = requestId
      if (pageOptions.append) {
        this.paperMoveAppendLoading = true
      } else {
        this.paperMoveLoading = true
        this.paperMoveFolders = []
        this.paperMoveSelectedFolder = null
      }
      return getResourceSyncFolderPage(this.buildPaperMoveFolderParams(pageOptions.pageIndex), { skipGlobalErrorMessage: true }).then(ret => {
        if (requestId != this.paperMoveRequestSeq) {
          return false
        }
        const payload = this.resolveApiPayload(ret)
        if (payload === null || this.isPaperApiCodeFailed(ret)) {
          throw ret
        }
        this.applyPaperMoveFolderPage(payload, this.normalizePaperMoveFolderList(payload), pageOptions)
        return true
      }).catch(error => {
        if (requestId != this.paperMoveRequestSeq) {
          return false
        }
        this.handlePaperMoveFolderLoadFailed(error, pageOptions)
        return false
      }).finally(() => {
        if (requestId != this.paperMoveRequestSeq) {
          return
        }
        this.paperMoveLoading = false
        this.paperMoveAppendLoading = false
        if (this.paperMovePendingLoad) {
          const pendingOptions = this.paperMovePendingLoad
          this.paperMovePendingLoad = null
          this.loadPaperMoveFolders(pendingOptions)
        }
      })
    },
    applyPaperMoveFolderPage(payload, folders, options = {}) {
      const list = Array.isArray(folders) ? folders : []
      const total = this.extractPaperListTotalCount(payload)
      const pageIndex = Math.max(1, Number(options.pageIndex) || 1)
      const pageSize = this.getPaperMoveFolderPageSize()
      this.paperMovePageIndex = pageIndex
      this.paperMoveLastPageSize = list.length
      this.paperMoveTotalCount = total
      this.paperMoveFolders = options.append ? this.mergePaperMoveFolders(this.paperMoveFolders, list) : list
      this.paperMoveReachedEnd = total > 0
        ? pageIndex >= Math.max(1, Math.ceil(total / pageSize)) || list.length < pageSize
        : list.length < pageSize
    },
    mergePaperMoveFolders(currentFolders, nextFolders) {
      const merged = Array.isArray(currentFolders) ? currentFolders.slice() : []
      const seen = new Set(merged.map(folder => folder && folder.id).filter(Boolean))
      ;(Array.isArray(nextFolders) ? nextFolders : []).forEach(folder => {
        const id = folder && folder.id
        if (id && seen.has(id)) {
          return
        }
        if (id) {
          seen.add(id)
        }
        merged.push(folder)
      })
      return merged
    },
    normalizePaperMoveFolderList(payload) {
      return this.extractArray(payload)
        .filter(item => this.isPaperMoveFolderCandidate(item))
        .map((item, index) => this.normalizePaperMoveFolder(item, index))
        .filter(Boolean)
    },
    isPaperMoveFolderCandidate(raw) {
      const item = raw && typeof raw == 'object' ? raw : {}
      const resourceType = this.pickValue(item, ['resourceType'])
      const numericResourceType = Number(resourceType)
      if (resourceType !== undefined && resourceType !== null && resourceType !== '' && Number.isFinite(numericResourceType)) {
        return numericResourceType === 0 || numericResourceType === 5
      }
      const explicitFolder = this.pickValue(item, ['isFolder', 'folder', 'isDir', 'directory'])
      if (explicitFolder !== undefined && explicitFolder !== null && explicitFolder !== '') {
        return this.pickBoolean(item, ['isFolder', 'folder', 'isDir', 'directory'])
      }
      const explicitFile = this.pickValue(item, ['isFile', 'file'])
      if (explicitFile !== undefined && explicitFile !== null && explicitFile !== '') {
        return !this.pickBoolean(item, ['isFile', 'file'])
      }
      return true
    },
    normalizePaperMoveFolder(raw, index) {
      const item = raw && typeof raw == 'object' ? raw : {}
      const explicitResourceId = this.pickValue(item, ['resourceId'])
      const fallbackResourceId = this.pickString(item, ['uuid', 'id', 'resourceUuid', 'resource_uuid', 'folderId'])
      const resourceId = explicitResourceId !== undefined && explicitResourceId !== null
        ? String(explicitResourceId).trim()
        : fallbackResourceId
      const parentId = this.pickString(item, ['parentId', 'parent_id', 'pid'])
      const resourceType = this.pickValue(item, ['resourceType', 'type'])
      const name = this.pickString(item, ['name', 'title', 'fileName', 'resourceName', 'folderName']) || `${this.ui.createFolder || 'Folder'} ${index + 1}`
      const fileSize = this.pickValue(item, ['fileSize', 'file_size', 'size', 'fileSizeBytes'])
      const pathKey = this.getPaperFolderPathKey({ resourceId, resourceType, name }, index)
      const id = this.getPaperItemKey({ resourceId, resourceType, name }, index) || `paper-move-folder-${index}`
      return {
        id,
        pathKey,
        resourceId,
        parentId,
        resourceType,
        name,
        fileSize,
        raw: item
      }
    },
    handlePaperMoveFolderLoadFailed(error, options = {}) {
      if (!options.append) {
        this.resetPaperMoveFolderPagination()
        this.paperMoveFolders = []
        this.paperMoveSelectedFolder = null
      }
      const message = this.getPaperApiErrorMessage(error) || this.ui.loadFailed || '\u52a0\u8f7d\u5931\u8d25'
      if (this.$message && typeof this.$message.error == 'function') {
        this.$message.error(message)
      }
    },
    loadNextPaperMoveFolderPage() {
      if (this.paperMoveLoading || this.paperMoveAppendLoading || !this.hasNextPaperMoveFolderPage) {
        return
      }
      this.loadPaperMoveFolders({
        pageIndex: (Number(this.paperMovePageIndex) || 1) + 1,
        append: true
      })
    },
    handlePaperMoveFolderScroll(event) {
      const target = event && event.target ? event.target : this.$refs.paperMoveFolderList
      if (!target || this.paperMoveLoading || this.paperMoveAppendLoading || !this.hasNextPaperMoveFolderPage) {
        return
      }
      const distance = target.scrollHeight - target.scrollTop - target.clientHeight
      if (distance <= 80) {
        this.loadNextPaperMoveFolderPage()
      }
    },
    isPaperMoveBreadcrumbLocked(index) {
      return index <= 0
    },
    openPaperMoveBreadcrumb(index) {
      if (this.paperMoveSubmitting || this.paperMoveLoading || this.isPaperMoveBreadcrumbLocked(index)) {
        return
      }
      const folderPath = this.normalizePaperMoveFolderPath(this.paperMoveFolderPath)
      const breadcrumbs = this.paperMoveBreadcrumbItems
      const target = breadcrumbs[index]
      if (!target || index == breadcrumbs.length - 1) {
        return
      }
      this.paperMoveResourceId = target.resourceId || ''
      this.paperMoveFolderPath = folderPath.slice(0, index)
      this.loadPaperMoveFolders({ pageIndex: 1 })
    },
    selectPaperMoveFolder(folder) {
      if (this.paperMoveSubmitting || this.isPaperMoveFolderDisabled(folder)) {
        return
      }
      this.paperMoveSelectedFolder = folder
    },
    enterPaperMoveFolder(folder) {
      if (this.paperMoveSubmitting || this.paperMoveLoading || this.isPaperMoveFolderDisabled(folder)) {
        return
      }
      const resourceId = this.getPaperMoveFolderResourceId(folder)
      if (!resourceId) {
        return
      }
      const normalizedFolder = this.normalizePaperFolderPathItem({
        name: folder.name,
        resourceId,
        uuid: resourceId,
        parentId: folder.parentId,
        resourceType: folder.resourceType,
        fileSize: folder.fileSize,
        pathKey: folder.pathKey
      }, this.paperMoveFolderPath.length)
      const folderPath = this.normalizePaperMoveFolderPath(this.paperMoveFolderPath)
      this.paperMoveFolderPath = normalizedFolder ? folderPath.concat(normalizedFolder) : folderPath
      this.paperMoveResourceId = resourceId
      this.loadPaperMoveFolders({ pageIndex: 1 })
    },
    getPaperMoveFolderResourceId(folder) {
      if (!folder) {
        return ''
      }
      const raw = folder.raw && typeof folder.raw == 'object' ? folder.raw : folder
      return this.pickString(raw, ['resourceId', 'resource_id', 'uuid', 'id', 'resourceUuid', 'resource_uuid', 'folderId'])
        || (folder.resourceId !== undefined && folder.resourceId !== null ? String(folder.resourceId).trim() : '')
    },
    getPaperMoveTargetParentId() {
      const selectedId = this.getPaperMoveFolderResourceId(this.paperMoveSelectedFolder)
      if (selectedId) {
        return selectedId
      }
      return this.paperMoveResourceId ? String(this.paperMoveResourceId).trim() : ''
    },
    getPaperMoveOriginalParentId(item) {
      if (!item) {
        return ''
      }
      const raw = item.raw && typeof item.raw == 'object' ? item.raw : item
      const parentId = this.pickString(raw, ['parentId', 'parent_id', 'pid'])
        || (item.parentId !== undefined && item.parentId !== null ? String(item.parentId).trim() : '')
      if (parentId) {
        return parentId
      }
      return this.paperListResourceId ? String(this.paperListResourceId).trim() : ''
    },
    isPaperMoveTargetSameAsOriginalParent(parentId) {
      const targetParentId = parentId === undefined || parentId === null ? '' : String(parentId).trim()
      return this.paperMoveItems.some(item => this.getPaperMoveOriginalParentId(item) == targetParentId)
    },
    isPaperMoveFolderSelected(folder) {
      const selectedId = this.getPaperMoveFolderResourceId(this.paperMoveSelectedFolder)
      const folderId = this.getPaperMoveFolderResourceId(folder)
      return !!selectedId && !!folderId && selectedId == folderId
    },
    isPaperMoveFolderDisabled(folder) {
      const targetId = this.getPaperMoveFolderResourceId(folder)
      if (!targetId) {
        return true
      }
      return this.paperMoveItems.some(item => this.getPaperResourceUuid(item) == targetId)
    },
    getPaperMoveFolderIcon(folder) {
      const value = Number(folder && folder.fileSize)
      return Number.isFinite(value) && value > 0 ? this.paperIcons.folderListFilled : this.paperIcons.folderListEmpty
    },
    getPaperMoveCreateFolderSelectionMarker(ret, name) {
      const payload = this.resolveApiPayload(ret)
      const sources = []
      if (payload && typeof payload == 'object') {
        sources.push(payload)
        ;['resource', 'folder', 'item', 'data'].forEach(key => {
          if (payload[key] && typeof payload[key] == 'object') {
            sources.push(payload[key])
          }
        })
      }
      const resourceId = sources.reduce((result, source) => {
        return result || this.pickString(source, ['resourceId', 'resource_id', 'uuid', 'id', 'resourceUuid', 'resource_uuid', 'folderId'])
      }, '')
      return {
        resourceId,
        name: String(name || '').trim(),
        parentId: this.paperMoveResourceId || ''
      }
    },
    selectPaperMoveCreatedFolder(marker) {
      if (!marker || !Array.isArray(this.paperMoveFolders) || this.paperMoveFolders.length == 0) {
        return false
      }
      const markerParentId = marker.parentId === undefined || marker.parentId === null ? '' : String(marker.parentId).trim()
      const currentParentId = this.paperMoveResourceId ? String(this.paperMoveResourceId).trim() : ''
      if (markerParentId != currentParentId) {
        return false
      }
      const resourceId = marker.resourceId ? String(marker.resourceId).trim() : ''
      let target = null
      if (resourceId) {
        target = this.paperMoveFolders.find(folder => this.getPaperMoveFolderResourceId(folder) == resourceId)
      }
      if (!target && marker.name) {
        target = this.paperMoveFolders.find(folder => String(folder && folder.name || '').trim() == marker.name)
      }
      if (!target || this.isPaperMoveFolderDisabled(target)) {
        return false
      }
      this.paperMoveSelectedFolder = target
      return true
    },
    openPaperMoveCreateFolderModal() {
      if (!this.paperMoveModalOpen || this.paperMoveSubmitting) {
        return
      }
      this.stopPaperMoveCreateFolderTimer()
      this.paperMoveCreateFolderInput = ''
      this.paperMoveCreateFolderSubmitting = false
      this.paperMoveCreateFolderModalOpen = true
      this.$nextTick(() => {
        const input = this.$refs.paperMoveCreateFolderInput
        if (input && typeof input.focus == 'function') {
          input.focus()
        }
      })
    },
    closePaperMoveCreateFolderModal(options = {}) {
      if (this.paperMoveCreateFolderSubmitting && !(options && options.force)) {
        return
      }
      this.paperMoveCreateFolderRequestSeq += 1
      this.paperMoveCreateFolderModalOpen = false
      this.paperMoveCreateFolderInput = ''
      this.paperMoveCreateFolderSubmitting = false
      this.stopPaperMoveCreateFolderTimer()
    },
    async confirmPaperMoveCreateFolder() {
      if (this.paperMoveCreateFolderSubmitting) {
        return
      }
      const name = String(this.paperMoveCreateFolderInput || '').trim().slice(0, 255)
      if (!name) {
        this.showPaperCreateFolderEmptyMessage()
        return
      }
      const requestId = this.paperMoveCreateFolderRequestSeq + 1
      this.paperMoveCreateFolderRequestSeq = requestId
      this.paperMoveCreateFolderSubmitting = true
      this.startPaperMoveCreateFolderTimer()
      const startedAt = this.paperMoveCreateFolderStartedAt
      const waitSeconds = this.paperMoveCreateFolderWaitSeconds
      try {
        const ret = await createWebSyncFolder({
          appType: KNOWLEDGE_BASE_APP_TYPE,
          name,
          parentId: this.paperMoveResourceId || ''
        }, { skipGlobalErrorMessage: true })
        if (requestId != this.paperMoveCreateFolderRequestSeq) {
          return
        }
        if (!this.isPaperCreateFolderSuccessResponse(ret)) {
          throw ret
        }
        const selectionMarker = this.getPaperMoveCreateFolderSelectionMarker(ret, name)
        await this.waitPaperCreateFolderMinimumDuration(startedAt, waitSeconds)
        if (requestId != this.paperMoveCreateFolderRequestSeq) {
          return
        }
        this.paperMoveCreateFolderModalOpen = false
        this.paperMoveCreateFolderInput = ''
        this.showPaperCreateFolderSuccessMessage()
        await this.loadPaperMoveFolders({ pageIndex: 1 })
        this.selectPaperMoveCreatedFolder(selectionMarker)
      } catch (error) {
        if (requestId != this.paperMoveCreateFolderRequestSeq) {
          return
        }
        this.showPaperCreateFolderFailedMessage(error)
      } finally {
        if (requestId == this.paperMoveCreateFolderRequestSeq) {
          this.paperMoveCreateFolderSubmitting = false
          this.stopPaperMoveCreateFolderTimer()
        }
      }
    },
    startPaperMoveCreateFolderTimer() {
      this.stopPaperMoveCreateFolderTimer()
      this.paperMoveCreateFolderWaitSeconds = this.getPaperCreateFolderRandomWaitSeconds()
      this.paperMoveCreateFolderStartedAt = Date.now()
      this.paperMoveCreateFolderElapsedSeconds = 1
      this.paperMoveCreateFolderTimer = setInterval(() => {
        const elapsedSeconds = Math.floor((Date.now() - this.paperMoveCreateFolderStartedAt) / 1000) + 1
        this.paperMoveCreateFolderElapsedSeconds = Math.max(1, Math.min(this.paperMoveCreateFolderWaitSeconds, elapsedSeconds))
        if (elapsedSeconds >= this.paperMoveCreateFolderWaitSeconds && this.paperMoveCreateFolderTimer) {
          clearInterval(this.paperMoveCreateFolderTimer)
          this.paperMoveCreateFolderTimer = null
        }
      }, 250)
    },
    stopPaperMoveCreateFolderTimer() {
      if (this.paperMoveCreateFolderTimer) {
        clearInterval(this.paperMoveCreateFolderTimer)
        this.paperMoveCreateFolderTimer = null
      }
      this.paperMoveCreateFolderElapsedSeconds = 0
      this.paperMoveCreateFolderStartedAt = 0
      this.paperMoveCreateFolderWaitSeconds = 5
    },
    limitPaperMoveCreateFolderInput() {
      const value = String(this.paperMoveCreateFolderInput || '')
      if (value.length > 255) {
        this.paperMoveCreateFolderInput = value.slice(0, 255)
      }
    },
    clearPaperMoveCreateFolderInput() {
      if (this.paperMoveCreateFolderSubmitting) {
        return
      }
      this.paperMoveCreateFolderInput = ''
      this.$nextTick(() => {
        const input = this.$refs.paperMoveCreateFolderInput
        if (input && typeof input.focus == 'function') {
          input.focus()
        }
      })
    },
    async confirmPaperMove() {
      if (this.paperMoveSubmitting || this.paperMoveConfirmDisabled) {
        return
      }
      const parentId = this.getPaperMoveTargetParentId()
      const list = this.paperMoveItems.filter(item => this.isPaperMoveableItem(item))
      if (list.length == 0) {
        this.showPaperMoveFailedMessage()
        return
      }
      if (this.isPaperMoveTargetSameAsOriginalParent(parentId)) {
        this.showPaperMoveSameDirectoryUnsupportedMessage()
        return
      }
      this.paperMoveSubmitting = true
      const succeededItems = []
      const failedItems = []
      try {
        for (let i = 0; i < list.length; i += 1) {
          const item = list[i]
          try {
            await this.movePaperResourceItem(item, parentId)
            succeededItems.push(item)
          } catch (error) {
            failedItems.push({ item, error })
            this.showPaperMoveItemFailedMessage(item)
          }
        }
      } finally {
        this.paperMoveSubmitting = false
      }
      if (succeededItems.length == 0) {
        this.showPaperMoveFailedMessage()
        return
      }
      if (failedItems.length == 0) {
        this.showPaperMoveSuccessMessage()
      }
      this.closePaperMoveModal({ force: true })
      this.clearSelection()
      this.loadPaperList({ pageIndex: 1 })
    },
    async movePaperResourceItem(item, parentId) {
      const uuid = this.getPaperResourceUuid(item)
      if (!uuid) {
        throw new Error('\u7f3a\u5c11 uuid')
      }
      const ret = await moveWebSyncResource({
        uuid,
        appType: KNOWLEDGE_BASE_APP_TYPE,
        parentId
      }, { skipGlobalErrorMessage: true })
      if (!this.isPaperMoveSuccessResponse(ret)) {
        throw ret
      }
      return ret
    },
    isPaperMoveSuccessResponse(source) {
      return !this.isPaperApiCodeFailed(source)
    },
    showPaperMoveSuccessMessage() {
      if (this.$message && typeof this.$message.success == 'function') {
        this.$message.success(this.ui.moveSuccess || '\u79fb\u52a8\u6210\u529f')
      }
    },
    showPaperMoveFailedMessage() {
      if (this.$message && typeof this.$message.error == 'function') {
        this.$message.error(this.ui.moveFailed || '\u79fb\u52a8\u5931\u8d25')
      }
    },
    showPaperMoveSameDirectoryUnsupportedMessage() {
      if (this.$message && typeof this.$message.warning == 'function') {
        this.$message.warning(this.ui.moveSameDirectoryUnsupported || '\u76f8\u540c\u76ee\u5f55\u4e0d\u652f\u6301\u79fb\u52a8')
      }
    },
    showPaperMoveItemFailedMessage(item) {
      if (this.$message && typeof this.$message.error == 'function') {
        this.$message.error(this.getPaperMoveItemFailedMessage(item))
      }
    },
    getPaperMoveItemFailedMessage(item) {
      const name = item ? this.getKnowledgeBaseDisplayName(item) : ''
      if (!name) {
        return this.ui.moveFailed || '\u79fb\u52a8\u5931\u8d25'
      }
      const template = this.ui.moveItemFailed || '{name}\u79fb\u52a8\u5931\u8d25'
      return String(template).replace('{name}', name)
    },
    openPaperDeleteModal(items) {
      const list = Array.isArray(items) ? items.filter(item => this.isPaperDeletableItem(item)) : []
      if (list.length == 0) {
        this.showPaperDeleteFailedMessage(null)
        return
      }
      this.paperDeleteItems = list.slice()
      this.paperDeleteModalOpen = true
    },
    closePaperDeleteModal() {
      if (this.paperDeleteSubmitting) {
        return
      }
      this.paperDeleteModalOpen = false
      this.paperDeleteItems = []
    },
    async confirmPaperDelete() {
      if (this.paperDeleteSubmitting) {
        return
      }
      const list = Array.isArray(this.paperDeleteItems) ? this.paperDeleteItems.filter(Boolean) : []
      if (list.length == 0) {
        this.closePaperDeleteModal()
        return
      }
      this.paperDeleteSubmitting = true
      this.paperDeleteModalOpen = false
      try {
        await this.deletePaperItems(list)
      } finally {
        this.paperDeleteSubmitting = false
        this.paperDeleteItems = []
      }
    },
    async deletePaperItems(items) {
      const list = Array.isArray(items) ? items.filter(Boolean) : []
      let hasFailed = false
      for (let i = 0; i < list.length; i += 1) {
        const item = list[i]
        try {
          await this.deletePaperItem(item)
        } catch (error) {
          hasFailed = true
          this.showPaperDeleteFailedMessage(item, error)
        }
      }
      const isBatchDelete = list.length > 1
      if (!hasFailed || isBatchDelete) {
        this.clearSelection()
      }
      if (!hasFailed) {
        this.showPaperDeleteSuccessMessage()
      }
      if (!hasFailed || isBatchDelete) {
        this.loadPaperList()
      }
    },
    async deletePaperItem(item) {
      const uuid = item && item.resourceId !== undefined && item.resourceId !== null
        ? String(item.resourceId).trim()
        : ''
      if (!uuid) {
        throw new Error('\u5220\u9664\u5931\u8d25')
      }
      const params = {
        uuid,
        appType: KNOWLEDGE_BASE_APP_TYPE
      }
      const request = this.isPaperDeleteFolderItem(item) ? deleteWebSyncFolder : deleteWebSyncNote
      const ret = await request(params, { skipGlobalErrorMessage: true })
      if (!this.isPaperDeleteSuccessResponse(ret)) {
        throw ret
      }
      return ret
    },
    isPaperDeleteFolderItem(item) {
      const resourceType = Number(item && item.resourceType)
      if (Number.isFinite(resourceType)) {
        return resourceType === 0 || resourceType === 5
      }
      return !!(item && item.isFolder)
    },
    isPaperDeleteSuccessResponse(source) {
      const code = this.getPaperApiCode(source)
      return code == 200 || code == '200'
    },
    showPaperDeleteSuccessMessage() {
      if (this.$message && typeof this.$message.success == 'function') {
        this.$message.success('\u5220\u9664\u6210\u529f')
      }
    },
    showPaperDeleteFailedMessage(item, error) {
      if (this.$message && typeof this.$message.error == 'function') {
        this.$message.error(this.getPaperDeleteFailureMessage(item, error))
      }
    },
    getPaperDeleteFailureMessage(item, error) {
      const name = item ? this.getKnowledgeBaseDisplayName(item) : ''
      const fallback = name ? `${name}\u5220\u9664\u5931\u8d25` : '\u5220\u9664\u5931\u8d25'
      const detail = this.getPaperApiErrorMessage(error)
      return detail ? `${fallback}\uff1a${detail}` : fallback
    },
    openPaperDownloadFormatModal(items, options = {}) {
      const list = Array.isArray(items) ? items.filter(Boolean) : []
      if (list.length == 0) {
        this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684 Knowledge Base \u6587\u4ef6')
        return
      }
      const hasUnsupportedItem = list.some(item => !this.isPaperDownloadableItem(item))
      if (hasUnsupportedItem) {
        this.$message.warning('\u6587\u4ef6\u5939\u548c\u6a21\u677f\u4e0d\u652f\u6301\u4e0b\u8f7d\uff0c\u8bf7\u9009\u62e9 Knowledge Base \u6587\u4ef6')
        return
      }
      if (!this.$canStartFileDownloadBatch(list.length)) {
        return
      }
      this.paperDownloadPassword = options.password || ''
      this.paperDownloadItems = list
      this.paperDownloadDetailPages = []
      this.paperDownloadFormatContext = 'items'
      this.paperDownloadFormat = 'pdf'
      this.paperDownloadFormatModalOpen = true
    },
    shouldClearSelectionAfterPaperDownloadStart(items) {
      const list = Array.isArray(items) ? items.filter(Boolean) : []
      if (list.length == 0 || this.selectedPaperIds.length != list.length) {
        return false
      }
      const selectedIds = new Set(this.selectedPaperIds)
      return list.every(item => item && selectedIds.has(item.id))
    },
    clearSelectionAfterPaperDownloadStart(items) {
      if (this.shouldClearSelectionAfterPaperDownloadStart(items)) {
        this.clearSelection()
      }
    },
    enqueuePaperDownloadItems(items, format, password = '') {
      const list = Array.isArray(items) ? items.filter(Boolean) : []
      if (this.$shouldBundleDownloadItems(list)) {
        const task = this.$createDownloadBundleTask(list, format, password, {
          idPrefix: 'knowledge-bundle-download',
          kind: 'paper-bundle',
          fallbackName: 'Knowledge Base',
          sanitizeFileName: this.sanitizeFileName
        })
        this.$enqueuePaperDownloadTask(task, () => this.runPaperDownloadBundleTask(task))
        return
      }
      list.forEach(item => {
        const task = this.createPaperDownloadTask(item, format, password)
        this.$enqueuePaperDownloadTask(task, () => this.runPaperDownloadTask(task, item, format))
      })
    },
    runPaperDownloadBundleTask(task) {
      this.$runDownloadBundleTask(task, {
        items: task.sourceItems,
        format: task.format,
        password: task.password || '',
        kind: 'paper-bundle',
        fallbackName: 'Knowledge Base',
        sanitizeFileName: this.sanitizeFileName,
        createItemTask: (item, format, password) => this.createPaperDownloadTask(item, format, password),
        runItemTask: (bundleTask, item, format) => this.runPaperDownloadTask(bundleTask, item, format),
        getItemFailureStatusText: error => this.isKnowledgeBaseNoPagesDownloadError(error) ? 'No Pages' : '',
        shouldNotifyItemFailure: error => !this.isKnowledgeBaseNoPagesDownloadError(error)
      })
    },
    openDetailPagesDownloadFormatModal(pages) {
      const list = Array.isArray(pages)
        ? pages.filter(page => this.isKnowledgeBaseDetailPageDownloadable(page))
        : []
      if (list.length == 0) {
        this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u9875\u9762')
        return
      }
      if (!this.$canStartDownloadBatch(list.length, 1, { skipSelectionLimit: true })) {
        return
      }
      this.paperDownloadItems = []
      this.paperDownloadDetailPages = list
      this.paperDownloadFormatContext = 'detail-pages'
      this.paperDownloadFormat = 'png'
      this.paperDownloadFormatModalOpen = true
    },
    closePaperDownloadFormatModal() {
      this.paperDownloadFormatModalOpen = false
      this.paperDownloadItems = []
      this.paperDownloadPassword = ''
      this.paperDownloadDetailPages = []
      this.paperDownloadFormatContext = 'items'
    },
    confirmPaperDownloadFormat() {
      const format = this.paperDownloadFormat == 'png' ? 'png' : 'pdf'
      if (this.paperDownloadFormatContext == 'detail-pages') {
        const pages = this.paperDownloadDetailPages.slice()
        if (!this.$canStartDownloadBatch(pages.length, 1, { skipSelectionLimit: true })) {
          return
        }
        this.closePaperDownloadFormatModal()
        this.startDetailPagesDownload(pages, format)
        return
      }
      const items = this.paperDownloadItems.slice()
      if (!this.$canStartFileDownloadBatch(items.length)) {
        return
      }
      const password = this.paperDownloadPassword || ''
      this.closePaperDownloadFormatModal()
      this.enqueuePaperDownloadItems(items, format, password)
      this.clearSelectionAfterPaperDownloadStart(items)
    },
    createPaperDownloadTask(item, format, password = '') {
      const baseName = this.$getDownloadBaseName(item && item.name, 'Knowledge Base', this.sanitizeFileName)
      const extension = this.$getPaperDownloadTaskExtension(item, format)
      return {
        id: `paper-download-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        name: baseName,
        fileName: `${baseName}.${extension}`,
        format,
        sourceItem: item,
        password: this.isPaperEncryptedItem(item) ? (password || '') : '',
        openUrl: '',
        pages: [],
        pngFiles: [],
        pdfPages: [],
        pdfPageGroups: [],
        failedPageIndex: 0,
        abortController: null,
        cancelled: false,
        progress: 0,
        state: 'running',
        statusText: this.ui.downloadPreparing
      }
    },
    updatePaperDownloadTask(task, patch) {
      patch = typeof this.$normalizePaperDownloadTaskPatch == 'function' ? this.$normalizePaperDownloadTaskPatch(task, patch) : patch
      Object.keys(patch || {}).forEach(key => {
        this.$set(task, key, patch[key])
      })
    },
    async runPaperDownloadTask(task, item, format) {
      try {
        this.ensurePaperDownloadTaskActive(task)
        this.updatePaperDownloadTask(task, { state: 'running', progress: Math.max(task.progress || 0, 5), statusText: '\u83b7\u53d6\u8be6\u60c5' })
        if (this.isEmptyKnowledgeBaseVisualNote(item)) {
          throw new Error('No Pages')
        }
        const detail = task.detail || await this.fetchPaperDownloadDetail(item, task.password || '')
        const pages = task.pages && task.pages.length > 0 ? task.pages : this.getPaperDetailPagesForDownload(detail)
        if (this.isPaperPdfResourceDetail(detail)) {
          const compositePages = task.pages && task.pages.length > 0 ? task.pages : this.getPaperDetailPagesForComposite(detail)
          this.updatePaperDownloadTask(task, { detail, pages: compositePages, progress: Math.max(task.progress || 0, 10) })
          await this.downloadPaperPdfResource(task, detail, format)
        } else {
          if (pages.length == 0) {
            throw new Error('No Pages')
          }
          this.updatePaperDownloadTask(task, { detail, pages, progress: Math.max(task.progress || 0, 10) })
          if (format == 'png') {
            await this.downloadPaperAsPngZip(task, pages)
          } else {
            await this.downloadPaperAsPdf(task, pages)
          }
        }
        this.ensurePaperDownloadTaskActive(task)
        this.updatePaperDownloadTask(task, {
          progress: 100,
          state: 'done',
          statusText: this.ui.downloadDone
        })
      } catch (error) {
        if (error && error.isPaperDownloadCancelled) {
          return
        }
        const isCorsLikeError = this.isLikelyCorsDownloadError(error)
        this.updatePaperDownloadTask(task, {
          state: 'error',
          progress: task.progress || 0,
          statusText: isCorsLikeError ? this.ui.imageReadFailed : (error && error.message ? error.message : this.ui.downloadFailed)
        })
      }
    },
    retryPaperDownloadTask(task) {
      if (!task || task.state != 'error') {
        return
      }
      this.paperDownloadListVisible = true
      this.$set(task, 'cancelled', false)
      this.$set(task, 'abortController', null)
      this.updatePaperDownloadTask(task, {
        progress: task.progress || 0,
        state: 'running',
        statusText: this.ui.continueDownload
      })
      if (task.kind == 'paper-bundle') {
        this.runPaperDownloadBundleTask(task)
        return
      }
      if (task.kind == 'detail-pages') {
        this.runDetailPagesDownloadTask(task)
        return
      }
      this.runPaperDownloadTask(task, task.sourceItem, task.format)
    },
    getPaperDownloadNativeOpener() {
      if (typeof window === 'undefined') {
        return null
      }
      return window.viwoodsNative || window.electronAPI || window.nativeBridge || null
    },
    canOpenPaperDownloadTask(task) {
      if (!task || task.state != 'done') {
        return false
      }
      const nativeOpener = this.getPaperDownloadNativeOpener()
      if (task.filePath && nativeOpener && typeof nativeOpener.showItemInFolder == 'function') {
        return true
      }
      return !!(nativeOpener && typeof nativeOpener.openDownloadFolder == 'function')
    },
    openPaperDownloadTask(task) {
      if (!this.canOpenPaperDownloadTask(task)) {
        return
      }
      const nativeOpener = this.getPaperDownloadNativeOpener()
      if (task.filePath && nativeOpener && typeof nativeOpener.showItemInFolder == 'function') {
        nativeOpener.showItemInFolder(task.filePath)
        return
      }
      if (nativeOpener && typeof nativeOpener.openDownloadFolder == 'function') {
        nativeOpener.openDownloadFolder()
        return
      }
    },
    removePaperDownloadTask(task) {
      if (!task) {
        return
      }
      this.$set(task, 'cancelled', true)
      if (task.abortController && typeof task.abortController.abort == 'function') {
        task.abortController.abort()
      }
      this.revokePaperDownloadTaskUrl(task)
      this.paperDownloadTasks = this.paperDownloadTasks.filter(item => item.id != task.id)
      if (this.paperDownloadTasks.length == 0) {
        this.paperDownloadListVisible = false
      }
    },
    closePaperDownloadList() {
      this.clearPaperDownloadTasks()
    },
    clearPaperDownloadTasks() {
      this.paperDownloadTasks.forEach(task => {
        this.$set(task, 'cancelled', true)
        if (task.abortController && typeof task.abortController.abort == 'function') {
          task.abortController.abort()
        }
        this.revokePaperDownloadTaskUrl(task)
      })
      this.paperDownloadTasks = []
      this.paperDownloadListVisible = false
    },
    async fetchPaperDownloadDetail(item, password = '') {
      const ret = await this.fetchPaperDetailResponse(item, this.isPaperEncryptedItem(item) ? password : '')
      const payload = this.resolveApiPayload(ret)
      if (payload === null) {
        throw new Error('Knowledge Base detail failed')
      }
      return this.normalizePaperDetail(payload, item)
    },
    revokePaperPreviewObjectUrls(urls) {
      const list = Array.isArray(urls) ? urls : []
      if (typeof this.revokePaperPreviewObjectUrlsSafely == 'function') {
        this.revokePaperPreviewObjectUrlsSafely(list)
        return
      }
      list.forEach(url => {
        if (url && String(url).startsWith('blob:')) {
          URL.revokeObjectURL(url)
        }
      })
    },
    clearPaperPreviewObjectUrls(options = {}) {
      if (options.revoke !== false) {
        this.revokePaperPreviewObjectUrls(this.paperPreviewObjectUrls)
      }
      this.paperPreviewObjectUrls = []
    },
    clearPaperDetailCache() {
      const cache = this.paperDetailCache || {}
      Object.keys(cache).forEach(key => {
        this.revokePaperPreviewObjectUrls(cache[key] && cache[key].objectUrls)
      })
      this.paperDetailCache = {}
    },
    async preparePaperPreviewDetail(detail, onProgress) {
      const objectUrls = []
      const reportProgress = progress => {
        if (typeof onProgress == 'function') {
          onProgress(progress)
        }
      }
      const revokePreparedUrls = () => {
        this.revokePaperPreviewObjectUrls(objectUrls)
      }
      reportProgress(30)
      if (this.canUseExternalPaperPdfPreview(detail)) {
        reportProgress(100)
        return {
          detail: this.createExternalPaperPdfPreviewDetail(detail),
          objectUrls
        }
      }
      if (!this.isPaperPdfResourceDetail(detail)) {
        reportProgress(100)
        return { detail, objectUrls }
      }
      let pdfDocument = null
      try {
        const pdfBlob = await this.fetchPaperBinaryBlob(detail.resourceUrl, percent => {
          reportProgress(30 + (Math.max(0, Math.min(Number(percent) || 0, 100)) * 0.2))
        })
        reportProgress(52)
        pdfDocument = await this.loadPaperPdfDocumentFromBlob(pdfBlob)
        reportProgress(56)
        const pages = this.getPaperPdfCompositePages(detail, pdfDocument.numPages)
        const compositeFrameSize = await this.getUnifiedPaperPdfCompositeFrameSize(pdfDocument, pages, null, percent => {
          reportProgress(56 + Math.max(0, Math.min(Number(percent) || 0, 100)) * 0.04)
        })
        const imagePages = await this.buildRenderedPaperPreviewPages({
          pages,
          objectUrls,
          onProgress: reportProgress,
          startProgress: 56,
          endProgress: 96,
          renderPage: async (page, index, total, progress) => {
            const canvas = await this.renderPaperPdfCompositeCanvas(pdfDocument, page, index, total, null, null, null, progress, compositeFrameSize)
            try {
              return await this.canvasToPngBlob(canvas)
            } finally {
              this.releaseCanvas(canvas)
            }
          }
        })
        reportProgress(100)
        return { detail: Object.assign({}, detail, { imagePages }), objectUrls }
      } catch (error) {
        revokePreparedUrls()
        throw error
      } finally {
        if (pdfDocument && typeof pdfDocument.destroy == 'function') {
          await pdfDocument.destroy()
        }
      }
    },
    canUseExternalPaperPdfPreview(detail) {
      return !!(!this.isDirectFilePreviewDetail(detail) && this.isPaperPdfResourceDetail(detail) && this.getExternalPaperPdfPreviewUrl(detail.resourceUrl))
    },
    getExternalPaperPdfPreviewUrl(url) {
      return String(url || '').trim()
    },
    createExternalPaperPdfPreviewDetail(detail) {
      const previewUrl = this.getExternalPaperPdfPreviewUrl(detail && detail.resourceUrl)
      return Object.assign({}, detail, {
        pdfPreviewUrl: previewUrl,
        imagePages: [{
          pageNo: 1,
          imageUrl: '',
          pdfPreviewUrl: previewUrl,
          isPdfPreview: true
        }]
      })
    },
    getPaperDetailPagesForDownload(detail) {
      const pages = detail && Array.isArray(detail.imagePages) ? detail.imagePages : []
      return pages
        .filter(page => this.isKnowledgeBaseDownloadablePage(page))
        .slice()
        .sort((a, b) => Number(a.pageNo) - Number(b.pageNo))
    },
    getPaperDetailPagesForComposite(detail) {
      const pages = detail && Array.isArray(detail.imagePages) ? detail.imagePages : []
      return pages
        .filter(page => page && page.pageNo)
        .slice()
        .sort((a, b) => Number(a.pageNo) - Number(b.pageNo))
    },
    isPaperPdfResourceDetail(detail) {
      if (!detail || !detail.resourceUrl) {
        return false
      }
      if (this.isDirectFilePreviewDetail(detail)) {
        return this.isPaperPdfUrl(detail.resourceUrl)
      }
      if (Number(detail.resourceType) !== 5) {
        return false
      }
      return this.isPaperPdfUrl(detail.resourceUrl)
    },
    isPaperPdfUrl(url) {
      const text = String(url || '').toLowerCase()
      if (!text) {
        return false
      }
      return this.getUrlExtension(text, '') == 'pdf' || text.includes('.pdf')
    },
    getPaperPdfCompositePages(detail, pageCount) {
      const metaPages = this.getPaperDetailPagesForComposite(detail)
      const pagesByNo = {}
      let maxMetaPageNo = 0
      metaPages.forEach(page => {
        const pageNo = Number(page.pageNo)
        if (pageNo > 0) {
          pagesByNo[pageNo] = page
          maxMetaPageNo = Math.max(maxMetaPageNo, pageNo)
        }
      })
      const totalPageCount = Math.max(Number(pageCount) || 0, metaPages.length, maxMetaPageNo)
      const pages = []
      for (let i = 1; i <= totalPageCount; i++) {
        pages.push(Object.assign({ pageNo: i, imageUrl: '' }, pagesByNo[i] || {}))
      }
      return pages
    },
    ensurePaperDownloadTaskActive(task) {
      const isInList = !!(task && this.paperDownloadTasks.some(item => item.id == task.id))
      if (!task || task.cancelled || !isInList) {
        const error = new Error('Download cancelled')
        error.isPaperDownloadCancelled = true
        throw error
      }
    },
    getPaperDownloadImageProgress(index, total, start, end, percent) {
      const safeTotal = Math.max(total || 1, 1)
      const safePercent = Math.max(0, Math.min(Number(percent) || 0, 100))
      return Math.min(end, Math.round(start + ((index + safePercent / 100) / safeTotal) * (end - start)))
    },
    async downloadPaperPdfResource(task, detail, format) {
      if (format == 'png') {
        await this.downloadPaperPdfResourceAsPngZip(task, detail)
        return
      }
      await this.downloadPaperPdfResourceAsPdf(task, detail)
    },
    async getPaperPdfDocument(task, detail) {
      if (task.pdfDocument) {
        return task.pdfDocument
      }
      this.updatePaperDownloadTask(task, {
        progress: Math.max(task.progress || 0, 10),
        statusText: this.ui.downloading
      })
      const pdfBlob = await this.fetchPaperBinaryBlob(detail.resourceUrl, percent => {
        this.updatePaperDownloadTask(task, {
          progress: this.getPaperDownloadImageProgress(0, 1, 10, 25, percent),
          statusText: this.ui.downloading
        })
      }, task)
      this.ensurePaperDownloadTaskActive(task)
      this.updatePaperDownloadTask(task, { progress: 28, statusText: '\u89e3\u6790 PDF' })
      const pdfDocument = await this.loadPaperPdfDocumentFromBlob(pdfBlob)
      task.pdfDocument = pdfDocument
      task.pdfPageCount = pdfDocument.numPages
      return pdfDocument
    },
    async loadPaperPdfDocumentFromBlob(pdfBlob) {
      const bytes = new Uint8Array(await this.blobToArrayBuffer(pdfBlob))
      const loadingTask = pdfjsLib.getDocument({
        data: bytes
      })
      return loadingTask.promise
    },
    async downloadPaperPdfResourceAsPdf(task, detail) {
      const renderedPreviewPages = this.getPaperDetailPagesForDownload(detail)
      if (this.areRenderedPaperPreviewPages(renderedPreviewPages)) {
        await this.downloadPaperAsPdf(task, renderedPreviewPages)
        return
      }
      const pdfDocument = await this.getPaperPdfDocument(task, detail)
      const pages = this.getPaperPdfCompositePages(detail, pdfDocument.numPages)
      this.$updatePaperDownloadTaskFileNameForPages(task, 'pdf', pages)
      const compositeFrameSize = await this.getUnifiedPaperPdfCompositeFrameSize(pdfDocument, pages, task)
      const imagePages = Array.isArray(task.pdfPages) ? task.pdfPages.slice() : []
      this.updatePaperDownloadTask(task, { pages, pdfPages: imagePages })
      for (let i = 0; i < pages.length; i++) {
        this.ensurePaperDownloadTaskActive(task)
        if (imagePages[i]) {
          continue
        }
        const canvas = await this.renderPaperPdfCompositeCanvas(pdfDocument, pages[i], i, pages.length, task, 30, 85, null, compositeFrameSize)
        imagePages[i] = await this.canvasToJpegPage(canvas)
        this.releaseCanvas(canvas)
        this.updatePaperDownloadTask(task, {
          pdfPages: imagePages.slice(),
          failedPageIndex: i + 1
        })
      }
      this.ensurePaperDownloadTaskActive(task)
      this.updatePaperDownloadTask(task, { progress: 90, statusText: '\u751f\u6210 PDF' })
      const pdfBlob = this.buildPdfBlob(imagePages)
      this.ensurePaperDownloadTaskActive(task)
      this.savePaperDownloadBlob(task, pdfBlob)
    },
    async downloadPaperPdfResourceAsPngZip(task, detail) {
      const renderedPreviewPages = this.getPaperDetailPagesForDownload(detail)
      if (this.areRenderedPaperPreviewPages(renderedPreviewPages)) {
        await this.downloadPaperAsPngZip(task, renderedPreviewPages)
        return
      }
      const pdfDocument = await this.getPaperPdfDocument(task, detail)
      const pages = this.getPaperPdfCompositePages(detail, pdfDocument.numPages)
      const compositeFrameSize = await this.getUnifiedPaperPdfCompositeFrameSize(pdfDocument, pages, task)
      const pngFiles = Array.isArray(task.pngFiles) ? task.pngFiles.slice() : []
      this.updatePaperDownloadTask(task, { pages, pngFiles })
      for (let i = 0; i < pages.length; i++) {
        this.ensurePaperDownloadTaskActive(task)
        if (pngFiles[i]) {
          continue
        }
        const canvas = await this.renderPaperPdfCompositeCanvas(pdfDocument, pages[i], i, pages.length, task, 30, 85, null, compositeFrameSize)
        const blob = await this.canvasToPngBlob(canvas)
        pngFiles[i] = {
          fileName: this.getPaperPageFileName(pages[i], i, 'png'),
          buffer: await this.blobToArrayBuffer(blob)
        }
        this.releaseCanvas(canvas)
        this.updatePaperDownloadTask(task, {
          pngFiles: pngFiles.slice(),
          failedPageIndex: i + 1
        })
      }
      this.ensurePaperDownloadTaskActive(task)
      if (pngFiles.length == 1 && pngFiles[0]) {
        this.updatePaperDownloadTask(task, {
          fileName: `${task.name}.png`,
          progress: 99,
          statusText: this.ui.savingFile
        })
        this.savePaperDownloadBlob(task, new Blob([pngFiles[0].buffer], { type: 'image/png' }))
        return
      }
      const zip = new JSZip()
      pngFiles.forEach((file, index) => {
        if (file) {
          zip.file(file.fileName || this.getPaperPageFileName(pages[index], index, 'png'), file.buffer)
        }
      })
      this.updatePaperDownloadTask(task, { progress: 90, statusText: '\u6253\u5305 ZIP' })
      const zipBlob = await zip.generateAsync({ type: 'blob' }, metadata => {
        this.updatePaperDownloadTask(task, {
          progress: Math.min(98, Math.round(90 + (metadata.percent || 0) * 0.08)),
          statusText: '\u6253\u5305 ZIP'
        })
      })
      this.ensurePaperDownloadTaskActive(task)
      this.savePaperDownloadBlob(task, zipBlob)
    },
    async renderPaperPdfCompositeCanvas(pdfDocument, pageInfo, index, total, task, startProgress, endProgress, onProgress, frameSize) {
      const reportCompositeProgress = progress => {
        if (typeof onProgress == 'function') {
          onProgress(Math.max(0, Math.min(Number(progress) || 0, 100)))
        }
      }
      if (task) {
        this.updatePaperDownloadTask(task, {
          failedPageIndex: index,
          progress: this.getPaperDownloadImageProgress(index, total, startProgress, endProgress, 0),
          statusText: this.ui.downloading
        })
      }
      reportCompositeProgress(5)
      let overlayBlob = null
      let overlaySize = null
      const canRenderPdfInParallel = !!(pageInfo && pageInfo.imageUrl) && this.hasPaperPreviewFrameSize(frameSize)
      let pdfCanvasError = null
      const pdfCanvasPromise = canRenderPdfInParallel
        ? this.renderPaperPdfPageCanvas(
          pdfDocument,
          Number(pageInfo.pageNo) || index + 1,
          frameSize,
          !task
        ).catch(error => {
          pdfCanvasError = error
          return null
        })
        : null
      if (pageInfo && pageInfo.imageUrl) {
        try {
          overlayBlob = await this.fetchPaperImageBlob(pageInfo.imageUrl, percent => {
            if (task) {
              this.updatePaperDownloadTask(task, {
                progress: this.getPaperDownloadImageProgress(index, total, startProgress, endProgress, percent),
                statusText: this.ui.downloading
              })
            }
            reportCompositeProgress(10 + Math.max(0, Math.min(Number(percent) || 0, 100)) * 0.45)
          }, task)
          overlaySize = await this.getPaperImageBlobSize(overlayBlob)
          reportCompositeProgress(55)
        } catch (error) {
          this.releasePaperPreviewCanvasWhenReady(pdfCanvasPromise)
          throw error
        }
      }
      const targetFrameSize = frameSize || overlaySize
      const outputScale = this.getPaperPreviewFrameScale(targetFrameSize, !task)
      const canvas = pdfCanvasPromise
        ? await pdfCanvasPromise
        : await this.renderPaperPdfPageCanvas(
          pdfDocument,
          Number(pageInfo.pageNo) || index + 1,
          targetFrameSize,
          !task
        )
      if (pdfCanvasError) {
        throw pdfCanvasError
      }
      this.fitPaperCompositeCanvasToFrame(canvas, targetFrameSize, outputScale, { allowHeightFallback: true })
      reportCompositeProgress(overlayBlob ? 80 : 90)
      if (overlayBlob) {
        await this.drawPaperOverlayImage(canvas, overlayBlob)
        reportCompositeProgress(95)
      }
      if (task) {
        this.updatePaperDownloadTask(task, {
          progress: this.getPaperDownloadImageProgress(index, total, startProgress, endProgress, 100),
          statusText: this.ui.downloading
        })
      }
      reportCompositeProgress(100)
      return canvas
    },
    async renderPaperPdfPageCanvas(pdfDocument, pageNo, targetSize, includeDisplayScale = true) {
      if (!pdfDocument || !pdfDocument.numPages) {
        return this.createBlankPaperPdfCanvas(null, targetSize)
      }
      if (pageNo > pdfDocument.numPages) {
        return this.createBlankPaperPdfCanvas(pdfDocument, targetSize)
      }
      const page = await pdfDocument.getPage(pageNo)
      const baseViewport = page.getViewport({ scale: 1 })
      const renderScale = this.getPaperPreviewRenderScale(baseViewport, targetSize, { includeDisplayScale })
      const viewport = page.getViewport({ scale: renderScale })
      const canvas = document.createElement('canvas')
      canvas.width = Math.ceil(viewport.width)
      canvas.height = Math.ceil(viewport.height)
      const context = canvas.getContext('2d')
      if (context && 'imageSmoothingEnabled' in context) {
        context.imageSmoothingEnabled = true
        if ('imageSmoothingQuality' in context) {
          context.imageSmoothingQuality = 'high'
        }
      }
      context.fillStyle = '#fff'
      context.fillRect(0, 0, canvas.width, canvas.height)
      await page.render({ canvasContext: context, viewport }).promise
      if (typeof page.cleanup == 'function') {
        page.cleanup()
      }
      return canvas
    },
    async createBlankPaperPdfCanvas(pdfDocument, targetSize) {
      let width = Math.round(Number(targetSize && targetSize.width) || 0)
      let height = Math.round(Number(targetSize && targetSize.height) || 0)
      if (!(width > 0 && height > 0) && pdfDocument && pdfDocument.numPages) {
        try {
          const page = await pdfDocument.getPage(1)
          const baseViewport = page.getViewport({ scale: 1 })
          const renderScale = this.getPaperPreviewRenderScale(baseViewport, targetSize)
          const viewport = page.getViewport({ scale: renderScale })
          width = Math.ceil(viewport.width)
          height = Math.ceil(viewport.height)
          if (typeof page.cleanup == 'function') {
            page.cleanup()
          }
        } catch (error) {
          // Keep the default A4-like page size.
        }
      }
      if (!(width > 0 && height > 0)) {
        width = 1240
        height = 1754
      }
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const context = canvas.getContext('2d')
      context.fillStyle = '#fff'
      context.fillRect(0, 0, width, height)
      return canvas
    },
    async getPaperImageBlobSize(blob) {
      const bitmapSize = await this.getPreviewPerformanceImageBlobSize(blob)
      if (bitmapSize) {
        return bitmapSize
      }
      return new Promise(resolve => {
        const url = URL.createObjectURL(blob)
        const image = new Image()
        image.onload = () => {
          const width = image.naturalWidth || image.width || 0
          const height = image.naturalHeight || image.height || 0
          URL.revokeObjectURL(url)
          resolve(width > 0 && height > 0 ? { width, height } : null)
        }
        image.onerror = () => {
          URL.revokeObjectURL(url)
          resolve(null)
        }
        image.src = url
      })
    },
    drawPaperOverlayImage(canvas, blob) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob)
        const image = new Image()
        image.onload = () => {
          const context = canvas.getContext('2d')
          if (context && 'imageSmoothingEnabled' in context) {
            context.imageSmoothingEnabled = true
            if ('imageSmoothingQuality' in context) {
              context.imageSmoothingQuality = 'high'
            }
          }
          context.drawImage(image, 0, 0, canvas.width, canvas.height)
          URL.revokeObjectURL(url)
          resolve()
        }
        image.onerror = () => {
          URL.revokeObjectURL(url)
          reject(new Error('Overlay image load failed'))
        }
        image.src = url
      })
    },
    canvasToJpegPage(canvas) {
      const isBlank = this.isKnowledgeBasePdfPageCanvasBlank(canvas)
      return this.canvasToBlobWithRetry(canvas, 'image/jpeg', 1).then(async converted => ({
        width: converted.width,
        height: converted.height,
        data: new Uint8Array(await this.blobToArrayBuffer(converted.blob)),
        isBlank
      }))
    },
    isKnowledgeBasePdfPageCanvasBlank(canvas) {
      const context = canvas && typeof canvas.getContext == 'function' ? canvas.getContext('2d') : null
      if (!context) {
        return false
      }
      const width = Math.max(1, Math.round(canvas.width || 1))
      const height = Math.max(1, Math.round(canvas.height || 1))
      const sampleStep = Math.max(4, Math.min(12, Math.floor(width / 180)))
      let marked = 0
      try {
        for (let y = 0; y < height; y += sampleStep) {
          const rowHeight = Math.min(sampleStep, height - y)
          const data = context.getImageData(0, y, width, rowHeight).data
          for (let row = 0; row < rowHeight; row += sampleStep) {
            for (let x = 0; x < width; x += sampleStep) {
              const offset = (row * width + x) * 4
              const alpha = data[offset + 3]
              const red = data[offset]
              const green = data[offset + 1]
              const blue = data[offset + 2]
              if (alpha > 16 && (red < 250 || green < 250 || blue < 250)) {
                marked += 1
                if (marked >= 4) {
                  return false
                }
              }
            }
          }
        }
      } catch (error) {
        return false
      }
      return true
    },
    trimTrailingBlankPdfPages(imagePages) {
      const pages = Array.isArray(imagePages) ? imagePages.filter(Boolean) : []
      while (pages.length > 1 && pages[pages.length - 1] && pages[pages.length - 1].isBlank) {
        pages.pop()
      }
      return pages
    },
    getKnowledgeBasePdfPageGroupsFromTask(task) {
      const groups = Array.isArray(task && task.pdfPageGroups) ? task.pdfPageGroups : []
      if (groups.length > 0) {
        return groups.map(group => {
          if (Array.isArray(group)) {
            return group.filter(Boolean)
          }
          return group ? [group] : null
        })
      }
      const pages = Array.isArray(task && task.pdfPages) ? task.pdfPages : []
      return pages.map(page => page ? [page] : null)
    },
    flattenKnowledgeBasePdfPageGroups(groups) {
      if (!Array.isArray(groups)) {
        return []
      }
      return groups.reduce((list, group) => {
        if (Array.isArray(group)) {
          list.push(...group.filter(Boolean))
        } else if (group) {
          list.push(group)
        }
        return list
      }, [])
    },
    async canvasToKnowledgeBasePdfPages(canvas, options = {}) {
      if (!canvas || !(canvas.width > 0 && canvas.height > 0)) {
        return []
      }
      const standardHeight = Math.max(1, Math.round(canvas.width * 4 / 3))
      if (!options.splitLongPage || canvas.height <= standardHeight) {
        return [await this.canvasToJpegPage(canvas)]
      }
      const pages = []
      let sourceY = 0
      while (sourceY < canvas.height) {
        const margin = this.getKnowledgeBasePdfSliceMargin(canvas)
        const topInset = pages.length == 0 ? 0 : margin
        const bottomInset = margin
        const sourceCapacity = Math.max(1, standardHeight - topInset - bottomInset)
        const remainingHeight = canvas.height - sourceY
        const sourceHeight = remainingHeight <= sourceCapacity
          ? remainingHeight
          : this.getKnowledgeBasePdfSliceHeight(canvas, sourceY, sourceCapacity)
        const sliceCanvas = this.createKnowledgeBasePdfSliceCanvas(canvas, sourceY, sourceHeight, standardHeight, topInset)
        pages.push(await this.canvasToJpegPage(sliceCanvas))
        this.releaseCanvas(sliceCanvas)
        sourceY += Math.max(1, sourceHeight)
      }
      return pages.length > 0 ? this.trimTrailingBlankPdfPages(pages) : [await this.canvasToJpegPage(canvas)]
    },
    getKnowledgeBasePdfSliceMargin(canvas) {
      const width = Math.max(1, Math.round(canvas && canvas.width || 1))
      return Math.max(48, Math.min(120, Math.round(width * 0.075)))
    },
    getKnowledgeBasePdfSliceHeight(canvas, sourceY, sourceCapacity) {
      const proposedY = Math.min(canvas.height, sourceY + sourceCapacity)
      if (proposedY >= canvas.height) {
        return canvas.height - sourceY
      }
      const searchRadius = Math.max(24, Math.min(160, Math.round(sourceCapacity * 0.1)))
      const minY = Math.max(sourceY + Math.round(sourceCapacity * 0.55), proposedY - searchRadius)
      const boundaryY = this.findKnowledgeBasePdfSliceBoundary(canvas, proposedY, minY)
      return Math.max(1, Math.min(sourceCapacity, boundaryY - sourceY))
    },
    findKnowledgeBasePdfSliceBoundary(canvas, proposedY, minY) {
      const context = canvas && typeof canvas.getContext == 'function' ? canvas.getContext('2d') : null
      if (!context) {
        return proposedY
      }
      const width = Math.max(1, Math.round(canvas.width || 1))
      const bandHeight = Math.max(4, Math.min(12, Math.round(width / 180)))
      const startY = Math.max(0, Math.round(minY || 0))
      const endY = Math.max(startY, Math.min(Math.round(proposedY), Math.round((canvas.height || proposedY) - bandHeight)))
      try {
        for (let y = endY; y >= startY; y -= 2) {
          if (this.isKnowledgeBaseCanvasBlankBand(context, width, y, bandHeight)) {
            return y
          }
        }
      } catch (error) {
        return proposedY
      }
      return proposedY
    },
    isKnowledgeBaseCanvasBlankBand(context, width, y, bandHeight) {
      const data = context.getImageData(0, y, width, bandHeight).data
      const sampleStep = Math.max(4, Math.floor(width / 180))
      let sampled = 0
      let marked = 0
      for (let row = 0; row < bandHeight; row++) {
        for (let x = 0; x < width; x += sampleStep) {
          const offset = (row * width + x) * 4
          const alpha = data[offset + 3]
          const red = data[offset]
          const green = data[offset + 1]
          const blue = data[offset + 2]
          sampled += 1
          if (alpha > 16 && (red < 245 || green < 245 || blue < 245)) {
            marked += 1
            if (marked / sampled > 0.015) {
              return false
            }
          }
        }
      }
      return true
    },
    createKnowledgeBasePdfSliceCanvas(sourceCanvas, sourceY, sourceHeight, targetHeight, drawY = 0) {
      const width = Math.max(1, Math.round(sourceCanvas && sourceCanvas.width || 1))
      const safeSourceY = Math.max(0, Math.round(sourceY || 0))
      const safeSourceHeight = Math.max(1, Math.min(
        Math.round(sourceHeight || 1),
        Math.max(1, Math.round((sourceCanvas && sourceCanvas.height || 1) - safeSourceY))
      ))
      const safeDrawY = Math.max(0, Math.round(drawY || 0))
      const safeTargetHeight = Math.max(safeDrawY + safeSourceHeight, Math.round(targetHeight || safeDrawY + safeSourceHeight))
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = safeTargetHeight
      const context = canvas.getContext('2d')
      if (context && 'imageSmoothingEnabled' in context) {
        context.imageSmoothingEnabled = true
        if ('imageSmoothingQuality' in context) {
          context.imageSmoothingQuality = 'high'
        }
      }
      if (context) {
        context.fillStyle = '#fff'
        context.fillRect(0, 0, width, safeTargetHeight)
        context.drawImage(sourceCanvas, 0, safeSourceY, width, safeSourceHeight, 0, safeDrawY, width, safeSourceHeight)
      }
      return canvas
    },
    canvasToPngBlob(canvas) {
      return this.canvasToBlobWithRetry(canvas, 'image/png').then(converted => converted.blob)
    },
    canvasToBlobWithRetry(canvas, type, quality) {
      const sourceCanvas = canvas
      const tryConvert = (workingCanvas, attempt) => new Promise((resolve, reject) => {
        if (!workingCanvas || typeof workingCanvas.toBlob != 'function') {
          reject(new Error('Canvas convert failed'))
          return
        }
        workingCanvas.toBlob(blob => {
          if (blob) {
            const result = {
              blob,
              width: workingCanvas.width,
              height: workingCanvas.height
            }
            if (workingCanvas !== sourceCanvas) {
              this.releaseCanvas(workingCanvas)
            }
            resolve(result)
            return
          }
          const scaledCanvas = this.createScaledCanvasForRetry(workingCanvas, 0.8)
          if (!scaledCanvas || attempt >= 4) {
            if (workingCanvas !== sourceCanvas) {
              this.releaseCanvas(workingCanvas)
            }
            reject(new Error('Canvas convert failed'))
            return
          }
          if (workingCanvas !== sourceCanvas) {
            this.releaseCanvas(workingCanvas)
          }
          tryConvert(scaledCanvas, attempt + 1).then(resolve).catch(reject)
        }, type, quality)
      })
      return tryConvert(sourceCanvas, 0)
    },
    createScaledCanvasForRetry(canvas, ratio = 0.8) {
      if (!canvas || !canvas.width || !canvas.height) {
        return null
      }
      const nextWidth = Math.max(320, Math.floor(canvas.width * ratio))
      const nextHeight = Math.max(320, Math.floor(canvas.height * ratio))
      if (nextWidth >= canvas.width || nextHeight >= canvas.height) {
        return null
      }
      const scaledCanvas = document.createElement('canvas')
      scaledCanvas.width = nextWidth
      scaledCanvas.height = nextHeight
      const context = scaledCanvas.getContext('2d')
      if (!context) {
        return null
      }
      context.fillStyle = '#fff'
      context.fillRect(0, 0, nextWidth, nextHeight)
      context.drawImage(canvas, 0, 0, nextWidth, nextHeight)
      return scaledCanvas
    },
    releaseCanvas(canvas) {
      if (canvas) {
        canvas.width = 0
        canvas.height = 0
      }
    },
    async downloadPaperAsPngZip(task, pages) {
      this.$updatePaperDownloadTaskFileNameForPages(task, 'png', pages)
      const compositeFrameSize = await this.getKnowledgeBaseDownloadCompositeFrameSize(pages, task)
      if (pages.length == 1) {
        const page = pages[0]
        this.updatePaperDownloadTask(task, {
          fileName: `${task.name}.png`,
          progress: Math.max(task.progress || 0, 10),
          statusText: this.ui.downloading
        })
        const canvas = await this.renderKnowledgeBaseDownloadPageCanvas(page, task, 0, 1, 10, 95, compositeFrameSize)
        const blob = await this.canvasToPngBlob(canvas)
        this.releaseCanvas(canvas)
        this.ensurePaperDownloadTaskActive(task)
        this.updatePaperDownloadTask(task, { progress: 99, statusText: this.ui.savingFile })
        this.savePaperDownloadBlob(task, blob)
        return
      }
      const pngFiles = Array.isArray(task.pngFiles) ? task.pngFiles.slice() : []
      this.updatePaperDownloadTask(task, { pngFiles })
      for (let i = 0; i < pages.length; i++) {
        this.ensurePaperDownloadTaskActive(task)
        if (pngFiles[i]) {
          continue
        }
        const page = pages[i]
        this.updatePaperDownloadTask(task, {
          failedPageIndex: i,
          progress: this.getPaperDownloadImageProgress(i, pages.length, 10, 80, 0),
          statusText: this.ui.downloading
        })
        const canvas = await this.renderKnowledgeBaseDownloadPageCanvas(page, task, i, pages.length, 10, 80, compositeFrameSize)
        const blob = await this.canvasToPngBlob(canvas)
        pngFiles[i] = {
          fileName: this.getPaperPageFileName(page, i, 'png'),
          buffer: await this.blobToArrayBuffer(blob)
        }
        this.releaseCanvas(canvas)
        this.updatePaperDownloadTask(task, {
          pngFiles: pngFiles.slice(),
          failedPageIndex: i + 1
        })
      }
      this.ensurePaperDownloadTaskActive(task)
      const zip = new JSZip()
      pngFiles.forEach((file, index) => {
        if (file) {
          zip.file(file.fileName || this.getPaperPageFileName(pages[index], index, 'png'), file.buffer)
        }
      })
      this.updatePaperDownloadTask(task, { progress: 85, statusText: '\u6253\u5305 ZIP' })
      const zipBlob = await zip.generateAsync({ type: 'blob' }, metadata => {
        this.updatePaperDownloadTask(task, {
          progress: Math.min(98, Math.round(85 + (metadata.percent || 0) * 0.13)),
          statusText: '\u6253\u5305 ZIP'
        })
      })
      this.ensurePaperDownloadTaskActive(task)
      this.savePaperDownloadBlob(task, zipBlob)
    },
    async downloadPaperAsPdf(task, pages) {
      const compositeFrameSize = await this.getKnowledgeBaseDownloadCompositeFrameSize(pages, task)
      const pdfPageGroups = this.getKnowledgeBasePdfPageGroupsFromTask(task)
      this.updatePaperDownloadTask(task, {
        pdfPageGroups,
        pdfPages: this.flattenKnowledgeBasePdfPageGroups(pdfPageGroups)
      })
      for (let i = 0; i < pages.length; i++) {
        this.ensurePaperDownloadTaskActive(task)
        if (Array.isArray(pdfPageGroups[i]) && pdfPageGroups[i].length > 0) {
          continue
        }
        const page = pages[i]
        this.updatePaperDownloadTask(task, {
          failedPageIndex: i,
          progress: this.getPaperDownloadImageProgress(i, pages.length, 10, 75, 0),
          statusText: this.ui.downloading
        })
        const canvas = await this.renderKnowledgeBaseDownloadPageCanvas(page, task, i, pages.length, 10, 75, compositeFrameSize, {
          pdfText: this.isKnowledgeBaseTextPage(page)
        })
        pdfPageGroups[i] = await this.canvasToKnowledgeBasePdfPages(canvas, {
          splitLongPage: this.isKnowledgeBaseTextPage(page)
        })
        this.releaseCanvas(canvas)
        this.updatePaperDownloadTask(task, {
          pdfPageGroups: pdfPageGroups.slice(),
          pdfPages: this.flattenKnowledgeBasePdfPageGroups(pdfPageGroups),
          failedPageIndex: i + 1
        })
      }
      this.ensurePaperDownloadTaskActive(task)
      this.updatePaperDownloadTask(task, { progress: 85, statusText: '\u751f\u6210 PDF' })
      const imagePages = this.flattenKnowledgeBasePdfPageGroups(pdfPageGroups)
      const pdfBlob = this.buildPdfBlob(imagePages)
      this.ensurePaperDownloadTaskActive(task)
      this.savePaperDownloadBlob(task, pdfBlob)
    },
    savePaperDownloadBlob(task, blob) {
      if (this.$captureDownloadBlob(task, blob, task && task.fileName)) {
        return
      }
      this.revokePaperDownloadTaskUrl(task)
      const openUrl = URL.createObjectURL(blob)
      this.updatePaperDownloadTask(task, { openUrl, progress: Math.max(task.progress || 0, 99) })
      saveAs(blob, task.fileName)
    },
    revokePaperDownloadTaskUrl(task) {
      if (task && task.openUrl && String(task.openUrl).startsWith('blob:')) {
        URL.revokeObjectURL(task.openUrl)
      }
      if (task) {
        this.$set(task, 'openUrl', '')
      }
    },
    async fetchPaperBinaryBlob(url, onProgress, task) {
      return this.fetchCachedPreviewPerformanceBlob(url, async progressHandler => {
      const requestUrls = this.getPaperImageFetchUrls(url)
      let lastError = null
      for (const requestUrl of requestUrls) {
        let controller = null
        try {
          if (task) {
            this.ensurePaperDownloadTaskActive(task)
          }
          if (typeof AbortController !== 'undefined') {
            controller = new AbortController()
            if (task) {
              this.registerPreviewPerformanceTaskAbortController(task, controller)
            }
          }
          const response = await fetch(requestUrl, controller ? { signal: controller.signal } : undefined)
          if (!response.ok) {
            throw new Error(`File failed: ${response.status}`)
          }
          if (this.isPaperUnexpectedProxyResponse(response, requestUrl, url)) {
            throw new Error('File proxy returned unexpected content')
          }
          return await this.readPaperImageResponseBlob(response, progressHandler)
        } catch (error) {
          if (error && (error.name == 'AbortError' || task && task.cancelled)) {
            const cancelError = new Error('Download cancelled')
            cancelError.isPaperDownloadCancelled = true
            throw cancelError
          }
          error.isCorsLikeDownloadError = true
          lastError = error
        } finally {
          if (task) {
            this.unregisterPreviewPerformanceTaskAbortController(task, controller)
          }
        }
      }
      throw lastError || new Error('File download failed')
      }, { onProgress, task })
    },
    async fetchPaperImageBlob(url, onProgress, task) {
      return this.fetchCachedPreviewPerformanceBlob(url, async progressHandler => {
      if (this.getPaperImageFetchUrls) {
        const requestUrls = this.getPaperImageFetchUrls(url)
        let lastError = null
        for (const requestUrl of requestUrls) {
        let controller = null
        try {
            if (task) {
              this.ensurePaperDownloadTaskActive(task)
            }
            if (typeof AbortController !== 'undefined') {
              controller = new AbortController()
              if (task) {
                this.registerPreviewPerformanceTaskAbortController(task, controller)
              }
            }
            const response = await fetch(requestUrl, controller ? { signal: controller.signal } : undefined)
            if (!response.ok) {
              const error = new Error(`Image failed: ${response.status}`)
              error.isCorsLikeDownloadError = requestUrl != url
              throw error
            }
            const contentType = response.headers && response.headers.get ? response.headers.get('content-type') : ''
            const normalizedContentType = String(contentType).toLowerCase()
            const isImageResponse = normalizedContentType.includes('image/') || normalizedContentType.includes('octet-stream')
            if (contentType && !isImageResponse) {
              const error = new Error('Image proxy returned non-image content')
              error.isCorsLikeDownloadError = true
              throw error
            }
            const blob = await this.readPaperImageResponseBlob(response, progressHandler)
            if (task) {
              this.ensurePaperDownloadTaskActive(task)
            }
            const imageMimeType = this.getPaperImageMimeType(url)
            if (imageMimeType && !String(blob.type || '').toLowerCase().includes('image/')) {
              return new Blob([blob], { type: imageMimeType })
            }
            return blob
          } catch (error) {
            if (error && (error.name == 'AbortError' || task && task.cancelled)) {
              const cancelError = new Error('Download cancelled')
              cancelError.isPaperDownloadCancelled = true
              throw cancelError
            }
            error.isCorsLikeDownloadError = true
            lastError = error
          } finally {
            if (task) {
              this.unregisterPreviewPerformanceTaskAbortController(task, controller)
            }
          }
        }
        throw lastError || new Error('Image download failed')
      }
      const requestUrl = this.getPaperImageFetchUrl(url)
      let response
      let controller = null
      try {
        if (task) {
          this.ensurePaperDownloadTaskActive(task)
        }
        if (typeof AbortController !== 'undefined') {
          controller = new AbortController()
          if (task) {
            this.registerPreviewPerformanceTaskAbortController(task, controller)
          }
        }
        response = await fetch(requestUrl, controller ? { signal: controller.signal } : undefined)
      } catch (error) {
        if (error && (error.name == 'AbortError' || task && task.cancelled)) {
          const cancelError = new Error('Download cancelled')
          cancelError.isPaperDownloadCancelled = true
          throw cancelError
        }
        error.isCorsLikeDownloadError = true
        throw error
      } finally {
        if (task) {
          this.unregisterPreviewPerformanceTaskAbortController(task, controller)
        }
      }
      if (!response.ok) {
        throw new Error(`Image failed: ${response.status}`)
      }
      const contentType = response.headers && response.headers.get ? response.headers.get('content-type') : ''
      if (contentType && !String(contentType).toLowerCase().includes('image/')) {
        const error = new Error('\u56fe\u7247\u4ee3\u7406\u672a\u751f\u6548\uff0c\u8fd4\u56de\u5185\u5bb9\u4e0d\u662f\u56fe\u7247')
        error.isCorsLikeDownloadError = true
        throw error
      }
      const blob = await response.blob()
      if (progressHandler) {
        progressHandler(100)
      }
      return blob
      }, { onProgress, task })
    },
    async readPaperImageResponseBlob(response, onProgress) {
      const contentType = response.headers && response.headers.get ? response.headers.get('content-type') : ''
      const contentLength = Number(response.headers && response.headers.get ? response.headers.get('content-length') : 0)
      if (!response.body || typeof response.body.getReader != 'function' || !contentLength) {
        const blob = await response.blob()
        if (onProgress) {
          onProgress(100)
        }
        return blob
      }
      const reader = response.body.getReader()
      const chunks = []
      let receivedLength = 0
      let result = await reader.read()
      while (!result.done) {
        chunks.push(result.value)
        receivedLength += result.value.length
        if (onProgress) {
          onProgress(Math.min(100, Math.round((receivedLength / contentLength) * 100)))
        }
        result = await reader.read()
      }
      if (onProgress) {
        onProgress(100)
      }
      return new Blob(chunks, { type: contentType || 'application/octet-stream' })
    },
    getPaperImageFetchUrl(url) {
      return url || ''
    },
    getPaperImageFetchUrls(url) {
      return url ? [url] : []
    },
    getPaperProxyFetchUrl(url) {
      return url || ''
    },
    getPaperProxyBaseUrl() {
      return ''
    },
    canUsePaperImageProxy() {
      return false
    },
    isPaperUnexpectedProxyResponse() {
      return false
    },
    getPaperImageMimeType(url) {
      const extension = this.getUrlExtension(url, '').toLowerCase()
      if (extension == 'jpg' || extension == 'jpeg') {
        return 'image/jpeg'
      }
      if (extension == 'png') {
        return 'image/png'
      }
      if (extension == 'webp') {
        return 'image/webp'
      }
      if (extension == 'gif') {
        return 'image/gif'
      }
      if (extension == 'bmp') {
        return 'image/bmp'
      }
      return ''
    },
    isLikelyCorsDownloadError(error) {
      return !!(error && (error.isCorsLikeDownloadError || error.name == 'TypeError' || String(error.message || '').toLowerCase().includes('failed to fetch')))
    },
    downloadExternalUrl(url, fileName) {
      if (!url) {
        return
      }
      const link = document.createElement('a')
      link.href = url
      link.download = this.sanitizeFileName(fileName) || ''
      link.target = '_blank'
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    getPaperPageFileName(page, index, extension) {
      const pageNo = page && page.pageNo ? page.pageNo : index + 1
      return `${pageNo}.${extension}`
    },
    blobToArrayBuffer(blob) {
      if (blob && typeof blob.arrayBuffer == 'function') {
        return blob.arrayBuffer()
      }
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsArrayBuffer(blob)
      })
    },
    imageBlobToJpegPage(blob) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob)
        const image = new Image()
        image.onload = () => {
          const width = image.naturalWidth || image.width || 1
          const height = image.naturalHeight || image.height || 1
          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          const context = canvas.getContext('2d')
          if (context && 'imageSmoothingEnabled' in context) {
            context.imageSmoothingEnabled = true
            if ('imageSmoothingQuality' in context) {
              context.imageSmoothingQuality = 'high'
            }
          }
          context.fillStyle = '#fff'
          context.fillRect(0, 0, width, height)
          context.drawImage(image, 0, 0, width, height)
          URL.revokeObjectURL(url)
          canvas.toBlob(async jpegBlob => {
            if (!jpegBlob) {
              reject(new Error('Image convert failed'))
              return
            }
            resolve({
              width,
              height,
              data: new Uint8Array(await this.blobToArrayBuffer(jpegBlob))
            })
          }, 'image/jpeg', 1)
        }
        image.onerror = () => {
          URL.revokeObjectURL(url)
          reject(new Error('Image load failed'))
        }
        image.src = url
      })
    },
    buildPdfBlob(imagePages) {
      const pdfImagePages = this.trimTrailingBlankPdfPages(imagePages)
      const encoder = new TextEncoder()
      const chunks = []
      const offsets = []
      let offset = 0
      const writeString = value => {
        const bytes = encoder.encode(value)
        chunks.push(bytes)
        offset += bytes.length
      }
      const writeBytes = bytes => {
        chunks.push(bytes)
        offset += bytes.length
      }
      const objectCount = 2 + pdfImagePages.length * 3
      const writeObject = (id, parts) => {
        offsets[id] = offset
        writeString(`${id} 0 obj\n`)
        parts.forEach(part => {
          if (typeof part == 'string') {
            writeString(part)
          } else {
            writeBytes(part)
          }
        })
        writeString('\nendobj\n')
      }
      writeString('%PDF-1.4\n')
      writeObject(1, ['<< /Type /Catalog /Pages 2 0 R >>'])
      const pageIds = pdfImagePages.map((page, index) => 3 + index * 3)
      writeObject(2, [`<< /Type /Pages /Kids [${pageIds.map(id => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`])
      pdfImagePages.forEach((page, index) => {
        const pageId = 3 + index * 3
        const contentId = pageId + 1
        const imageId = pageId + 2
        const layout = this.$getDownloadPdfPageLayout(page)
        const imageName = `Im${index + 1}`
        const content = `q\n${layout.drawWidth} 0 0 ${layout.drawHeight} ${layout.offsetX} ${layout.offsetY} cm\n/${imageName} Do\nQ`
        writeObject(pageId, [`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${layout.pageWidth} ${layout.pageHeight}] /Resources << /XObject << /${imageName} ${imageId} 0 R >> >> /Contents ${contentId} 0 R >>`])
        writeObject(contentId, [`<< /Length ${encoder.encode(content).length} >>\nstream\n${content}\nendstream`])
        writeObject(imageId, [
          `<< /Type /XObject /Subtype /Image /Width ${layout.imageWidth} /Height ${layout.imageHeight} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${page.data.length} >>\nstream\n`,
          page.data,
          '\nendstream'
        ])
      })
      const xrefOffset = offset
      writeString(`xref\n0 ${objectCount + 1}\n`)
      writeString('0000000000 65535 f \n')
      for (let id = 1; id <= objectCount; id++) {
        writeString(`${String(offsets[id]).padStart(10, '0')} 00000 n \n`)
      }
      writeString(`trailer\n<< /Size ${objectCount + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`)
      return new Blob(chunks, { type: 'application/pdf' })
    },
    toggleFavorite(item) {
      if (!item) {
        return
      }
      this.$set(item, 'favorite', !item.favorite)
      this.moreMenuId = ''
      if (this.paperFilter == 'favorite' && !item.favorite) {
        this.selectedPaperIds = this.selectedPaperIds.filter(id => id != item.id)
        this.selectedPaperId = this.selectedPaperIds[0] || ''
      }
    },
    openPaperItem(item, options = {}) {
      if (!item) {
        return
      }
      if (item.isFolder || !item.resourceId) {
        this.clearSelection()
        this.fakeAction(item.isFolder ? '\u6253\u5f00\u6587\u4ef6\u5939' : '\u6253\u5f00\u8d44\u6e90')
        return
      }
      if (this.isPaperEncryptedItem(item)) {
        this.clearSelection()
        this.resetPaperDetailBeforePasswordPrompt()
        this.openPaperPasswordModal(item, '', false, 'open', [], options)
        return
      }
      this.openPaperDetail(item, '', options)
    },
    async openPaperDetail(item, password = '', options = {}) {
      this.rememberPaperListScrollPosition()
      this.clearSelection()
      this.detailMode = true
      this.currentPaper = item
      this.currentPaperPassword = password || ''
      this.clearPreviewZoomTip()
      return this.loadPaperDetail(item, Object.assign({}, options, { password: this.currentPaperPassword }))
    },
    resetPaperDetailBeforePasswordPrompt() {
      this.paperDetailRequestSeq += 1
      this.detailMode = false
      this.currentPaper = null
      this.currentPaperPassword = ''
      this.paperDetail = null
      this.paperDetailLoading = false
      this.paperDetailProgress = 0
      this.paperDetailError = ''
      this.clearPaperPreviewObjectUrls({ revoke: false })
      this.currentPageNo = 1
      this.pageGridOpen = false
      this.selectedDetailPageNos = []
      this.recordPanelOpen = false
      this.recordTextOpen = false
      this.activeRecording = null
      this.resetRecordTextState()
      this.stopPaperPreviewDrag()
      this.resetPaperZoom()
      this.clearPreviewZoomTip()
    },
    openPaperPasswordModal(item, error = '', keepInput = false, action = 'open', items = [], openOptions = {}) {
      this.paperPasswordTarget = item
      this.paperPasswordAction = action == 'download' ? 'download' : 'open'
      this.paperPasswordItems = Array.isArray(items) ? items.filter(Boolean) : []
      this.paperPasswordOpenOptions = openOptions && typeof openOptions == 'object' ? Object.assign({}, openOptions) : null
      this.paperPasswordError = error || ''
      this.paperPasswordVisible = false
      this.paperPasswordSubmitting = false
      if (!keepInput) {
        this.paperPasswordInput = ''
      }
      this.paperPasswordModalOpen = true
      this.$nextTick(() => {
        const input = this.$refs.paperPasswordInput
        if (input && typeof input.focus == 'function') {
          input.focus()
        }
      })
    },
    closePaperPasswordModal() {
      if (this.paperPasswordSubmitting) {
        return
      }
      this.paperPasswordRequestSeq += 1
      this.paperPasswordModalOpen = false
      this.paperPasswordTarget = null
      this.paperPasswordInput = ''
      this.paperPasswordVisible = false
      this.paperPasswordError = ''
      this.paperPasswordAction = 'open'
      this.paperPasswordItems = []
      this.paperPasswordOpenOptions = null
    },
    togglePaperPasswordVisible() {
      this.paperPasswordVisible = !this.paperPasswordVisible
    },
    async confirmPaperPassword() {
      if (this.paperPasswordSubmitting) {
        return
      }
      const item = this.paperPasswordTarget
      const rawPassword = String(this.paperPasswordInput || '')
      if (!rawPassword) {
        this.paperPasswordError = this.ui.passwordRequired
        this.$nextTick(() => {
          const input = this.$refs.paperPasswordInput
          if (input && typeof input.focus == 'function') {
            input.focus()
          }
        })
        return
      }
      if (!item) {
        this.closePaperPasswordModal()
        return
      }
      const action = this.paperPasswordAction == 'download' ? 'download' : 'open'
      const actionItems = Array.isArray(this.paperPasswordItems) ? this.paperPasswordItems.slice() : []
      this.paperPasswordSubmitting = true
      this.paperPasswordError = ''
      const requestId = this.paperPasswordRequestSeq + 1
      this.paperPasswordRequestSeq = requestId
      try {
        const hashedPassword = await this.encryptPaperPasswordToSHA256String(rawPassword)
        if (requestId != this.paperPasswordRequestSeq) {
          return
        }
        const ret = await checkCloudPassword({ password: hashedPassword }, { skipGlobalErrorMessage: true })
        if (requestId != this.paperPasswordRequestSeq) {
          return
        }
        if (this.isPaperApiCodeFailed(ret)) {
          this.paperPasswordError = this.getPaperPasswordErrorMessage()
          return
        }
        const passwordOpenOptions = this.paperPasswordOpenOptions && typeof this.paperPasswordOpenOptions == 'object'
          ? this.paperPasswordOpenOptions
          : {}
        this.paperPasswordModalOpen = false
        this.paperPasswordSubmitting = false
        this.paperPasswordTarget = null
        this.paperPasswordError = ''
        this.paperPasswordInput = ''
        this.paperPasswordVisible = false
        this.paperPasswordAction = 'open'
        this.paperPasswordItems = []
        this.paperPasswordOpenOptions = null
        if (action == 'download') {
          this.openPaperDownloadFormatModal(actionItems.length > 0 ? actionItems : [item], { password: hashedPassword })
          return
        }
        await this.openPaperDetail(item, hashedPassword, passwordOpenOptions)
      } catch (error) {
        if (requestId != this.paperPasswordRequestSeq) {
          return
        }
        if (this.isPaperPasswordCodeError(error) || this.isPaperApiCodeFailed(error)) {
          this.paperPasswordError = this.getPaperPasswordErrorMessage()
        } else {
          this.paperPasswordError = this.getPaperApiErrorMessage(error) || this.getPaperPasswordErrorMessage()
        }
      } finally {
        if (requestId == this.paperPasswordRequestSeq) {
          this.paperPasswordSubmitting = false
        }
      }
    },
    async encryptPaperPasswordToSHA256String(pass) {
      if (!pass) {
        return ''
      }
      if (typeof window === 'undefined' || !window.crypto || !window.crypto.subtle || typeof TextEncoder === 'undefined') {
        throw new Error('\u5f53\u524d\u6d4f\u89c8\u5668\u4e0d\u652f\u6301\u5bc6\u7801\u52a0\u5bc6')
      }
      const bytes = new TextEncoder().encode(pass)
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', bytes)
      return Array.from(new Uint8Array(hashBuffer))
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase()
    },
    closePaperDetail() {
      this.paperDetailRequestSeq += 1
      this.detailMode = false
      this.currentPaper = null
      this.currentPaperPassword = ''
      this.paperDetail = null
      this.paperDetailLoading = false
      this.paperDetailProgress = 0
      this.paperDetailError = ''
      this.clearPaperPreviewObjectUrls({ revoke: false })
      this.currentPageNo = 1
      this.pageGridOpen = false
      this.pageGridReturnPageNo = 1
      this.selectedDetailPageNos = []
      this.selectedDetailPageAnchorNo = ''
      this.recordPanelOpen = false
      this.recordTextOpen = false
      this.activeRecording = null
      this.resetRecordTextState()
      this.stopPaperPreviewDrag()
      this.resetPaperZoom()
      this.clearPreviewZoomTip()
      this.restorePaperListScrollPosition()
    },
    handleDetailBack() {
      if (this.paperDetailLoading) {
        this.closePaperDetail()
        if (this.detailBackRoot) {
          this.$emit('back-root')
        }
        return
      }
      if (this.pageGridOpen) {
        const targetPageNo = Number(this.pageGridReturnPageNo) || Number(this.currentPageNo) || 1
        const returnZoomLevel = this.takePreviewGridZoomLevel('paper')
        this.pageGridOpen = false
        this.currentPageNo = targetPageNo
        this.setPaperZoomLevel(returnZoomLevel)
        return
      }
      this.closePaperDetail()
      if (this.detailBackRoot) {
        this.$emit('back-root')
      }
    },
    toggleRecordPanel() {
      if (this.paperDetailLoading || !this.hasDetailRecordings) {
        return
      }
      this.recordPanelOpen = !this.recordPanelOpen
      if (!this.recordPanelOpen) {
        this.recordTextOpen = false
        this.activeRecording = null
        this.resetRecordTextState()
      }
    },
    closeRecordPanel() {
      if (this.paperDetailLoading) {
        return
      }
      this.recordPanelOpen = false
      this.recordTextOpen = false
      this.activeRecording = null
      this.resetRecordTextState()
    },
    togglePageGrid() {
      if (this.paperDetailLoading || this.pageGridOpen || this.detailPages.length == 0) {
        return
      }
      this.pageGridReturnPageNo = Number(this.currentPageNo) || 1
      this.rememberPreviewGridZoomLevel('paper', this.zoomLevel)
      this.selectedDetailPageNos = []
      this.selectedDetailPageAnchorNo = this.pageGridReturnPageNo
      this.pageGridOpen = true
      this.recordPanelOpen = false
      this.recordTextOpen = false
      this.activeRecording = null
      this.resetRecordTextState()
      this.clearPreviewZoomTip()
    },
    selectCurrentDetailPageInGrid() {
      this.selectedDetailPageNos = []
      this.selectedDetailPageAnchorNo = Number(this.currentPageNo) || 1
    },
    isDetailPageSelected(pageNo) {
      return this.selectedDetailPageNos.includes(pageNo)
    },
    toggleDetailPageSelection(pageNo, event) {
      if (this.paperDetailLoading) {
        return
      }
      const result = selectItemWithWindowsRange(
        this.detailPages,
        this.selectedDetailPageNos,
        { pageNo },
        this.selectedDetailPageAnchorNo || this.currentPageNo,
        event,
        page => page && page.pageNo
      )
      this.selectedDetailPageNos = result.selectedIds
      this.selectedDetailPageAnchorNo = result.anchorId
    },
    openDetailPageFromGrid(pageNo) {
      if (this.paperDetailLoading) {
        return
      }
      const isSamePage = Number(pageNo) == Number(this.pageGridReturnPageNo)
      const returnZoomLevel = this.takePreviewGridZoomLevel('paper')
      this.currentPageNo = pageNo
      this.pageGridOpen = false
      if (isSamePage) {
        this.setPaperZoomLevel(returnZoomLevel)
        return
      }
      this.resetPaperZoom()
      this.resetKnowledgeBaseAiNotePreviewScroll()
      this.clearPreviewZoomTip()
    },
    selectDetailPage(pageNo) {
      if (this.paperDetailLoading) {
        return
      }
      this.currentPageNo = pageNo
      this.pageGridOpen = false
      this.clearPreviewZoomTip()
      if (this.isKnowledgeBaseAppAiNotePreview) {
        this.resetKnowledgeBaseAiNotePreviewScroll()
        return
      }
      this.centerPaperPreviewScroll()
    },
    goPrevPage() {
      if (this.isPaperPreviewNavigationLocked()) {
        return
      }
      if (this.currentPageIndex <= 0) {
        return
      }
      this.currentPageNo = this.detailPages[this.currentPageIndex - 1].pageNo
      this.clearPreviewZoomTip()
      this.resetKnowledgeBaseAiNotePreviewScroll()
    },
    goNextPage() {
      if (this.isPaperPreviewNavigationLocked()) {
        return
      }
      if (this.currentPageIndex >= this.detailPages.length - 1) {
        return
      }
      this.currentPageNo = this.detailPages[this.currentPageIndex + 1].pageNo
      this.clearPreviewZoomTip()
      this.resetKnowledgeBaseAiNotePreviewScroll()
    },
    zoomIn() {
      if (this.paperDetailLoading || this.pageGridOpen || this.detailPages.length == 0) {
        return
      }
      const nextLevel = this.setPaperZoomLevel(this.zoomLevel + 1)
      this.showPreviewZoomTip('paper', 'in', nextLevel, this.ui)
    },
    zoomOut() {
      if (this.paperDetailLoading || this.pageGridOpen || this.detailPages.length == 0) {
        return
      }
      const nextLevel = this.setPaperZoomLevel(this.zoomLevel - 1)
      this.showPreviewZoomTip('paper', 'out', nextLevel, this.ui)
    },
    resetPaperZoom() {
      this.setPaperZoomLevel(0)
    },
    setPaperZoomLevel(level) {
      const nextLevel = this.clampPreviewZoomLevel(level)
      this.zoomLevel = nextLevel
      this.zoomValue = this.getPaperZoomValue(nextLevel)
      this.centerPaperPreviewScroll()
      return nextLevel
    },
    getPaperZoomValue(level) {
      return this.getPreviewZoomValue(level)
    },
    centerPaperPreviewScroll() {
      this.$nextTick(() => {
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame == 'function') {
          window.requestAnimationFrame(this.applyPaperPreviewCenterScroll)
          return
        }
        this.applyPaperPreviewCenterScroll()
      })
    },
    applyPaperPreviewCenterScroll() {
      const shell = this.$refs.paperPreviewShell
      if (!shell) {
        return
      }
      shell.scrollLeft = Math.max(0, (shell.scrollWidth - shell.clientWidth) / 2)
    },
    resetKnowledgeBaseAiNotePreviewScroll() {
      if (!this.isKnowledgeBaseAppAiNotePreview) {
        return
      }
      this.$nextTick(() => {
        const applyScroll = () => {
          const shell = this.$refs.paperPreviewShell
          if (!shell) {
            return
          }
          shell.scrollLeft = Math.max(0, (shell.scrollWidth - shell.clientWidth) / 2)
          shell.scrollTop = 0
          const textPageRef = this.$refs.knowledgeTextPage
          const textPage = Array.isArray(textPageRef) ? textPageRef[0] : textPageRef
          const currentTextPage = textPage && shell.contains(textPage)
            ? textPage
            : shell.querySelector('.knowledge-text-page')
          if (currentTextPage) {
            currentTextPage.scrollLeft = 0
            currentTextPage.scrollTop = 0
          }
        }
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame == 'function') {
          window.requestAnimationFrame(applyScroll)
          return
        }
        applyScroll()
      })
    },
    startPaperPreviewDrag(event) {
      const shell = this.$refs.paperPreviewShell
      if (this.paperDetailLoading || !event || event.button !== 0 || !shell) {
        return
      }
      if (shell.scrollWidth <= shell.clientWidth && shell.scrollHeight <= shell.clientHeight) {
        return
      }
      event.preventDefault()
      this.paperPreviewDragState = {
        x: event.clientX,
        y: event.clientY,
        scrollLeft: shell.scrollLeft,
        scrollTop: shell.scrollTop
      }
      if (typeof document !== 'undefined') {
        document.addEventListener('mousemove', this.handlePaperPreviewDrag)
        document.addEventListener('mouseup', this.stopPaperPreviewDrag)
      }
    },
    handlePaperPreviewDrag(event) {
      const shell = this.$refs.paperPreviewShell
      const state = this.paperPreviewDragState
      if (!shell || !state || !event) {
        return
      }
      shell.scrollLeft = state.scrollLeft - (event.clientX - state.x)
      shell.scrollTop = state.scrollTop - (event.clientY - state.y)
    },
    stopPaperPreviewDrag() {
      if (typeof document !== 'undefined') {
        document.removeEventListener('mousemove', this.handlePaperPreviewDrag)
        document.removeEventListener('mouseup', this.stopPaperPreviewDrag)
      }
      this.paperPreviewDragState = null
    },
    resetRecordTextState() {
      this.recordTextRequestSeq += 1
      this.recordTextLoading = false
      this.recordTextError = this.ui.loadFailed
    },
    closeRecordingText() {
      this.recordTextOpen = false
      this.activeRecording = null
      this.resetRecordTextState()
    },
    openRecordingText(recording) {
      if (!this.hasTranscript(recording)) {
        return
      }
      this.resetRecordTextState()
      this.activeRecording = recording
      this.recordTextOpen = true
      if (!recording.transcriptText && recording.transcriptTextUrl) {
        this.loadRecordingTranscript(recording)
      }
    },
    async loadRecordingTranscript(recording) {
      const url = recording && recording.transcriptTextUrl ? String(recording.transcriptTextUrl).trim() : ''
      if (!url) {
        return
      }
      const requestId = this.recordTextRequestSeq + 1
      this.recordTextRequestSeq = requestId
      this.recordTextLoading = true
      this.recordTextError = this.ui.loadFailed
      try {
        let text = ''
        if (url.startsWith('mock://')) {
          text = ''
        } else {
          const response = await fetch(url)
          if (!response.ok) {
            throw new Error(`Transcript failed: ${response.status}`)
          }
          text = this.normalizeTranscriptText(await response.text())
        }
        if (requestId != this.recordTextRequestSeq || this.activeRecording !== recording) {
          return
        }
        this.$set(recording, 'transcriptText', text || this.ui.noTranscript)
      } catch (error) {
        if (requestId == this.recordTextRequestSeq && this.activeRecording === recording) {
          this.recordTextError = this.ui.loadFailed
        }
      } finally {
        if (requestId == this.recordTextRequestSeq && this.activeRecording === recording) {
          this.recordTextLoading = false
        }
      }
    },
    normalizeTranscriptText(value) {
      const text = String(value || '').trim()
      if (!text) {
        return ''
      }
      const parsed = this.parseMetaJson(text)
      if (parsed) {
        const extracted = this.extractTranscriptTextPayload(parsed)
        if (extracted) {
          return extracted
        }
      }
      return text
    },
    extractTranscriptTextPayload(payload) {
      if (payload === undefined || payload === null) {
        return ''
      }
      if (Array.isArray(payload)) {
        return payload.map(item => this.extractTranscriptTextPayload(item)).filter(Boolean).join('\n')
      }
      if (typeof payload == 'object') {
        const direct = this.pickValue(payload, ['transcriptText', 'text', 'content', 'transcript', 'data', 'result'])
        if (direct !== undefined) {
          return this.extractTranscriptTextPayload(direct)
        }
        return ''
      }
      return String(payload).trim()
    },
    hasTranscript(recording) {
      return !!(recording && (recording.transcriptText || recording.transcriptTextUrl))
    },
    hasKnowledgeBaseTextContent(page) {
      return !!(page && (this.normalizeKnowledgeBaseContentText(page.content || '').trim() || this.getKnowledgeBasePageRemark(page)))
    },
    isKnowledgeBaseTextPage(page) {
      return !!(page && (this.hasKnowledgeBaseTextContent(page) || (!page.imageUrl && (page.pageType == 'text' || page.name))))
    },
    isKnowledgeBaseImageOnlyPage(page) {
      return !!(page && page.imageUrl && !this.isKnowledgeBaseTextPage(page))
    },
    isKnowledgeBaseDownloadablePage(page) {
      return !!(page && (page.imageUrl || this.isKnowledgeBaseTextPage(page)))
    },
    isKnowledgeBaseDetailPageDownloadable(page) {
      return this.isKnowledgeBaseDownloadablePage(page) || this.isPaperDetailBlankPage(page)
    },
    isPaperDetailBlankPage(page) {
      return !!(page && Number(page.pageNo) > 0 && !page.imageUrl && !page.pdfPreviewUrl && page.pageType == 'blank')
    },
    getKnowledgeBasePageTitle(page) {
      const pageNo = page && page.pageNo ? page.pageNo : ''
      return page && page.name ? page.name : (pageNo ? `Page ${pageNo}` : 'Page')
    },
    getKnowledgeBasePageLabel(page) {
      const title = this.getKnowledgeBasePageTitle(page)
      return title || `Page ${page && page.pageNo ? page.pageNo : ''}`
    },
    getKnowledgeBasePagePreviewText(page) {
      const text = this.getKnowledgeBasePlainText(page && page.content ? page.content : '')
      return text || this.getKnowledgeBasePageRemarkDisplayText(page) || '\u6682\u65e0\u5185\u5bb9'
    },
    getKnowledgeBasePageRemark(page) {
      return page && page.remark !== undefined && page.remark !== null ? String(page.remark).trim() : ''
    },
    getKnowledgeBasePageRemarkDisplayText(page) {
      const remark = this.getKnowledgeBasePageRemark(page)
      if (!remark) {
        return ''
      }
      return remark.charAt(0) == '#' ? remark : `#${remark}`
    },
    renderKnowledgeBaseMarkdownContent(value) {
      const text = this.normalizeKnowledgeBaseContentText(value).trim()
      if (!text) {
        return ''
      }
      return KNOWLEDGE_BASE_MARKDOWN.render(text)
    },
    normalizeKnowledgeBaseContentText(value) {
      const text = this.decodeKnowledgeBaseContentText(value).replace(/\r\n?/g, '\n')
      if (!text) {
        return ''
      }
      return text
        .replace(/^([ \t]*)(?:\uFFFD[?\uFF1F]|\u951F\?|\u951F\u65A4\u62F7|&#65533;\?|&#xFFFD;\?|&amp;#65533;\?|&amp;#xFFFD;\?)\s*/gmi, '$1- ')
        .replace(/^([ \t]*)(?:&bull;|&#8226;|&#x2022;|\u2022|\u25CF|\u25CB|\u25E6|\u25AA|\u25AB)\s*/gmi, '$1- ')
    },
    decodeKnowledgeBaseContentText(value) {
      let current = String(value || '')
      if (!current) {
        return ''
      }
      for (let i = 0; i < 6; i++) {
        const jsonDecoded = this.tryDecodeKnowledgeBaseJsonContent(current)
        const base = jsonDecoded === null ? current : jsonDecoded
        const next = this.decodeKnowledgeBaseEscapesOnce(base)
        if (next == current) {
          current = next
          break
        }
        current = next
      }
      return current
    },
    tryDecodeKnowledgeBaseJsonContent(value) {
      const text = String(value || '').trim()
      if (!text) {
        return ''
      }
      const looksLikeQuoted = text.startsWith('"') && text.endsWith('"')
      const looksLikeJson = (text.startsWith('{') && text.endsWith('}')) || (text.startsWith('[') && text.endsWith(']'))
      if (!looksLikeQuoted && !looksLikeJson) {
        return null
      }
      try {
        const parsed = JSON.parse(text)
        if (typeof parsed == 'string') {
          return parsed
        }
        const extracted = this.extractKnowledgeBaseContentPayload(parsed)
        if (extracted) {
          return extracted
        }
      } catch (error) {
        return null
      }
      return null
    },
    extractKnowledgeBaseContentPayload(payload) {
      if (payload === undefined || payload === null) {
        return ''
      }
      if (typeof payload == 'string') {
        return payload
      }
      if (Array.isArray(payload)) {
        return payload.map(item => this.extractKnowledgeBaseContentPayload(item)).filter(Boolean).join('\n')
      }
      if (typeof payload == 'object') {
        const direct = this.pickValue(payload, [
          'content',
          'text',
          'message',
          'answer',
          'result',
          'output',
          'response',
          'data',
          'final',
          'reasoning'
        ])
        if (direct !== undefined) {
          const extracted = this.extractKnowledgeBaseContentPayload(direct)
          if (extracted) {
            return extracted
          }
        }
        const values = Object.keys(payload).map(key => payload[key])
        return values.map(item => this.extractKnowledgeBaseContentPayload(item)).filter(Boolean).join('\n')
      }
      return String(payload)
    },
    decodeKnowledgeBaseEscapesOnce(value) {
      const text = String(value || '')
      if (!text) {
        return ''
      }
      return text
        .replace(/\\u\{([0-9a-fA-F]{1,6})\}/g, (match, code) => {
          try {
            return String.fromCodePoint(parseInt(code, 16))
          } catch (error) {
            return match
          }
        })
        .replace(/\\u([0-9a-fA-F]{4})/g, (match, code) => String.fromCharCode(parseInt(code, 16)))
        .replace(/\\x([0-9a-fA-F]{2})/g, (match, code) => String.fromCharCode(parseInt(code, 16)))
        .replace(/\\r\\n/g, '\n')
        .replace(/\\n/g, '\n')
        .replace(/\\r/g, '\n')
        .replace(/\\t/g, '\t')
        .replace(/\\f/g, '\f')
        .replace(/\\v/g, '\v')
        .replace(/\\(["'\\/])/g, '$1')
        .replace(/\\\\/g, '\\')
    },
    getKnowledgeBasePlainText(value) {
      const text = this.normalizeKnowledgeBaseContentText(value)
      if (!text) {
        return ''
      }
      if (typeof document === 'undefined' || !/[<>&]/.test(text)) {
        return text
      }
      const element = document.createElement('div')
      element.innerHTML = text.replace(/<br\s*\/?>/gi, '\n')
      return element.textContent || element.innerText || ''
    },
    downloadCurrentPage() {
      if (this.paperDetailLoading) {
        return
      }
      if (this.pageGridOpen) {
        if (this.selectedDetailPages.length > 0) {
          this.openDetailPagesDownloadFormatModal(this.selectedDetailPages)
          return
        }
        this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u9875\u9762')
        return
      }
      if (this.isKnowledgeBaseDownloadablePage(this.currentPage)) {
        this.openDetailPagesDownloadFormatModal([this.currentPage])
        return
      }
      if (this.isPaperDetailBlankPage(this.currentPage)) {
        this.openDetailPagesDownloadFormatModal([this.currentPage])
        return
      }
      this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u9875\u9762')
    },
    startDetailPagesDownload(pages, format = 'png') {
      const list = Array.isArray(pages)
        ? pages.filter(page => this.isKnowledgeBaseDetailPageDownloadable(page))
        : []
      if (list.length == 0) {
        this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u9875\u9762')
        return
      }
      if (!this.$canStartDownloadBatch(list.length, 1, { skipSelectionLimit: true })) {
        return
      }
      const task = this.createDetailPagesDownloadTask(list, format)
      this.$enqueuePaperDownloadTask(task, () => this.runDetailPagesDownloadTask(task))
      if (this.pageGridOpen) {
        this.selectedDetailPageNos = []
        this.selectedDetailPageAnchorNo = ''
      }
    },
    createDetailPagesDownloadTask(pages, format = 'png') {
      const baseName = this.$getDownloadBaseName(this.paperDetailTitle, 'Knowledge Base', this.sanitizeFileName)
      const safeFormat = format == 'png' ? 'png' : 'pdf'
      const isSingle = pages.length == 1
      const extension = safeFormat == 'pdf' ? 'pdf' : (isSingle ? 'png' : 'zip')
      const suffix = isSingle ? ` page ${pages[0].pageNo || 1}` : ''
      return {
        id: `paper-page-download-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        kind: 'detail-pages',
        name: baseName,
        fileName: `${baseName}${suffix}.${extension}`,
        format: safeFormat,
        pages: pages.slice(),
        pngFiles: [],
        pdfPages: [],
        pdfPageGroups: [],
        failedPageIndex: 0,
        abortController: null,
        cancelled: false,
        progress: 0,
        state: 'running',
        statusText: this.ui.downloadPreparing
      }
    },
    async runDetailPagesDownloadTask(task) {
      try {
        this.ensurePaperDownloadTaskActive(task)
        const pages = Array.isArray(task.pages) ? task.pages : []
        if (pages.length == 0) {
          throw new Error('No pages')
        }
        if (task.format == 'pdf') {
          await this.runKnowledgeBaseDetailPdfTask(task, pages)
        } else {
          await this.runKnowledgeBaseDetailPngTask(task, pages)
        }
        this.updatePaperDownloadTask(task, {
          progress: 100,
          state: 'done',
          statusText: this.ui.downloadDone
        })
      } catch (error) {
        if (error && error.isPaperDownloadCancelled) {
          return
        }
        const isCorsLikeError = this.isLikelyCorsDownloadError(error)
        this.updatePaperDownloadTask(task, {
          state: 'error',
          progress: task.progress || 0,
          statusText: isCorsLikeError ? this.ui.imageReadFailed : (error && error.message ? error.message : this.ui.downloadFailed)
        })
      }
    },
    async getKnowledgeBaseDownloadCompositeFrameSize(pages, task) {
      if (task && task.compositeFrameSize && task.compositeFrameSize.width > 0 && task.compositeFrameSize.height > 0) {
        return task.compositeFrameSize
      }
      const pageList = Array.isArray(pages)
        ? pages.filter(page => this.isKnowledgeBaseImageOnlyPage(page))
        : []
      if (pageList.length == 0) {
        return null
      }
      const sizes = await this.runPaperPerformanceTasks(
        pageList,
        this.getPaperPreviewRenderConcurrency(),
        async page => {
          const blob = await this.fetchPaperImageBlob(page.imageUrl, null, task)
          return this.getPaperImageBlobSize(blob)
        }
      )
      const frameSize = this.getPaperWidthAlignedFrameSize(sizes)
      if (task && frameSize && frameSize.width > 0 && frameSize.height > 0) {
        this.$set(task, 'compositeFrameSize', frameSize)
      }
      return frameSize
    },
    async runKnowledgeBaseDetailPngTask(task, pages) {
      const compositeFrameSize = await this.getKnowledgeBaseDownloadCompositeFrameSize(pages, task)
      const pngFiles = Array.isArray(task.pngFiles) ? task.pngFiles.slice() : []
      this.updatePaperDownloadTask(task, { pngFiles })
      for (let i = 0; i < pages.length; i++) {
        this.ensurePaperDownloadTaskActive(task)
        if (pngFiles[i]) {
          continue
        }
        const canvas = await this.renderKnowledgeBaseDownloadPageCanvas(pages[i], task, i, pages.length, 5, 85, compositeFrameSize, {
          pdfText: this.isKnowledgeBaseTextPage(pages[i])
        })
        const blob = await this.canvasToPngBlob(canvas)
        pngFiles[i] = {
          fileName: this.getPaperPageFileName(pages[i], i, 'png'),
          buffer: await this.blobToArrayBuffer(blob)
        }
        this.releaseCanvas(canvas)
        this.updatePaperDownloadTask(task, {
          pngFiles: pngFiles.slice(),
          failedPageIndex: i + 1
        })
      }
      this.ensurePaperDownloadTaskActive(task)
      if (pages.length == 1 && pngFiles[0]) {
        this.updatePaperDownloadTask(task, { progress: 99, statusText: this.ui.savingFile })
        this.savePaperDownloadBlob(task, new Blob([pngFiles[0].buffer], { type: 'image/png' }))
        return
      }
      const zip = new JSZip()
      pngFiles.forEach((file, index) => {
        if (file) {
          zip.file(file.fileName || this.getPaperPageFileName(pages[index], index, 'png'), file.buffer)
        }
      })
      this.updatePaperDownloadTask(task, { progress: 90, statusText: '\u6253\u5305 ZIP' })
      const zipBlob = await zip.generateAsync({ type: 'blob' }, metadata => {
        this.updatePaperDownloadTask(task, {
          progress: Math.min(98, Math.round(90 + (metadata.percent || 0) * 0.08)),
          statusText: '\u6253\u5305 ZIP'
        })
      })
      this.ensurePaperDownloadTaskActive(task)
      this.savePaperDownloadBlob(task, zipBlob)
    },
    async runKnowledgeBaseDetailPdfTask(task, pages) {
      const compositeFrameSize = await this.getKnowledgeBaseDownloadCompositeFrameSize(pages, task)
      const pdfPageGroups = this.getKnowledgeBasePdfPageGroupsFromTask(task)
      this.updatePaperDownloadTask(task, {
        pdfPageGroups,
        pdfPages: this.flattenKnowledgeBasePdfPageGroups(pdfPageGroups)
      })
      for (let i = 0; i < pages.length; i++) {
        this.ensurePaperDownloadTaskActive(task)
        if (Array.isArray(pdfPageGroups[i]) && pdfPageGroups[i].length > 0) {
          continue
        }
        const canvas = await this.renderKnowledgeBaseDownloadPageCanvas(pages[i], task, i, pages.length, 5, 85, compositeFrameSize)
        pdfPageGroups[i] = await this.canvasToKnowledgeBasePdfPages(canvas, {
          splitLongPage: this.isKnowledgeBaseTextPage(pages[i])
        })
        if (this.isPaperDetailBlankPage(pages[i])) {
          pdfPageGroups[i].forEach(pdfPage => {
            pdfPage.isBlank = false
          })
        }
        this.releaseCanvas(canvas)
        this.updatePaperDownloadTask(task, {
          pdfPageGroups: pdfPageGroups.slice(),
          pdfPages: this.flattenKnowledgeBasePdfPageGroups(pdfPageGroups),
          failedPageIndex: i + 1
        })
      }
      this.ensurePaperDownloadTaskActive(task)
      this.updatePaperDownloadTask(task, { progress: 90, statusText: '\u751f\u6210 PDF' })
      const pdfPages = this.flattenKnowledgeBasePdfPageGroups(pdfPageGroups)
      const pdfBlob = this.buildPdfBlob(pdfPages)
      this.ensurePaperDownloadTaskActive(task)
      this.savePaperDownloadBlob(task, pdfBlob)
    },
    async renderKnowledgeBaseDownloadPageCanvas(page, task, index, total, startProgress, endProgress, compositeFrameSize = null, options = {}) {
      this.updatePaperDownloadTask(task, {
        failedPageIndex: index,
        progress: this.getPaperDownloadImageProgress(index, total, startProgress, endProgress, 0),
        statusText: this.ui.downloading
      })
      if (this.isKnowledgeBaseImageOnlyPage(page)) {
        const blob = await this.fetchPaperImageBlob(page.imageUrl, percent => {
          this.updatePaperDownloadTask(task, {
            progress: this.getPaperDownloadImageProgress(index, total, startProgress, endProgress, percent),
            statusText: this.ui.downloading
          })
        }, task)
        this.ensurePaperDownloadTaskActive(task)
        return this.createPaperImageFrameCanvas(blob, compositeFrameSize)
      }
      if (task && task.kind == 'detail-pages' && this.isPaperDetailBlankPage(page)) {
        this.updatePaperDownloadTask(task, {
          progress: this.getPaperDownloadImageProgress(index, total, startProgress, endProgress, 100),
          statusText: this.ui.downloading
        })
        return this.createPaperBlankPageCanvas(compositeFrameSize)
      }
      const canvas = await this.createKnowledgeBaseTextCanvas(page, {
        pdfText: !!(options && options.pdfText)
      })
      this.updatePaperDownloadTask(task, {
        progress: this.getPaperDownloadImageProgress(index, total, startProgress, endProgress, 100),
        statusText: this.ui.downloading
      })
      return canvas
    },
    imageBlobToCanvas(blob) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob)
        const image = new Image()
        image.onload = () => {
          const width = image.naturalWidth || image.width || 1
          const height = image.naturalHeight || image.height || 1
          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          const context = canvas.getContext('2d')
          context.fillStyle = '#fff'
          context.fillRect(0, 0, width, height)
          context.drawImage(image, 0, 0, width, height)
          URL.revokeObjectURL(url)
          resolve(canvas)
        }
        image.onerror = () => {
          URL.revokeObjectURL(url)
          reject(new Error('Image load failed'))
        }
        image.src = url
      })
    },
    async createKnowledgeBaseTextCanvas(page, options = {}) {
      const errors = []
      try {
        return await this.createKnowledgeBaseRenderedTextCanvas(page, options)
      } catch (error) {
        errors.push(error)
      }
      try {
        return await this.createKnowledgeBaseRenderedTextCanvas(page, options, { renderer: 'svg' })
      } catch (error) {
        errors.push(error)
      }
      return this.createKnowledgeBaseMarkdownCanvas(page, options, errors)
    },
    async createKnowledgeBaseRenderedTextCanvas(page, options = {}, renderOptions = {}) {
      if (typeof document === 'undefined') {
        return this.createKnowledgeBasePlainTextCanvas(page, options)
      }
      const renderer = renderOptions && renderOptions.renderer == 'svg' ? 'svg' : 'html2canvas'
      if (renderer == 'svg' && typeof XMLSerializer === 'undefined') {
        throw new Error('Text SVG render unavailable')
      }
      const layout = this.getKnowledgeBaseTextDownloadLayout(options)
      const mount = document.createElement('div')
      mount.className = 'knowledge-text-download-host'
      mount.style.position = 'fixed'
      mount.style.left = '0'
      mount.style.top = '0'
      mount.style.width = `${layout.cssWidth}px`
      mount.style.pointerEvents = 'none'
      mount.style.zIndex = '-1'
      const styleElement = document.createElement('style')
      styleElement.textContent = this.getKnowledgeBaseDownloadTextCss(layout)
      const pageElement = this.createKnowledgeBaseDownloadTextElement(page)
      mount.appendChild(styleElement)
      mount.appendChild(pageElement)
      document.body.appendChild(mount)
      try {
        await this.waitForKnowledgeBaseDownloadTextRender()
        await this.waitForKnowledgeBaseDownloadElementImages(pageElement)
        const measuredHeight = Math.ceil(Math.max(
          pageElement.scrollHeight || 0,
          pageElement.offsetHeight || 0,
          layout.cssMinHeight
        ))
        const cssHeight = options.thumbnail
          ? Math.max(layout.cssMinHeight, Math.min(layout.cssMaxHeight, measuredHeight))
          : Math.max(layout.cssMinHeight, measuredHeight)
        pageElement.style.height = `${cssHeight}px`
        pageElement.style.overflow = 'hidden'
        await this.waitForKnowledgeBaseDownloadTextRender()
        await this.waitForKnowledgeBaseDownloadElementImages(pageElement)
        if (renderer == 'svg') {
          const svg = this.serializeKnowledgeBaseDownloadTextElement(pageElement, styleElement, layout.cssWidth, cssHeight)
          return await this.createKnowledgeBaseSvgCanvas(svg, layout.cssWidth, cssHeight, layout.outputScale)
        }
        return await this.captureKnowledgeBaseDownloadTextElement(pageElement, layout, cssHeight)
      } finally {
        if (mount.parentNode) {
          mount.parentNode.removeChild(mount)
        }
      }
    },
    async captureKnowledgeBaseDownloadTextElement(pageElement, layout, cssHeight) {
      const width = Math.max(1, Math.round(Number(layout && layout.cssWidth) || pageElement.offsetWidth || 1))
      const height = Math.max(1, Math.round(Number(cssHeight) || pageElement.offsetHeight || 1))
      const windowWidth = typeof window === 'undefined' ? width : Math.max(width, Math.round(Number(window.innerWidth) || 0))
      const windowHeight = typeof window === 'undefined' ? height : Math.max(height, Math.round(Number(window.innerHeight) || 0))
      return html2canvas(pageElement, {
        backgroundColor: '#fff',
        scale: Math.max(1, Number(layout && layout.outputScale) || 1),
        width,
        height,
        windowWidth,
        windowHeight,
        scrollX: 0,
        scrollY: 0,
        useCORS: true,
        allowTaint: false,
        logging: false,
        imageTimeout: 5000
      })
    },
    getKnowledgeBaseTextDownloadLayout(options = {}) {
      const thumbnail = !!(options && options.thumbnail)
      if (thumbnail) {
        return {
          cssWidth: 740,
          cssMinHeight: 990,
          cssMaxHeight: 4096,
          outputScale: 1
        }
      }
      const previewStage = typeof document === 'undefined' ? null : document.querySelector('.paper-preview-stage')
      let cssWidth = 0
      let cssHeight = 0
      if (previewStage && typeof window !== 'undefined') {
        const computed = window.getComputedStyle(previewStage)
        cssWidth = Math.round(parseFloat(computed.width) || 0)
        cssHeight = Math.round(parseFloat(computed.height) || 0)
      }
      if (!(cssWidth > 0 && cssHeight > 0)) {
        const viewportHeight = typeof window === 'undefined' ? 0 : Math.max(0, Number(window.innerHeight) || 0)
        cssHeight = Math.round(Math.max(480, viewportHeight - 64))
        cssWidth = Math.round(cssHeight * 0.75)
      }
      if (!(cssWidth > 0 && cssHeight > 0)) {
        cssWidth = 672
        cssHeight = 896
      }
      const deviceScale = typeof window === 'undefined' ? 1 : Math.max(1, Number(window.devicePixelRatio) || 1)
      const isPdfText = !!(options && options.pdfText)
      const targetOutputWidth = isPdfText ? 1240 : 1240
      const maxOutputScale = isPdfText ? 2.5 : 3
      const minOutputScale = targetOutputWidth / Math.max(1, cssWidth)
      const outputScale = Math.max(1, Math.min(maxOutputScale, Math.max(deviceScale, minOutputScale)))
      return {
        cssWidth,
        cssMinHeight: cssHeight,
        cssMaxHeight: Math.max(cssHeight, Math.floor(12000 / outputScale)),
        outputScale
      }
    },
    createKnowledgeBaseDownloadTextElement(page) {
      const pageElement = document.createElement('div')
      pageElement.className = 'knowledge-text-page'
      const titleElement = document.createElement('h2')
      titleElement.textContent = this.getKnowledgeBasePageTitle(page)
      const imageUrl = page && page.imageUrl ? String(page.imageUrl).trim() : ''
      const remarkText = this.getKnowledgeBasePageRemarkDisplayText(page)
      const contentElement = document.createElement('div')
      contentElement.className = 'knowledge-text-content'
      const renderedContent = this.renderKnowledgeBaseMarkdownContent(page && page.content ? page.content : '')
      if (renderedContent) {
        contentElement.innerHTML = renderedContent
      } else if (!remarkText) {
        const emptyElement = document.createElement('p')
        emptyElement.textContent = '\u6682\u65e0\u5185\u5bb9'
        contentElement.appendChild(emptyElement)
      }
      Array.prototype.forEach.call(contentElement.querySelectorAll('img'), image => {
        image.removeAttribute('loading')
        image.setAttribute('decoding', 'sync')
      })
      pageElement.appendChild(titleElement)
      if (imageUrl) {
        const imageBlock = document.createElement('div')
        imageBlock.className = 'knowledge-text-image-block'
        const imageElement = document.createElement('img')
        imageElement.src = imageUrl
        imageElement.alt = ''
        imageElement.setAttribute('decoding', 'sync')
        imageBlock.appendChild(imageElement)
        pageElement.appendChild(imageBlock)
      }
      if (remarkText) {
        const remarkElement = document.createElement('div')
        remarkElement.className = 'knowledge-text-remark'
        remarkElement.textContent = remarkText
        pageElement.appendChild(remarkElement)
      }
      pageElement.appendChild(contentElement)
      return pageElement
    },
    getKnowledgeBaseDownloadTextCss(layout) {
      const width = Math.max(1, Math.round(Number(layout && layout.cssWidth) || 672))
      const minHeight = Math.max(1, Math.round(Number(layout && layout.cssMinHeight) || 896))
      const px = value => `${value}px`
      return `
.knowledge-text-download-host {
  width: ${width}px;
  min-height: ${minHeight}px;
  height: auto;
  background: #fff;
}

.knowledge-text-download-host .knowledge-text-page {
  width: ${width}px;
  min-height: ${minHeight}px;
  height: auto;
  padding: ${px(52)} ${px(56)};
  box-sizing: border-box;
  overflow: visible;
  background: #fff;
  color: #15191b;
  text-align: left;
  user-select: text;
  cursor: default;
  font-family: PingFangSC, 'PingFang SC', 'Helvetica Neue', Arial, sans-serif;
}

.knowledge-text-download-host .knowledge-text-page > h2 {
  margin: 0 0 ${px(26)};
  color: #15191b;
  font-size: ${px(30)};
  line-height: ${px(40)};
  font-weight: 700;
  word-break: break-word;
}

.knowledge-text-download-host .knowledge-text-image-block {
  width: 100%;
  margin: 0 0 ${px(18)};
  padding: 0 0 ${px(15)};
  border-bottom: ${px(1)} solid #dce2df;
  box-sizing: border-box;
}

.knowledge-text-download-host .knowledge-text-image-block img {
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: ${px(233)};
  border: ${px(1)} solid #dce2df;
  background: #fff;
  object-fit: contain;
}

.knowledge-text-download-host .knowledge-text-remark {
  margin: 0 0 ${px(20)};
  padding: ${px(4)} 0;
  box-sizing: border-box;
  min-height: ${px(40)};
  color: #303735;
  font-size: ${px(18)};
  line-height: ${px(32)};
  font-weight: 400;
  text-align: left;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.knowledge-text-download-host .knowledge-text-content {
  color: #303735;
  font-size: ${px(18)};
  line-height: ${px(32)};
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.knowledge-text-download-host .knowledge-text-content > :first-child {
  margin-top: 0;
}

.knowledge-text-download-host .knowledge-text-content > :last-child {
  margin-bottom: 0;
}

.knowledge-text-download-host .knowledge-text-content p {
  margin: 0 0 ${px(14)};
}

.knowledge-text-download-host .knowledge-text-content h1,
.knowledge-text-download-host .knowledge-text-content h2,
.knowledge-text-download-host .knowledge-text-content h3,
.knowledge-text-download-host .knowledge-text-content h4,
.knowledge-text-download-host .knowledge-text-content h5,
.knowledge-text-download-host .knowledge-text-content h6 {
  margin: ${px(22)} 0 ${px(12)};
  color: #15191b;
  font-weight: 700;
  line-height: 1.35;
}

.knowledge-text-download-host .knowledge-text-content h1 {
  font-size: ${px(28)};
}

.knowledge-text-download-host .knowledge-text-content h2 {
  font-size: ${px(24)};
}

.knowledge-text-download-host .knowledge-text-content h3 {
  font-size: ${px(21)};
}

.knowledge-text-download-host .knowledge-text-content h4,
.knowledge-text-download-host .knowledge-text-content h5,
.knowledge-text-download-host .knowledge-text-content h6 {
  font-size: ${px(18)};
}

.knowledge-text-download-host .knowledge-text-content ul,
.knowledge-text-download-host .knowledge-text-content ol {
  margin: 0 0 ${px(14)};
  padding-left: 1.45em;
  list-style-position: outside;
}

.knowledge-text-download-host .knowledge-text-content li {
  margin: 0 0 ${px(4)};
  padding-left: 0.1em;
}

.knowledge-text-download-host .knowledge-text-content li::marker {
  font-size: 1em;
}

.knowledge-text-download-host .knowledge-text-content li > ul,
.knowledge-text-download-host .knowledge-text-content li > ol {
  margin: ${px(4)} 0 ${px(4)};
}

.knowledge-text-download-host .knowledge-text-content blockquote {
  margin: 0 0 ${px(14)};
  padding: 0 0 0 ${px(16)};
  border-left: ${px(4)} solid #d6ddd8;
  color: #59605d;
}

.knowledge-text-download-host .knowledge-text-content blockquote > :last-child {
  margin-bottom: 0;
}

.knowledge-text-download-host .knowledge-text-content a {
  color: #2d6cdf;
  text-decoration: underline;
  text-underline-offset: ${px(2)};
}

.knowledge-text-download-host .knowledge-text-content code {
  padding: ${px(2)} ${px(5)};
  border-radius: ${px(4)};
  background: #f3f5f4;
  color: #202725;
  font-family: Consolas, Monaco, 'Courier New', monospace;
  font-size: 0.88em;
  line-height: 1.45;
}

.knowledge-text-download-host .knowledge-text-content pre {
  margin: 0 0 ${px(14)};
  padding: ${px(14)} ${px(16)};
  border: ${px(1)} solid #e1e6e3;
  border-radius: ${px(6)};
  background: #f6f8f7;
  overflow: auto;
  white-space: pre;
}

.knowledge-text-download-host .knowledge-text-content pre code {
  display: block;
  padding: 0;
  border-radius: 0;
  background: transparent;
  font-size: ${px(14)};
  line-height: ${px(24)};
  white-space: inherit;
}

.knowledge-text-download-host .knowledge-text-content table {
  display: block;
  width: 100%;
  max-width: 100%;
  margin: 0 0 ${px(16)};
  border-collapse: collapse;
  overflow-x: auto;
}

.knowledge-text-download-host .knowledge-text-content th,
.knowledge-text-download-host .knowledge-text-content td {
  border: ${px(1)} solid #dce2df;
  padding: ${px(8)} ${px(10)};
  vertical-align: top;
}

.knowledge-text-download-host .knowledge-text-content th {
  background: #f2f5f3;
  color: #15191b;
  font-weight: 700;
}

.knowledge-text-download-host .knowledge-text-content img {
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  margin: ${px(12)} 0;
  object-fit: contain;
}

.knowledge-text-download-host .knowledge-text-content hr {
  height: 0;
  margin: ${px(22)} 0;
  border: 0;
  border-top: ${px(1)} solid #dce2df;
}
`.trim()
    },
    async waitForKnowledgeBaseDownloadTextRender() {
      if (typeof window === 'undefined') {
        return
      }
      const requestFrame = typeof window.requestAnimationFrame == 'function'
        ? window.requestAnimationFrame.bind(window)
        : callback => window.setTimeout(callback, 16)
      await new Promise(resolve => {
        requestFrame(() => requestFrame(resolve))
      })
      if (typeof document !== 'undefined' && document.fonts && document.fonts.ready && typeof document.fonts.ready.then == 'function') {
        await Promise.race([
          document.fonts.ready.catch(() => null),
          new Promise(resolve => window.setTimeout(resolve, 120))
        ])
      }
    },
    async waitForKnowledgeBaseDownloadElementImages(element) {
      if (typeof window === 'undefined' || !element || typeof element.querySelectorAll != 'function') {
        return
      }
      const images = Array.prototype.slice.call(element.querySelectorAll('img'))
      if (images.length == 0) {
        return
      }
      await Promise.all(images.map(image => {
        if (image.complete && image.naturalWidth > 0) {
          return Promise.resolve()
        }
        return new Promise(resolve => {
          let done = false
          const finish = () => {
            if (done) {
              return
            }
            done = true
            image.onload = null
            image.onerror = null
            resolve()
          }
          image.onload = finish
          image.onerror = finish
          window.setTimeout(finish, 2000)
        })
      }))
    },
    serializeKnowledgeBaseDownloadTextElement(pageElement, styleElement, width, height) {
      const holder = document.createElement('div')
      holder.className = 'knowledge-text-download-host'
      holder.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml')
      holder.appendChild(styleElement.cloneNode(true))
      holder.appendChild(pageElement.cloneNode(true))
      const markup = new XMLSerializer().serializeToString(holder)
      return [
        `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
        `<foreignObject x="0" y="0" width="${width}" height="${height}">`,
        markup,
        '</foreignObject>',
        '</svg>'
      ].join('')
    },
    createKnowledgeBaseSvgCanvas(svg, cssWidth, cssHeight, outputScale = 1) {
      return new Promise((resolve, reject) => {
        const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const image = new Image()
        image.onload = () => {
          try {
            const safeScale = Math.max(1, Number(outputScale) || 1)
            const width = Math.max(1, Math.round(cssWidth * safeScale))
            const height = Math.max(1, Math.round(cssHeight * safeScale))
            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height
            const context = canvas.getContext('2d')
            if (!context) {
              reject(new Error('Canvas context unavailable'))
              return
            }
            if ('imageSmoothingEnabled' in context) {
              context.imageSmoothingEnabled = true
              if ('imageSmoothingQuality' in context) {
                context.imageSmoothingQuality = 'high'
              }
            }
            context.fillStyle = '#fff'
            context.fillRect(0, 0, width, height)
            context.save()
            context.scale(safeScale, safeScale)
            context.drawImage(image, 0, 0, cssWidth, cssHeight)
            context.restore()
            try {
              context.getImageData(0, 0, 1, 1)
            } catch (error) {
              canvas.width = 0
              canvas.height = 0
              reject(error)
              return
            }
            resolve(canvas)
          } catch (error) {
            reject(error)
          } finally {
            URL.revokeObjectURL(url)
          }
        }
        image.onerror = () => {
          URL.revokeObjectURL(url)
          reject(new Error('Text render failed'))
        }
        image.src = url
      })
    },
    createKnowledgeBaseMarkdownCanvas(page, options = {}) {
      const fallbackPage = Object.assign({}, page, {
        content: this.getKnowledgeBaseRenderedFallbackText(page && page.content ? page.content : '')
      })
      return this.createKnowledgeBasePlainTextCanvas(fallbackPage, options)
    },
    getKnowledgeBaseRenderedFallbackText(value) {
      const html = this.renderKnowledgeBaseMarkdownContent(value)
      if (!html || typeof document === 'undefined') {
        return this.getKnowledgeBasePlainText(value)
      }
      const element = document.createElement('div')
      element.innerHTML = html
      const readNode = (node, state = {}) => {
        if (!node) {
          return ''
        }
        if (node.nodeType == 3) {
          return node.nodeValue || ''
        }
        if (node.nodeType != 1) {
          return ''
        }
        const tag = String(node.tagName || '').toLowerCase()
        if (tag == 'br') {
          return '\n'
        }
        if (tag == 'hr') {
          return '\n\n'
        }
        if (tag == 'img') {
          return node.getAttribute('alt') || ''
        }
        if (tag == 'li') {
          const marker = state.ordered ? `${state.index || 1}. ` : '\u2022 '
          const text = Array.prototype.map.call(node.childNodes, child => readNode(child, state)).join('').trim()
          return `${marker}${text}\n`
        }
        if (tag == 'ol' || tag == 'ul') {
          let index = 1
          const text = Array.prototype.map.call(node.childNodes, child => {
            const childText = readNode(child, { ordered: tag == 'ol', index })
            if (child && child.nodeType == 1 && String(child.tagName || '').toLowerCase() == 'li') {
              index += 1
            }
            return childText
          }).join('')
          return `${text}\n`
        }
        if (tag == 'tr') {
          return `${Array.prototype.map.call(node.children, child => readNode(child, state).trim()).join('\t')}\n`
        }
        if (tag == 'table') {
          return `${Array.prototype.map.call(node.children, child => readNode(child, state)).join('')}\n`
        }
        const text = Array.prototype.map.call(node.childNodes, child => readNode(child, state)).join('')
        if (/^(p|h1|h2|h3|h4|h5|h6|blockquote|pre)$/.test(tag)) {
          return `${text.trim()}\n\n`
        }
        return text
      }
      return Array.prototype.map.call(element.childNodes, child => readNode(child)).join('').replace(/\n{3,}/g, '\n\n').trim()
    },
    createKnowledgeBasePlainTextCanvas(page, options = {}) {
      const thumbnail = !!options.thumbnail
      const width = thumbnail ? 740 : 1240
      const minHeight = thumbnail ? 990 : 1754
      const maxHeight = thumbnail ? 4096 : 30000
      const padding = thumbnail ? 54 : 88
      const titleSize = thumbnail ? 38 : 44
      const contentSize = thumbnail ? 26 : 30
      const titleLineHeight = thumbnail ? 48 : 58
      const contentLineHeight = thumbnail ? 40 : 48
      const title = this.getKnowledgeBasePageTitle(page)
      const content = this.getKnowledgeBasePlainText(page && page.content ? page.content : '')
      const measureCanvas = document.createElement('canvas')
      const measureContext = measureCanvas.getContext('2d')
      if (!measureContext) {
        const fallbackCanvas = document.createElement('canvas')
        fallbackCanvas.width = width
        fallbackCanvas.height = minHeight
        const fallbackContext = fallbackCanvas.getContext('2d')
        if (fallbackContext) {
          fallbackContext.fillStyle = '#fff'
          fallbackContext.fillRect(0, 0, width, minHeight)
        }
        return fallbackCanvas
      }
      const maxWidth = width - padding * 2
      measureContext.font = `700 ${titleSize}px Arial`
      const titleLines = this.wrapKnowledgeBaseCanvasText(measureContext, title, maxWidth)
      measureContext.font = `400 ${contentSize}px Arial`
      const contentLines = this.wrapKnowledgeBaseParagraphs(measureContext, content || '\u6682\u65e0\u5185\u5bb9', maxWidth)
      const calculatedHeight = padding + titleLines.length * titleLineHeight + 36 + contentLines.length * contentLineHeight + padding
      const desiredHeight = thumbnail ? minHeight : Math.max(minHeight, calculatedHeight)
      const height = Math.max(minHeight, Math.min(maxHeight, desiredHeight))
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const context = canvas.getContext('2d')
      if (!context) {
        return canvas
      }
      context.fillStyle = '#fff'
      context.fillRect(0, 0, width, height)
      context.fillStyle = '#15191b'
      context.font = `700 ${titleSize}px Arial`
      context.textBaseline = 'top'
      let y = padding
      titleLines.forEach(line => {
        context.fillText(line, padding, y)
        y += titleLineHeight
      })
      y += 26
      context.fillStyle = '#303735'
      context.font = `400 ${contentSize}px Arial`
      const maxTextY = height - padding - contentLineHeight
      let contentClipped = false
      for (let i = 0; i < contentLines.length; i++) {
        const line = contentLines[i]
        if (y > maxTextY) {
          contentClipped = true
          break
        }
        context.fillText(line, padding, y)
        y += contentLineHeight
      }
      if (contentClipped) {
        context.fillText('...', padding, maxTextY)
      }
      return canvas
    },
    wrapKnowledgeBaseParagraphs(context, text, maxWidth) {
      const paragraphs = String(text || '').replace(/\r/g, '').split('\n')
      const lines = []
      paragraphs.forEach((paragraph, index) => {
        const wrapped = this.wrapKnowledgeBaseCanvasText(context, paragraph || ' ', maxWidth)
        lines.push(...wrapped)
        if (index < paragraphs.length - 1) {
          lines.push(' ')
        }
      })
      return lines
    },
    wrapKnowledgeBaseCanvasText(context, text, maxWidth) {
      const source = String(text || '')
      const lines = []
      let line = ''
      const pushLine = value => {
        if (value || lines.length == 0) {
          lines.push(value)
        }
      }
      for (let i = 0; i < source.length; i++) {
        const char = source[i]
        const next = line + char
        if (line && context.measureText(next).width > maxWidth) {
          pushLine(line)
          line = char
        } else {
          line = next
        }
      }
      pushLine(line)
      return lines
    },
    async downloadUrl(url, fileName) {
      if (url && !String(url).startsWith('mock://')) {
        const safeName = this.sanitizeFileName(fileName) || this.getUrlDownloadName(url, this.paperDetailTitle)
        try {
          const response = await fetch(url)
          if (!response.ok) {
            throw new Error(`Download failed: ${response.status}`)
          }
          const blob = await response.blob()
          saveAs(blob, safeName)
        } catch (error) {
          const link = document.createElement('a')
          link.href = url
          link.download = safeName
          link.target = '_blank'
          link.rel = 'noopener'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
        return
      }
      this.fakeAction(this.ui.download)
    },
    getCurrentPageDownloadName() {
      const pageNo = this.currentPage && this.currentPage.pageNo ? this.currentPage.pageNo : this.currentPageIndex + 1
      const extension = this.getUrlExtension(this.currentPage && this.currentPage.imageUrl, 'png')
      const baseName = this.$getDownloadBaseName(this.paperDetailTitle, this.ui.download, this.sanitizeFileName)
      return `${baseName}-\u7b2c${pageNo}\u9875.${extension}`
    },
    getRecordingDownloadName(recording) {
      const name = recording && recording.recordingName ? recording.recordingName : this.ui.recordings
      const extension = this.getUrlExtension(recording && recording.fileUrl, 'mp4')
      return this.$getDownloadFileName(name, extension, this.ui.recordings, this.sanitizeFileName)
    },
    getUrlDownloadName(url, fallbackName) {
      const extension = this.getUrlExtension(url, '')
      const name = this.$getDownloadBaseName(fallbackName, this.ui.download, this.sanitizeFileName)
      return extension ? `${name}.${extension}` : name
    },
    getUrlExtension(url, fallback = '') {
      const cleanPath = String(url || '').split(/[?#]/)[0]
      const fileName = cleanPath.split(/[\\/]/).pop() || ''
      const dotIndex = fileName.lastIndexOf('.')
      if (dotIndex < 0 || dotIndex == fileName.length - 1) {
        return fallback
      }
      return fileName.slice(dotIndex + 1).toLowerCase()
    },
    sanitizeFileName(name) {
      return String(name || '')
        .replace(/[\\/:*?"<>|]/g, '_')
        .replace(/\s+/g, ' ')
        .trim()
    },
    fakeAction() {
      this.moreMenuId = ''
      this.$message.info(this.ui.fakeTip)
    },
    markCoverFailed(item) {
      if (item) {
        this.$set(item, 'coverFailed', true)
      }
    },
    shouldDisplayCover(item) {
      return !!(item && item.cover && !item.coverFailed && !item.isFolder && !item.encrypted)
    },
    shouldDisplayGridCover(item) {
      return !!(item && this.getPaperGridCoverImageUrl(item) && !item.isFolder && !item.encrypted)
    },
    getFallbackCover(item, mode = this.viewMode) {
      const isGrid = mode == 'grid'
      if (this.isKnowledgeBaseResourceTypeOneOrTwo(item)) {
        return this.paperIcons.resourceType12
      }
      if (this.isKnowledgeBaseResourceType3(item)) {
        return this.paperIcons.resourceType3
      }
      if (item && item.encrypted) {
        return isGrid ? this.paperIcons.fileGridEncrypt : this.paperIcons.fileListEncrypt
      }
      if (item && item.isFolder) {
        const hasChildren = this.getFolderChildSize(item) > 0
        if (item.template) {
          if (isGrid) {
            return hasChildren ? this.paperIcons.templateGridFilled : this.paperIcons.templateGridEmpty
          }
          return hasChildren ? this.paperIcons.templateListFilled : this.paperIcons.templateListEmpty
        }
        if (isGrid) {
          return hasChildren ? this.paperIcons.folderGridFilled : this.paperIcons.folderGridEmpty
        }
        return hasChildren ? this.paperIcons.folderListFilled : this.paperIcons.folderListEmpty
      }
      const extension = this.getPaperFileExtension(item)
      const pdfIcon = isGrid ? this.paperIcons.fileGridPdf : this.paperIcons.fileListPdf
      const paperIcon = isGrid ? this.paperIcons.fileGridPaper : this.paperIcons.fileListPaper
      return extension == 'pdf' ? pdfIcon : paperIcon
    },
    isKnowledgeBaseResourceTypeOneOrTwo(item) {
      if (!item) {
        return false
      }
      const resourceType = Number(item.resourceType)
      return resourceType === 1 || resourceType === 2
    },
    isKnowledgeBaseResourceType3(item) {
      return !!(item && Number(item.resourceType) === 3)
    },
    isEmptyKnowledgeBaseVisualNote(item) {
      return !!(item && Number(item.resourceType) === 2 && Number(item.childSize) === 0)
    },
    isKnowledgeBaseNoPagesDownloadError(error) {
      const message = typeof error == 'string' ? error : (error && error.message)
      return String(message || '').trim().toLowerCase() == 'no pages'
    },
    getPaperFileExtension(item) {
      const rawPath = item && item.filePath ? String(item.filePath).trim() : ''
      const cleanPath = rawPath.split(/[?#]/)[0]
      const fileName = cleanPath.split(/[\\/]/).pop() || ''
      const dotIndex = fileName.lastIndexOf('.')
      if (dotIndex < 0 || dotIndex == fileName.length - 1) {
        return ''
      }
      return fileName.slice(dotIndex + 1).toLowerCase()
    },
    getFolderChildSize(item) {
      const value = item && item.childSize !== undefined && item.childSize !== null && item.childSize !== ''
        ? Number(item.childSize)
        : 0
      return Number.isFinite(value) ? value : 0
    },
    formatPageCount(item) {
      return this.formatResourceCountLabel(item, {
        language: this.language,
        itemUnit: this.ui.item,
        pageUnit: this.ui.page,
        emptyText: '-'
      })
    },
    formatDate(value) {
      const time = this.parseDateTime(value)
      if (!this.isDisplayableDateTime(time)) {
        return '-'
      }
      const date = new Date(time)
      const month = `${date.getMonth() + 1}`.padStart(2, '0')
      const day = `${date.getDate()}`.padStart(2, '0')
      return `${date.getFullYear()}/${month}/${day}`
    },
    isDisplayableDateTime(time) {
      if (!time) {
        return false
      }
      const date = new Date(time)
      return Number.isFinite(date.getTime()) && date.getFullYear() >= 2000
    },
    formatDateTime(value) {
      const time = this.parseDateTime(value)
      if (!time) {
        return '-'
      }
      const date = new Date(time)
      const month = `${date.getMonth() + 1}`.padStart(2, '0')
      const day = `${date.getDate()}`.padStart(2, '0')
      const hour = `${date.getHours()}`.padStart(2, '0')
      const minute = `${date.getMinutes()}`.padStart(2, '0')
      return `${date.getFullYear()}/${month}/${day} ${hour}:${minute}`
    },
    formatDuration(value) {
      const ms = Number(value) || 0
      const totalSeconds = Math.max(0, Math.floor(ms / 1000))
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = `${totalSeconds % 60}`.padStart(2, '0')
      return `${minutes}:${seconds}`
    }
  }
}
</script>

<style scoped>
.paper-workspace {
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  background: #f3f2ed;
  color: #181d1f;
  overflow: hidden;
  font-family: PingFangSC, PingFang SC, Helvetica Neue, Arial, sans-serif;
}

.paper-list-header,
.paper-detail-header {
  height: 64px;
  border-bottom: 1px solid #dededb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px 0 32px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.paper-list-header {
  height: 72px;
}

.paper-breadcrumb {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0;
  color: #202428;
  overflow: hidden;
}

.paper-breadcrumb-root {
  border: 0;
  background: transparent;
  padding: 0;
  color: #202428;
  font-size: 24px;
  line-height: 34px;
  font-weight: 700;
  cursor: pointer;
  flex: 0 0 auto;
}

.paper-breadcrumb-segment {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  flex: 0 1 auto;
}

.paper-breadcrumb-segment:not(:last-child) {
  flex: 0 0 auto;
}

.paper-breadcrumb-link {
  max-width: 160px;
  min-width: 0;
  border: 0;
  background: transparent;
  padding: 0;
  color: #1f2326;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-breadcrumb-separator {
  width: 32px;
  height: 32px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.paper-breadcrumb strong {
  min-width: 0;
  max-width: 240px;
  color: #1f2326;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-header-actions,
.paper-detail-actions {
  display: flex;
  align-items: center;
  gap: 24px;
}

.paper-icon-button,
.paper-tool-button,
.paper-order-button,
.paper-back-button,
.paper-filter-button,
.paper-sort-button,
.paper-view-hotspot,
.paper-more-button,
.paper-favorite,
.record-action,
.record-text-head button,
.paper-page-controls button {
  font: inherit;
}

.paper-icon-button,
.paper-tool-button {
  width: 36px;
  height: 36px;
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.paper-icon-button img {
  width: 24px;
  height: 24px;
  object-fit: contain;
  display: block;
}

.paper-import-button {
  width: 128px;
  height: 44px;
  border: 0;
  background: #1f3a2d;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  padding: 0;
}

.paper-import-button img {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.paper-toolbar {
  height: 78px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 22px 32px 0;
  box-sizing: border-box;
  flex-shrink: 0;
}

.paper-filter,
.paper-sort {
  position: relative;
}

.paper-filter-button,
.paper-sort-button {
  height: 36px;
  border: 1px solid #c8c8c5;
  background: transparent;
  color: #202428;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
}

.paper-filter-button {
  width: 138px;
  gap: 10px;
  padding: 0 12px;
}

.paper-sort-button {
  min-width: 88px;
  justify-content: center;
  padding: 0 14px;
}

.paper-filter-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
  flex: 0 0 22px;
  display: block;
}

.filter-arrow {
  width: 9px;
  height: 9px;
  border-right: 1px solid #111;
  border-bottom: 1px solid #111;
  transform: rotate(45deg) translateY(-3px);
  margin-left: auto;
}

.filter-arrow.open {
  transform: rotate(225deg) translate(-2px, -1px);
}

.paper-filter-menu,
.paper-sort-menu {
  position: absolute;
  top: 40px;
  left: auto;
  right: 0;
  z-index: 30;
  min-width: 130px;
  width: max-content;
  max-width: calc(100vw - 24px);
  background: #fff;
  border: 1px solid #d8d8d8;
  box-shadow: 0 4px 12px rgba(0, 0, 0, .04);
}

.paper-sort-menu {
  left: auto;
  right: 0;
}

.paper-filter-menu button,
.paper-sort-menu button {
  width: max-content;
  min-width: 100%;
  height: 40px;
  border: 0;
  background: #fff;
  color: #171b1d;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 17px;
  cursor: pointer;
  text-align: left;
  font-size: 14px;
  white-space: nowrap;
}

.paper-filter-menu button.active,
.paper-sort-menu button.active {
  background: #dce2d8;
}

.paper-menu-icon {
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  object-fit: contain;
  display: block;
}

.filter-check {
  width: 20px;
  height: 20px;
  object-fit: contain;
  margin-left: auto;
}

.paper-toolbar-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.paper-order-button {
  width: 36px;
  height: 36px;
  border: 1px solid #c8c8c5;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.paper-order-button img {
  width: 24px;
  height: 24px;
  object-fit: contain;
  display: block;
}

.paper-view-switch {
  width: 72px;
  height: 36px;
  position: relative;
  flex: 0 0 72px;
}

.paper-view-switch img {
  width: 72px;
  height: 36px;
  display: block;
  object-fit: contain;
}

.paper-view-hotspot {
  position: absolute;
  top: 0;
  width: 36px;
  height: 36px;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

.paper-view-list {
  left: 0;
}

.paper-view-grid {
  right: 0;
}

.paper-selection-layer {
  position: absolute;
  top: 72px;
  right: 0;
  left: 0;
  z-index: 40;
  padding: 12px 12px 10px;
  box-sizing: border-box;
  pointer-events: none;
  will-change: transform;
}

.paper-selection-layer.dragging {
  user-select: none;
}

.paper-selection-bar {
  height: 48px;
  width: 100%;
  min-width: 0;
  padding: 0 16px;
  background: #DDE4D9;
  color: #333;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 18px;
  box-sizing: border-box;
  flex-shrink: 0;
  overflow-x: auto;
  overflow-y: hidden;
  pointer-events: auto;
  cursor: grab;
  scrollbar-width: thin;
}

.paper-selection-layer.dragging .paper-selection-bar {
  cursor: grabbing;
}

.paper-selection-close {
  width: 24px;
  height: 24px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  position: relative;
}

.paper-selection-close span {
  width: 14px;
  height: 14px;
  display: block;
  position: relative;
}

.paper-selection-close span::before,
.paper-selection-close span::after {
  content: '';
  width: 14px;
  height: 1.5px;
  background: #111;
  position: absolute;
  left: 0;
  top: 6px;
}

.paper-selection-close span::before {
  transform: rotate(45deg);
}

.paper-selection-close span::after {
  transform: rotate(-45deg);
}

.paper-selection-bar > span {
  color: #606462;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  flex: 0 0 auto;
  white-space: nowrap;
}

.paper-selection-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  flex-wrap: nowrap;
  gap: 34px;
}

.paper-selection-actions button {
  height: 32px;
  border: 0;
  background: transparent;
  padding: 0;
  color: #1f2326;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  white-space: nowrap;
}

.paper-selection-actions button:disabled {
  display: none;
  color: #9da39f;
  cursor: not-allowed;
  opacity: .45;
}

.paper-selection-actions img {
  width: 18px;
  height: 18px;
  object-fit: contain;
  display: block;
}

.paper-search-line {
  height: 46px;
  margin: -10px 32px 16px;
  border: 1px solid #d7d9d6;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.paper-search-line img {
  width: 22px;
  height: 22px;
}

.paper-search-line input {
  min-width: 0;
  flex: 1;
  height: 44px;
  border: 0;
  outline: none;
  background: transparent;
  color: #202428;
  font-size: 15px;
}

.paper-search-line button {
  width: 18px;
  height: 18px;
  border: 0;
  background: transparent;
  position: relative;
  cursor: pointer;
}

.paper-search-line button::before,
.paper-search-line button::after {
  content: "";
  width: 16px;
  height: 1px;
  background: #777;
  position: absolute;
  left: 1px;
  top: 8px;
}

.paper-search-line button::before {
  transform: rotate(45deg);
}

.paper-search-line button::after {
  transform: rotate(-45deg);
}

.paper-list-area {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0 24px 40px 32px;
  box-sizing: border-box;
}

.paper-list-area /deep/ .ant-spin-nested-loading,
.paper-list-area /deep/ .ant-spin-container {
  min-height: calc(100vh - 150px);
}

.paper-list-area /deep/ .paper-list-spin.ant-spin-nested-loading {
  min-height: calc(100vh - 150px);
}

.paper-list-content {
  min-height: calc(100vh - 150px);
}

.paper-list-area.is-grid .paper-list-content {
  min-width: calc(148px * 4 + 15px * 3);
  padding-top: 7.5px;
  box-sizing: border-box;
}

.paper-list-area /deep/ .paper-list-spin.ant-spin-nested-loading > div > .ant-spin {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: auto;
  max-height: none;
  padding-top: 24px;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  color: #275B52;
}

.paper-list-area /deep/ .paper-list-spin.ant-spin-nested-loading > div > .ant-spin .ant-spin-dot {
  position: static;
  margin: 0;
}

.paper-list-area /deep/ .paper-list-spin .ant-spin-dot-item {
  background-color: #275B52;
}

.paper-empty {
  min-height: calc(100vh - 180px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: #15191b;
  font-size: 14px;
  gap: 12px;
}

.paper-empty img {
  width: 130px;
  height: auto;
}

.paper-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 148px));
  gap: 29.5px 15px;
  align-items: start;
  min-width: calc(148px * 4 + 15px * 3);
}

.paper-card {
  width: 148px;
  height: 245.4px;
  min-height: 245.4px;
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  outline: none;
  z-index: 0;
  --paper-card-height: 245.4px;
  --paper-grid-column-gap: 15px;
  --paper-grid-row-gap: 44px;
  --paper-grid-column-gap-half: calc(var(--paper-grid-column-gap) / 2);
  --paper-grid-row-gap-half: calc(var(--paper-grid-row-gap) / 2);
}

.paper-card.selected {
  z-index: 1;
}

.paper-card.selected::before {
  content: '';
  position: absolute;
  top: calc(-1 * var(--paper-grid-column-gap-half));
  right: calc(-1 * var(--paper-grid-column-gap-half));
  bottom: calc(-1 * var(--paper-grid-row-gap-half));
  left: calc(-1 * var(--paper-grid-column-gap-half));
  background: #DDE4D9;
  pointer-events: none;
  z-index: 0;
}

.paper-card.selected.grid-edge-bottom::before {
  bottom: 0;
}

.paper-card.selected.grid-edge-top::before {
  top: calc(-1 * var(--paper-grid-column-gap-half));
}

.paper-card > * {
  position: relative;
  z-index: 1;
}

.paper-card.selected .paper-cover,
.paper-card:hover .paper-cover,
.paper-card:focus-visible .paper-cover {
  border-color: #b6b8b4;
  background: #f8f8f6;
}

.paper-card.selected .paper-cover {
  background: #fff;
}

.paper-card.selected .paper-cover.template {
  border-style: solid;
}

.paper-cover {
  width: 146px;
  height: 194px;
  flex: 0 0 194px;
  margin: 0 auto;
  border: 1px solid #d7d9d6;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  overflow: hidden;
}

.paper-cover-image {
  width: 100%;
  max-width: 100%;
  height: auto;
  max-height: 100%;
  border: 0;
  background: transparent;
  box-sizing: border-box;
  object-fit: contain;
  object-position: center center;
  display: block;
}

.paper-cover-default {
  width: 46px;
  height: auto;
  object-fit: contain;
  display: block;
}

.paper-cover-default.folder-icon {
  width: 46px;
  height: auto;
}

.paper-cover-default.encrypt-icon {
  width: 46px;
  height: auto;
}

.paper-cover.template {
  border-style: solid;
}

.paper-card-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  height: 22px;
  flex: 0 0 22px;
  box-sizing: border-box;
  margin-top: 8px;
  min-width: 0;
}

.paper-card-name {
  display: block;
  min-width: 0;
  flex: 1 1 auto;
  height: 22px;
  color: #0f1112;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-card-meta {
  height: 19px;
  flex: 0 0 19px;
  margin-top: 0;
  color: #9a9a9a;
  font-size: 13px;
  line-height: 19px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.paper-favorite {
  width: 30px;
  height: 30px;
  border: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.paper-card .paper-favorite {
  position: absolute;
  right: 0;
  bottom: 17px;
  justify-content: flex-end;
  z-index: 2;
}

.paper-favorite img {
  width: 18px;
  height: 18px;
  object-fit: contain;
  display: block;
}

.paper-favorite-status {
  width: 18px;
  height: 18px;
  object-fit: contain;
  display: block;
}

.paper-card-favorite-status {
  flex: 0 0 18px;
}

.paper-table {
  margin: 0 -12px 0 -20px;
}

.paper-row {
  min-height: 78px;
  border-bottom: 1px solid #dededb;
  display: grid;
  grid-template-columns: 66px minmax(220px, 1fr) 120px 48px 170px 48px 52px 48px 56px;
  align-items: center;
  gap: 0;
  padding: 0 16px;
  box-sizing: border-box;
  cursor: pointer;
  outline: none;
}

.paper-row:hover,
.paper-row.selected,
.paper-row:focus-visible {
  background: #f1eee8;
}

.paper-row-cover {
  width: 54px;
  height: 54px;
  border: 1px solid transparent;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.paper-row-cover.has-cover {
  width: 46px;
  height: 62px;
  border-color: #d7d9d6;
  background: #fff;
  box-sizing: border-box;
}

.paper-row-cover-image {
  width: 100%;
  max-width: 100%;
  height: auto;
  max-height: 100%;
  border: 0;
  background: transparent;
  box-sizing: border-box;
  object-fit: contain;
  object-position: center center;
  display: block;
}

.paper-row-cover-default {
  width: 46px;
  height: auto;
  object-fit: contain;
  display: block;
}

.paper-row-cover-default.folder-icon {
  width: 46px;
  height: auto;
}

.paper-row-cover-default.encrypt-icon {
  width: 46px;
  height: auto;
}

.paper-row strong {
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 78px;
  min-width: 0;
  color: #121618;
  font-size: 15px;
  line-height: 22px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-row > span:not(.paper-row-cover):not(.paper-more-wrap) {
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 78px;
  color: #7d8284;
  font-size: 14px;
  line-height: 22px;
  text-align: center;
}

.paper-row-count {
  grid-column: 3;
}

.paper-row-date {
  grid-column: 5;
}

.paper-more-wrap {
  align-self: center;
  grid-column: 9;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 78px;
}

.paper-row .paper-favorite {
  align-self: center;
  grid-column: 7;
  justify-self: center;
  height: 78px;
}

.paper-row-favorite-status {
  grid-column: 7;
}

.paper-more-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 48px;
  border: 0;
  background: transparent;
  color: #7B7C87;
  font-size: 0;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}

.paper-more-icon {
  display: block;
  flex: 0 0 12px;
  width: 12px;
  min-width: 12px;
  max-width: 12px;
  height: 4px;
  min-height: 4px;
  max-height: 4px;
  fill: #7B7C87;
  overflow: hidden;
  pointer-events: none;
  shape-rendering: auto;
}

.paper-row:hover .paper-more-button,
.paper-more-button:focus-visible {
  background: #F1EEE8;
}

.paper-more-menu {
  width: 168px;
  position: absolute;
  right: 16px;
  top: 47px;
  z-index: 50;
  border: 1px solid #d8d8d8;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, .04);
  padding: 5px 0;
}

.paper-more-menu button {
  width: 100%;
  height: 48px;
  border: 0;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 18px;
  color: #171b1d;
  font-size: 14px;
  cursor: pointer;
  text-align: left;
}

.paper-more-menu button:hover {
  background: #F1EEE8;
}

.paper-more-menu button:disabled {
  display: none;
  color: #9da39f;
  cursor: not-allowed;
  opacity: .45;
}

.paper-more-menu button:disabled:hover {
  background: #fff;
}

.paper-more-menu img {
  width: 17px;
  height: 17px;
  flex: 0 0 17px;
  object-fit: contain;
  display: block;
}

.page-fallback {
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #183d2d;
  font-size: 13px;
  font-weight: 600;
}

.paper-preview-pdf {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
  background: #fff;
}

.paper-detail-title {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 14px;
}

.paper-back-button {
  width: 26px;
  height: 36px;
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.paper-back-button span,
.record-text-head button span {
  width: 12px;
  height: 12px;
  border-left: 2px solid #111;
  border-bottom: 2px solid #111;
  transform: rotate(45deg);
  display: block;
}

.paper-detail-title strong {
  min-width: 0;
  color: #14191b;
  font-size: 22px;
  line-height: 32px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-tool-button img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.paper-tool-button.active {
  background: #ebe8e2;
}

.paper-tool-button:disabled {
  opacity: .35;
  cursor: default;
}

.paper-back-button:disabled {
  opacity: .35;
  cursor: default;
}

.paper-detail-body {
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
  --paper-preview-base-height: max(480px, calc(100vh - 64px));
  --paper-preview-base-width: calc(var(--paper-preview-base-height) * 0.75);
}

.paper-detail-body.is-resolving {
  cursor: progress;
}

.paper-detail-body.is-resolving::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 30;
  cursor: progress;
}

.paper-detail-body /deep/ .ant-spin-nested-loading,
.paper-detail-body /deep/ .ant-spin-container {
  height: 100%;
}

.paper-preview-shell {
  height: calc(100vh - 64px);
  overflow: auto;
  display: block;
  padding: 0 96px 0 32px;
  box-sizing: border-box;
  --paper-preview-zoom: 1;
  cursor: grab;
  user-select: none;
}

.paper-detail-resolving {
  height: calc(100vh - 64px);
  padding: 0 96px 0 32px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
}

.paper-detail-empty {
  height: calc(100vh - 64px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  color: #8c8f8c;
  font-size: 14px;
  line-height: 22px;
}

.paper-detail-empty img,
.paper-preview-empty img {
  width: 130px;
  height: auto;
  object-fit: contain;
  display: block;
}

.paper-detail-empty p,
.paper-preview-empty p {
  margin: 0;
}

.paper-detail-progress-box {
  width: var(--paper-preview-base-width);
  flex: 0 0 var(--paper-preview-base-width);
  margin: 0 auto;
  color: #15191b;
}

.paper-detail-progress-box strong,
.paper-detail-progress-box span {
  display: block;
  text-align: center;
}

.paper-detail-progress-box strong {
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
}

.paper-detail-progress-box span {
  margin-top: 10px;
  color: #59605d;
  font-size: 16px;
  line-height: 22px;
}

.paper-detail-progress-track {
  width: 100%;
  height: 8px;
  margin-top: 18px;
  background: #d8ddd8;
  overflow: hidden;
}

.paper-detail-progress-track i {
  display: block;
  height: 100%;
  width: 0;
  background: #1f3a2d;
  transition: width .18s ease;
}

.paper-detail-progress-box p {
  margin: 14px 0 0;
  color: #7a3b33;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
}

.paper-detail-progress-box button {
  width: 96px;
  height: 40px;
  border: 0;
  background: #1f3a2d;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px auto 0;
  padding: 0;
  font-size: 15px;
  line-height: 20px;
  cursor: pointer;
}

.paper-preview-shell.dragging {
  cursor: grabbing;
}

.paper-preview-zoom {
  width: calc(var(--paper-preview-base-width) * var(--paper-preview-zoom));
  height: calc(var(--paper-preview-base-height) * var(--paper-preview-zoom));
  margin: 0 auto;
}

.paper-preview-stage {
  width: var(--paper-preview-base-width);
  height: var(--paper-preview-base-height);
  margin-top: 0;
  background: #fff;
  border: 1px solid #d7d9d6;
  transform-origin: top left;
  transform: scale(var(--paper-preview-zoom));
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
}

.paper-preview-stage img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.paper-preview-empty {
  width: 100%;
  height: 100%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  color: #8c8f8c;
  font-size: 14px;
  line-height: 22px;
}

.knowledge-text-page {
  width: 100%;
  height: 100%;
  padding: 52px 56px;
  box-sizing: border-box;
  overflow-y: auto;
  background: #fff;
  color: #15191b;
  text-align: left;
  user-select: text;
  cursor: default;
}

.knowledge-text-page h2 {
  margin: 0 0 26px;
  color: #15191b;
  font-size: 30px;
  line-height: 40px;
  font-weight: 700;
  word-break: break-word;
}

.knowledge-text-image-block {
  width: 100%;
  margin: 0 0 18px;
  padding: 0 0 15px;
  border-bottom: 1px solid #dce2df;
  box-sizing: border-box;
}

.knowledge-text-image-block img {
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 233px;
  border: 1px solid #dce2df;
  background: #fff;
  object-fit: contain;
}

.knowledge-text-remark {
  margin: 0 0 20px;
  padding: 4px 0;
  box-sizing: border-box;
  min-height: 40px;
  color: #303735;
  font-size: 18px;
  line-height: 32px;
  font-weight: 400;
  text-align: left;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.knowledge-text-content {
  color: #303735;
  font-size: 18px;
  line-height: 32px;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.knowledge-text-content /deep/ > :first-child {
  margin-top: 0;
}

.knowledge-text-content /deep/ > :last-child {
  margin-bottom: 0;
}

.knowledge-text-content /deep/ p {
  margin: 0 0 14px;
}

.knowledge-text-content /deep/ h1,
.knowledge-text-content /deep/ h2,
.knowledge-text-content /deep/ h3,
.knowledge-text-content /deep/ h4,
.knowledge-text-content /deep/ h5,
.knowledge-text-content /deep/ h6 {
  margin: 22px 0 12px;
  color: #15191b;
  font-weight: 700;
  line-height: 1.35;
}

.knowledge-text-content /deep/ h1 {
  font-size: 28px;
}

.knowledge-text-content /deep/ h2 {
  font-size: 24px;
}

.knowledge-text-content /deep/ h3 {
  font-size: 21px;
}

.knowledge-text-content /deep/ h4,
.knowledge-text-content /deep/ h5,
.knowledge-text-content /deep/ h6 {
  font-size: 18px;
}

.knowledge-text-content /deep/ ul,
.knowledge-text-content /deep/ ol {
  margin: 0 0 14px;
  padding-left: 1.45em;
  list-style-position: outside;
}

.knowledge-text-content /deep/ li {
  margin: 0 0 4px;
  padding-left: 0.1em;
}

.knowledge-text-content /deep/ li::marker {
  font-size: 1em;
}

.knowledge-text-content /deep/ li > ul,
.knowledge-text-content /deep/ li > ol {
  margin: 4px 0 4px;
}

.knowledge-text-content /deep/ blockquote {
  margin: 0 0 14px;
  padding: 0 0 0 16px;
  border-left: 4px solid #d6ddd8;
  color: #59605d;
}

.knowledge-text-content /deep/ blockquote > :last-child {
  margin-bottom: 0;
}

.knowledge-text-content /deep/ a {
  color: #2d6cdf;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.knowledge-text-content /deep/ code {
  padding: 2px 5px;
  border-radius: 4px;
  background: #f3f5f4;
  color: #202725;
  font-family: Consolas, Monaco, 'Courier New', monospace;
  font-size: 0.88em;
  line-height: 1.45;
}

.knowledge-text-content /deep/ pre {
  margin: 0 0 14px;
  padding: 14px 16px;
  border: 1px solid #e1e6e3;
  border-radius: 6px;
  background: #f6f8f7;
  overflow: auto;
  white-space: pre;
}

.knowledge-text-content /deep/ pre code {
  display: block;
  padding: 0;
  border-radius: 0;
  background: transparent;
  font-size: 14px;
  line-height: 24px;
  white-space: inherit;
}

.knowledge-text-content /deep/ table {
  display: block;
  width: 100%;
  max-width: 100%;
  margin: 0 0 16px;
  border-collapse: collapse;
  overflow-x: auto;
}

.knowledge-text-content /deep/ th,
.knowledge-text-content /deep/ td {
  border: 1px solid #dce2df;
  padding: 8px 10px;
  vertical-align: top;
}

.knowledge-text-content /deep/ th {
  background: #f2f5f3;
  color: #15191b;
  font-weight: 700;
}

.knowledge-text-content /deep/ img {
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  margin: 12px 0;
  object-fit: contain;
}

.knowledge-text-content /deep/ hr {
  height: 0;
  margin: 22px 0;
  border: 0;
  border-top: 1px solid #dce2df;
}

.paper-pages-grid {
  height: calc(100vh - 64px);
  overflow: auto;
  padding: 20px 20px 44px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 148px));
  align-content: start;
  justify-content: start;
  gap: 29.5px 15px;
  min-width: calc(148px * 4 + 15px * 3);
  box-sizing: border-box;
}

.paper-pages-grid button {
  width: 148px;
  height: 245.4px;
  min-height: 245.4px;
  border: 0;
  background: transparent;
  padding: 0;
  text-align: left;
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  outline: none;
  z-index: 0;
}

.paper-pages-grid button.selected {
  z-index: 1;
}

.paper-pages-grid button.selected::before {
  content: '';
  position: absolute;
  top: -7.5px;
  right: -7.5px;
  bottom: -22px;
  left: -7.5px;
  background: #DDE4D9;
  pointer-events: none;
  z-index: 0;
}

.paper-pages-grid button.selected.grid-edge-top::before {
  top: -7.5px;
}

.paper-pages-grid button.selected:not(.grid-selected-row-start)::before {
  top: -7.5px;
}

.paper-pages-grid button.selected.grid-selection-continues-below::before {
  bottom: -22px;
}

.paper-pages-grid button > * {
  position: relative;
  z-index: 1;
}

.paper-pages-grid button span {
  width: 148px;
  height: 196.4px;
  flex: 0 0 196.4px;
  border: 1px solid #d7d9d6;
  background: #fff;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.paper-pages-grid button.selected span {
  border-color: #b6b8b4;
  background: #fff;
}

.paper-pages-grid button.active span {
  border-bottom: 5px solid #050505;
}

.paper-pages-grid img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.knowledge-page-thumb {
  width: 100%;
  height: 100%;
  padding: 16px 14px;
  box-sizing: border-box;
  background: #fff;
  color: #15191b;
  text-align: left;
  overflow: hidden;
}

.knowledge-page-thumb b {
  display: -webkit-box;
  overflow: hidden;
  color: #15191b;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  word-break: break-word;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.paper-pages-grid .knowledge-page-thumb-image {
  display: block;
  width: 100%;
  height: 43px;
  margin: 7px 0;
  border: 1px solid #dce2df;
  box-sizing: border-box;
  background: #fff;
  object-fit: contain;
}

.knowledge-page-thumb p {
  display: -webkit-box;
  overflow: hidden;
  margin: 12px 0 0;
  color: #4d5652;
  font-size: 12px;
  line-height: 18px;
  white-space: pre-wrap;
  word-break: break-word;
  -webkit-line-clamp: 7;
  -webkit-box-orient: vertical;
}

.knowledge-page-thumb.has-image p {
  margin-top: 7px;
  -webkit-line-clamp: 4;
}

.paper-pages-grid strong {
  display: block;
  width: 100%;
  height: 22px;
  flex: 0 0 22px;
  box-sizing: border-box;
  margin-top: 8px;
  color: #121618;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-page-controls {
  position: absolute;
  right: 24px;
  bottom: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.paper-page-controls button {
  width: 36px;
  height: 36px;
  border: 1px solid #d8d8d8;
  background: #f3f2ed;
  position: relative;
  cursor: pointer;
}

.paper-page-controls button::before {
  content: "";
  width: 9px;
  height: 9px;
  border-left: 1px solid #111;
  border-bottom: 1px solid #111;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-35%, -50%) rotate(45deg);
}

.paper-page-controls button:last-child::before {
  transform: translate(-65%, -50%) rotate(225deg);
}

.paper-page-controls button:disabled {
  background: #f3f2ed;
  cursor: default;
}

.paper-page-controls button:disabled::before {
  border-left-color: #b7bbb8;
  border-bottom-color: #b7bbb8;
}

.paper-page-count {
  display: flex;
  align-items: center;
  gap: 10px;
}

.paper-page-count span {
  min-width: 66px;
  height: 36px;
  border: 1px solid #d8d8d8;
  background: #f3f2ed;
  color: #5d6062;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  box-sizing: border-box;
}

.paper-password-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, .32);
  z-index: 100;
}

.paper-password-modal {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 372px;
  min-height: 180px;
  transform: translate(-50%, -50%);
  background: #fff;
  z-index: 101;
  padding: 22px 20px 20px;
  box-sizing: border-box;
  box-shadow: 0 2px 12px rgba(0, 0, 0, .12);
}

.paper-password-modal header {
  color: #1d1d1f;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  margin-bottom: 18px;
}

.paper-password-field {
  height: 35px;
  border: 1px solid #c9c9c9;
  display: flex;
  align-items: center;
  box-sizing: border-box;
}

.paper-password-field.error {
  border-color: #c85f58;
}

.paper-password-field input {
  min-width: 0;
  flex: 1;
  height: 33px;
  border: 0;
  outline: 0;
  padding: 0 10px;
  color: #1d1d1f;
  font-size: 14px;
  box-sizing: border-box;
}

.paper-password-field input::placeholder {
  color: #b9b9b9;
}

.paper-password-field button {
  width: 36px;
  height: 33px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.paper-password-field button:disabled {
  cursor: default;
  opacity: .55;
}

.paper-password-field button span {
  width: 18px;
  height: 11px;
  border: 2px solid #5b5f62;
  border-radius: 50%;
  position: relative;
  display: block;
  box-sizing: border-box;
}

.paper-password-field button span::before {
  content: "";
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #5b5f62;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.paper-password-field button span::after {
  content: "";
  width: 21px;
  height: 2px;
  background: #5b5f62;
  position: absolute;
  left: -3px;
  top: 3px;
  transform: rotate(-38deg);
}

.paper-password-field button span.visible::after {
  display: none;
}

.paper-password-error {
  min-height: 18px;
  margin: 6px 0 0;
  color: #c85f58;
  font-size: 12px;
  line-height: 18px;
}

.paper-password-modal footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0;
  margin-top: 32px;
}

.paper-password-modal footer > div {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.paper-password-forgot {
  display: none;
  color: #5e6b63;
  font-size: 13px;
  line-height: 20px;
}

.paper-password-modal footer > div button {
  min-width: 60px;
  height: 32px;
  border: 1px solid #d8d8d8;
  background: #fff;
  color: #333;
  font-size: 14px;
  cursor: pointer;
}

.paper-password-modal footer > div button:last-child {
  border-color: #1f3a2d;
  background: #1f3a2d;
  color: #fff;
}

.paper-password-modal footer > div button:disabled {
  cursor: default;
  opacity: .6;
}

.paper-download-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, .38);
  z-index: 80;
}

.paper-download-modal {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 474px;
  min-height: 223px;
  transform: translate(-50%, -50%);
  background: #fff;
  z-index: 81;
  padding: 24px 28px 26px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.paper-download-modal header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #111;
  font-size: 19px;
  line-height: 27px;
  font-weight: 500;
}

.paper-download-modal header button {
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.paper-download-modal header button span {
  width: 18px;
  height: 18px;
  position: relative;
  display: block;
}

.paper-download-modal header button span::before,
.paper-download-modal header button span::after {
  content: "";
  width: 18px;
  height: 2px;
  background: #111;
  position: absolute;
  left: 0;
  top: 8px;
}

.paper-download-modal header button span::before {
  transform: rotate(45deg);
}

.paper-download-modal header button span::after {
  transform: rotate(-45deg);
}

.paper-download-options {
  padding: 30px 0 38px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 44px;
}

.paper-download-options label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #111;
  font-size: 17px;
  line-height: 23px;
  cursor: pointer;
}

.paper-download-options input {
  width: 20px;
  height: 20px;
  accent-color: #111;
}

.paper-download-modal footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.paper-download-modal footer button {
  min-width: 80px;
  height: 40px;
  border: 1px solid #d8d8d8;
  background: #fff;
  color: #333;
  font-size: 15px;
  cursor: pointer;
}

.paper-download-modal footer button:last-child {
  border-color: #1f3a2d;
  background: #1f3a2d;
  color: #fff;
}

.paper-rename-mask {
  position: absolute;
  inset: 0;
  background: rgba(243, 242, 237, .58);
  z-index: 90;
}

.paper-rename-modal {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 508px;
  min-height: 222px;
  transform: translate(-50%, -50%);
  background: #fff;
  z-index: 91;
  padding: 20px;
  box-sizing: border-box;
  box-shadow: 0 2px 10px rgba(0, 0, 0, .08);
  display: flex;
  flex-direction: column;
}

.paper-rename-modal header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 22px;
}

.paper-rename-modal header strong {
  color: #1d1d1f;
  font-size: 19px;
  line-height: 27px;
  font-weight: 700;
}

.paper-rename-modal header button {
  width: 24px;
  height: 24px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.paper-rename-modal header button:disabled {
  cursor: default;
  opacity: .55;
}

.paper-rename-modal header button span {
  width: 17px;
  height: 17px;
  position: relative;
  display: block;
}

.paper-rename-modal header button span::before,
.paper-rename-modal header button span::after {
  content: "";
  width: 17px;
  height: 2px;
  background: #1d1d1f;
  position: absolute;
  left: 0;
  top: 7px;
}

.paper-rename-modal header button span::before {
  transform: rotate(45deg);
}

.paper-rename-modal header button span::after {
  transform: rotate(-45deg);
}

.paper-rename-field {
  width: 100%;
  height: 44px;
  border: 2px solid #9b9b9b;
  background: #fff;
  display: flex;
  align-items: center;
  box-sizing: border-box;
}

.paper-rename-field:focus-within {
  border-color: #123a2b;
}

.paper-rename-field.disabled {
  background: #fafafa;
}

.paper-rename-input {
  min-width: 0;
  flex: 1;
  height: 40px;
  border: 0;
  outline: 0;
  background: transparent;
  color: #25292c;
  font-size: 17px;
  line-height: 25px;
  font-weight: 500;
  padding: 5px;
  box-sizing: border-box;
}

.paper-rename-input:disabled {
  color: #7f8582;
}

.paper-rename-clear {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border: 0;
  background: transparent;
  padding: 0;
  margin-right: 5px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.paper-rename-clear:disabled {
  cursor: default;
  opacity: .55;
}

.paper-rename-clear span {
  width: 15px;
  height: 15px;
  position: relative;
  display: block;
}

.paper-rename-clear span::before,
.paper-rename-clear span::after {
  content: "";
  width: 15px;
  height: 2px;
  background: #7b7f7d;
  position: absolute;
  left: 0;
  top: 6px;
}

.paper-rename-clear span::before {
  transform: rotate(45deg);
}

.paper-rename-clear span::after {
  transform: rotate(-45deg);
}

.paper-rename-modal footer {
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
  gap: 14px;
}

.paper-rename-modal footer button {
  min-width: 78px;
  height: 40px;
  border: 1px solid #dcdcdc;
  background: #fff;
  color: #333;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.paper-rename-modal footer button:last-child {
  border-color: #123a2b;
  background: #123a2b;
  color: #fff;
}

.paper-rename-modal footer button:disabled {
  cursor: default;
  opacity: .6;
}

.paper-move-mask {
  position: absolute;
  inset: 0;
  background: rgba(243, 242, 237, .58);
  z-index: 92;
}

.paper-move-modal {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 500px;
  height: 600px;
  transform: translate(-50%, -50%);
  background: #fff;
  z-index: 93;
  box-shadow: 0 2px 12px rgba(0, 0, 0, .09);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
}

.paper-move-modal header {
  height: 58px;
  flex: 0 0 58px;
  padding: 0 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
}

.paper-move-modal header strong {
  color: #1d1d1f;
  font-size: 18px;
  line-height: 25px;
  font-weight: 700;
}

.paper-move-modal header button {
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.paper-move-modal header button:disabled {
  cursor: default;
  opacity: .55;
}

.paper-move-modal header button span {
  width: 17px;
  height: 17px;
  position: relative;
  display: block;
}

.paper-move-modal header button span::before,
.paper-move-modal header button span::after {
  content: "";
  width: 17px;
  height: 2px;
  background: #1d1d1f;
  position: absolute;
  left: 0;
  top: 7px;
}

.paper-move-modal header button span::before {
  transform: rotate(45deg);
}

.paper-move-modal header button span::after {
  transform: rotate(-45deg);
}

.paper-move-breadcrumb {
  height: 48px;
  flex: 0 0 48px;
  border-top: 1px solid transparent;
  border-bottom: 1px solid #ececea;
  padding: 0 22px;
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
}

.paper-move-breadcrumb button,
.paper-move-breadcrumb-ellipsis {
  min-width: 0;
  max-width: 128px;
  border: 0;
  background: transparent;
  color: #1d2426;
  font-size: 15px;
  line-height: 22px;
  font-weight: 700;
  padding: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-move-breadcrumb button {
  cursor: pointer;
}

.paper-move-breadcrumb button:disabled {
  cursor: default;
}

.paper-move-breadcrumb button:not(.active):hover {
  color: #123a2b;
}

.paper-move-breadcrumb-ellipsis {
  flex: 0 0 auto;
  max-width: none;
  color: #4c5350;
}

.paper-move-breadcrumb-separator {
  width: 19px;
  height: 19px;
  flex: 0 0 19px;
  margin: 0 7px;
  align-self: center;
  object-fit: contain;
  display: block;
}

.paper-move-folder-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 0;
  box-sizing: border-box;
}

.paper-move-loading,
.paper-move-empty {
  height: 100%;
  min-height: 360px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #757b78;
  font-size: 14px;
}

.paper-move-folder-row {
  width: 100%;
  height: 44px;
  border: 0;
  background: #fff;
  color: #202428;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 22px;
  box-sizing: border-box;
  cursor: pointer;
  text-align: left;
}

.paper-move-folder-row:hover,
.paper-move-folder-row.selected {
  background: #DDE4D9;
}

.paper-move-folder-row.disabled {
  cursor: not-allowed;
  opacity: .45;
}

.paper-move-folder-row.disabled:hover {
  background: #fff;
}

.paper-move-folder-row img {
  width: 35px;
  height: 35px;
  flex: 0 0 35px;
  object-fit: contain;
  display: block;
}

.paper-move-folder-row span {
  min-width: 0;
  flex: 1;
  color: inherit;
  font-size: 15px;
  line-height: 22px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-move-bottom-loading {
  height: 40px;
  color: #68716c;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
}

.paper-move-modal footer {
  height: 80px;
  flex: 0 0 80px;
  border-top: 1px solid #ececea;
  padding: 0 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
}

.paper-move-create-button {
  height: 40px;
  border: 0;
  background: transparent;
  color: #23282a;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.paper-move-create-button:disabled {
  cursor: default;
  opacity: .55;
}

.paper-move-create-button img {
  width: 22px;
  height: 22px;
  object-fit: contain;
  display: block;
}

.paper-move-footer-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.paper-move-footer-actions button {
  min-width: 78px;
  height: 40px;
  border: 1px solid #dcdcdc;
  background: #fff;
  color: #333;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.paper-move-footer-actions button:last-child {
  border-color: #123a2b;
  background: #123a2b;
  color: #fff;
}

.paper-move-footer-actions button:disabled {
  border-color: #c9d4ce;
  background: #c9d4ce;
  color: #fff;
  cursor: default;
}

.paper-move-create-mask {
  z-index: 94;
}

.paper-move-create-modal {
  z-index: 95;
}

.paper-delete-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, .38);
  z-index: 82;
}

.paper-delete-modal {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 430px;
  min-height: 176px;
  transform: translate(-50%, -50%);
  background: #fff;
  z-index: 83;
  padding: 24px 28px 26px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.paper-delete-modal header {
  color: #111;
  font-size: 19px;
  line-height: 27px;
  font-weight: 500;
}

.paper-delete-modal p {
  min-height: 44px;
  margin: 24px 0 26px;
  color: #1d1f20;
  font-size: 15px;
  line-height: 22px;
  word-break: break-all;
}

.paper-delete-modal footer {
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.paper-delete-modal footer button {
  min-width: 80px;
  height: 40px;
  border: 1px solid #d8d8d8;
  background: #fff;
  color: #333;
  font-size: 15px;
  cursor: pointer;
}

.paper-delete-modal footer button:last-child {
  border-color: #1f3a2d;
  background: #1f3a2d;
  color: #fff;
}

.paper-record-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, .28);
  z-index: 55;
}

.paper-record-panel {
  position: absolute;
  left: 50%;
  top: 50%;
  width: min(842px, calc(100% - 96px));
  min-height: 532px;
  max-height: calc(100% - 108px);
  transform: translate(-50%, -50%);
  background: #fff;
  box-shadow: 0 8px 18px rgba(0, 0, 0, .1);
  z-index: 60;
  display: flex;
  flex-direction: column;
}

.paper-record-close {
  position: absolute;
  top: 18px;
  right: 28px;
  width: 40px;
  height: 40px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.paper-record-close:hover {
  background: #F1EEE8;
}

.paper-record-close span {
  width: 18px;
  height: 18px;
  position: relative;
  display: block;
}

.paper-record-close span::before,
.paper-record-close span::after {
  content: "";
  width: 20px;
  height: 2px;
  background: #111;
  position: absolute;
  left: -1px;
  top: 8px;
}

.paper-record-close span::before {
  transform: rotate(45deg);
}

.paper-record-close span::after {
  transform: rotate(-45deg);
}

.paper-record-panel > header {
  height: 76px;
  border-bottom: 1px solid #dededb;
  display: flex;
  align-items: center;
  padding: 0 40px;
  padding-right: 82px;
  box-sizing: border-box;
  color: #14191b;
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  flex-shrink: 0;
}

.record-list {
  padding: 0 36px;
  overflow: auto;
}

.record-row {
  height: 80px;
  border-bottom: 1px solid #dededb;
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr) 56px 56px;
  align-items: center;
  gap: 0;
}

.record-icon {
  width: 42px;
  height: 42px;
  object-fit: contain;
}

.record-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.record-main strong {
  color: #14191b;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.record-main span {
  color: #6e7578;
  font-size: 13px;
  line-height: 18px;
}

.record-main em {
  display: inline-block;
  width: 1px;
  height: 12px;
  background: #d8d8d8;
  margin: 0 16px;
  vertical-align: -2px;
}

.record-action {
  width: 40px;
  height: 40px;
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.record-action:disabled {
  opacity: .25;
  cursor: default;
}

.record-action img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.record-empty {
  min-height: 430px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.record-empty img {
  width: 140px;
  height: 102px;
  object-fit: contain;
}

.record-text-head {
  gap: 16px;
}

.record-text-head button {
  width: 28px;
  height: 40px;
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.record-text-head strong {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.record-text-body {
  padding: 32px;
  color: #161b1d;
  font-size: 16px;
  line-height: 24px;
  white-space: pre-wrap;
  overflow: auto;
}
</style>
